import { DefaultTemplate, COR, CORES_SIM_NAO, CORES_PRIORIDADE } from './types';

// ============================================================
// SERVIÇO PÚBLICO / GOVERNO
//
// Prefeituras, câmaras, autarquias, secretarias e ministérios.
// O que muda em relação ao setor privado: quase toda etapa tem PRAZO LEGAL,
// e o processo precisa de trilha auditável (quem despachou, quando, com que
// fundamento). Por isso os SLAs aqui saem da lei, não de meta interna:
//   - LAI (Lei 12.527/2011): 20 dias + 10 de prorrogação
//   - Ouvidoria (Lei 13.460/2017): 30 dias + 30 de prorrogação
//   - Contratação (Lei 14.133/2021): rito DFD → ETP → TR → licitação
// Os prazos estão em horas corridas (dias × 24), padrão dos demais templates.
// ============================================================

const processoAdministrativo: DefaultTemplate = {
  id: 'tpl_processo_administrativo',
  name: 'Protocolo e Processo Administrativo',
  descricao: 'Entrada de documento, autuação, tramitação entre setores com despacho registrado, decisão da autoridade e arquivamento. A espinha dorsal de qualquer órgão — cada movimentação fica com autor, data e fundamento.',
  categoria: 'publico',
  icone: '🗂️',
  etiquetas: [
    { nome: 'Prazo Legal', cor: COR.vermelho },
    { nome: 'Prioridade Legal (idoso/PCD)', cor: COR.roxo },
    { nome: 'Sigiloso', cor: COR.cinza },
  ],
  etapas: [
    {
      id: 'pa_e1', nome: 'Protocolo / Autuação',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['pa_c1', 'pa_c2', 'pa_c7'],
      permiteMoverPara: ['pa_e2'],
      campos: [
        { id: 'pa_c1', nome: 'Assunto do Processo', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        { id: 'pa_c2', nome: 'Nº do Processo', tipo: 'texto', obrigatorio: true },
        { id: 'pa_c3', nome: 'Interessado / Requerente', tipo: 'texto', obrigatorio: true },
        { id: 'pa_c4', nome: 'CPF / CNPJ', tipo: 'texto', obrigatorio: false },
        { id: 'pa_c5', nome: 'Origem', tipo: 'lista', obrigatorio: true, opcoes: ['Cidadão (balcão)', 'Portal eletrônico', 'Ofício de outro órgão', 'Servidor / interno', 'Ministério Público', 'Poder Legislativo', 'Judiciário'], coresOpcoes: { 'Ministério Público': COR.vermelho, 'Judiciário': COR.vermelho } },
        { id: 'pa_c6', nome: 'Tipo de Processo', tipo: 'lista', obrigatorio: false, opcoes: ['Requerimento', 'Denúncia', 'Recurso administrativo', 'Solicitação de documento', 'Processo de contratação', 'Processo de pessoal', 'Ofício / expediente'] },
        { id: 'pa_c7', nome: 'Prazo Legal de Resposta', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'pa_c8', nome: 'Documentos Protocolados', tipo: 'anexo', obrigatorio: false },
        { id: 'pa_c9', nome: 'Resumo da Solicitação', tipo: 'texto_longo', obrigatorio: false },
        { id: 'pa_c10', nome: 'Prioridade legal?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'pa_e2', nome: 'Distribuição',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['pa_c1', 'pa_c11', 'pa_c7'],
      permiteMoverPara: ['pa_e3'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'pa_c11', nome: 'Setor Responsável', tipo: 'texto', obrigatorio: true },
        { id: 'pa_c12', nome: 'Servidor Designado', tipo: 'texto', obrigatorio: false },
        { id: 'pa_c13', nome: 'Despacho de Distribuição', tipo: 'texto_longo', obrigatorio: false },
        { id: 'pa_c14', nome: 'Data da Distribuição', tipo: 'data', obrigatorio: false },
      ],
    },
    {
      id: 'pa_e3', nome: 'Análise Técnica',
      tempoMaximoHoras: 240,
      camposVisiveisNoCard: ['pa_c1', 'pa_c11', 'pa_c16'],
      permiteMoverPara: ['pa_e4', 'pa_e5', 'pa_e7'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'pa_c15', nome: 'Parecer Técnico', tipo: 'texto_longo', obrigatorio: false },
        { id: 'pa_c16', nome: 'Precisa de diligência?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'pa_c17', nome: 'Fundamentação Legal', tipo: 'texto', obrigatorio: false },
        { id: 'pa_c18', nome: 'Documentos Produzidos', tipo: 'anexo', obrigatorio: false },
        { id: 'pa_c19', nome: 'Precisa de parecer jurídico?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'pa_e4', nome: 'Diligência / Exigência',
      tempoMaximoHoras: 240,
      camposVisiveisNoCard: ['pa_c1', 'pa_c21'],
      permiteMoverPara: ['pa_e3'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'pa_c20', nome: 'O que foi exigido', tipo: 'texto_longo', obrigatorio: true },
        { id: 'pa_c21', nome: 'Prazo do Interessado', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'pa_c22', nome: 'Interessado foi notificado?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'pa_c23', nome: 'Diligência atendida?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'pa_c24', nome: 'Resposta do Interessado', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'pa_e5', nome: 'Decisão da Autoridade',
      tempoMaximoHoras: 120,
      camposVisiveisNoCard: ['pa_c1', 'pa_c25'],
      permiteMoverPara: ['pa_e6', 'pa_e7'],
      permiteCriarCards: false,
      campos: [
        { id: 'pa_c25', nome: 'Decisão', tipo: 'lista', obrigatorio: true, opcoes: ['Deferido', 'Deferido parcialmente', 'Indeferido', 'Encaminhado a outro órgão'], coresOpcoes: { 'Deferido': COR.verde, 'Deferido parcialmente': COR.amarelo, 'Indeferido': COR.vermelho } },
        { id: 'pa_c26', nome: 'Autoridade Decisória', tipo: 'texto', obrigatorio: false },
        { id: 'pa_c27', nome: 'Fundamento da Decisão', tipo: 'texto_longo', obrigatorio: true },
        { id: 'pa_c28', nome: 'Data da Decisão', tipo: 'data', obrigatorio: false },
        { id: 'pa_c29', nome: 'Ato / Despacho Assinado', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'pa_e6', nome: 'Ciência e Publicação',
      tempoMaximoHoras: 120,
      camposVisiveisNoCard: ['pa_c1', 'pa_c30', 'pa_c32'],
      permiteMoverPara: ['pa_e7'],
      permiteCriarCards: false,
      campos: [
        { id: 'pa_c30', nome: 'Interessado cientificado?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'pa_c31', nome: 'Forma da Ciência', tipo: 'lista', obrigatorio: false, opcoes: ['Pessoalmente', 'Correio com AR', 'E-mail', 'Diário oficial', 'Edital'] },
        { id: 'pa_c32', nome: 'Prazo para Recurso', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'pa_c33', nome: 'Publicação no Diário Oficial', tipo: 'anexo', obrigatorio: false },
        { id: 'pa_c34', nome: 'Houve recurso?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'pa_e7', nome: 'Arquivado',
      camposVisiveisNoCard: ['pa_c1', 'pa_c2', 'pa_c35'],
      permiteCriarCards: false,
      campos: [
        { id: 'pa_c35', nome: 'Motivo do Arquivamento', tipo: 'lista', obrigatorio: false, opcoes: ['Processo concluído', 'Desistência do interessado', 'Perda do objeto', 'Diligência não atendida', 'Encaminhado a outro órgão'], coresOpcoes: { 'Processo concluído': COR.verde, 'Diligência não atendida': COR.amarelo } },
        { id: 'pa_c36', nome: 'Data do Arquivamento', tipo: 'data', obrigatorio: false },
        { id: 'pa_c37', nome: 'Termo de Encerramento', tipo: 'anexo', obrigatorio: false },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'mover',
      nome: 'Diligência necessária abre exigência',
      etapaOrigemId: 'pa_e3',
      condicao: { campoId: 'pa_c16', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'pa_e4',
      etapaDestinoSenaoId: 'pa_e5',
    },
    {
      tipo: 'mover',
      nome: 'Diligência atendida volta para análise',
      etapaOrigemId: 'pa_e4',
      condicao: { campoId: 'pa_c23', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'pa_e3',
    },
    {
      tipo: 'mover',
      nome: 'Decisão proferida vai para ciência',
      etapaOrigemId: 'pa_e5',
      condicao: { campoId: 'pa_c25', operador: 'preenchido' },
      etapaDestinoId: 'pa_e6',
    },
    {
      tipo: 'exibir_ocultar',
      nome: 'Detalhar exigência só quando houver diligência',
      etapaOrigemId: 'pa_e3',
      condicao: { campoId: 'pa_c16', operador: 'igual', valor: 'Sim' },
      camposAfetadosIds: ['pa_c19'],
    },
  ],
  cards: [
    { id: 'pa_card1', titulo: 'Requerimento de isenção de IPTU', etapaId: 'pa_e3', etiquetas: ['Prioridade Legal (idoso/PCD)'], valores: { pa_c1: 'Requerimento de isenção de IPTU', pa_c2: '2026.0044871', pa_c3: 'Maria Alves de Souza', pa_c5: 'Cidadão (balcão)', pa_c6: 'Requerimento', pa_c10: 'Sim', pa_c11: 'Secretaria da Fazenda' } },
    { id: 'pa_card2', titulo: 'Ofício do MP - saneamento no Bairro Sul', etapaId: 'pa_e4', etiquetas: ['Prazo Legal'], valores: { pa_c1: 'Ofício do MP - saneamento no Bairro Sul', pa_c2: '2026.0044912', pa_c3: 'Ministério Público Estadual', pa_c5: 'Ministério Público', pa_c6: 'Ofício / expediente', pa_c11: 'Secretaria de Obras', pa_c20: 'Encaminhar cronograma de execução da rede coletora em 15 dias.' } },
    { id: 'pa_card3', titulo: 'Recurso contra multa de posturas', etapaId: 'pa_e5', valores: { pa_c1: 'Recurso contra multa de posturas', pa_c2: '2026.0044733', pa_c3: 'Comércio Silva ME', pa_c5: 'Portal eletrônico', pa_c6: 'Recurso administrativo', pa_c11: 'Procuradoria', pa_c25: 'Deferido parcialmente' } },
    { id: 'pa_card4', titulo: 'Solicitação de certidão de tempo de serviço', etapaId: 'pa_e6', valores: { pa_c1: 'Solicitação de certidão de tempo de serviço', pa_c2: '2026.0044690', pa_c3: 'João Batista Ferreira', pa_c5: 'Servidor / interno', pa_c6: 'Processo de pessoal', pa_c25: 'Deferido', pa_c30: 'Sim' } },
    { id: 'pa_card5', titulo: 'Denúncia de obra sem alvará', etapaId: 'pa_e2', valores: { pa_c1: 'Denúncia de obra sem alvará', pa_c2: '2026.0044955', pa_c3: 'Anônimo', pa_c5: 'Portal eletrônico', pa_c6: 'Denúncia', pa_c11: 'Fiscalização de Obras' } },
    { id: 'pa_card6', titulo: 'Pedido de uso de espaço público', etapaId: 'pa_e1', valores: { pa_c1: 'Pedido de uso de espaço público', pa_c2: '2026.0044980', pa_c3: 'Associação de Moradores Vila Nova', pa_c5: 'Cidadão (balcão)', pa_c6: 'Requerimento' } },
    { id: 'pa_card7', titulo: 'Requerimento de vista de processo', etapaId: 'pa_e7', valores: { pa_c1: 'Requerimento de vista de processo', pa_c2: '2026.0044501', pa_c3: 'Advogado - OAB 44.212', pa_c5: 'Cidadão (balcão)', pa_c35: 'Processo concluído' } },
  ],
  tour: [
    '🗂️ Cada processo administrativo com trilha completa: quem despachou, quando e com que fundamento.',
    '1️⃣ "Protocolo": número do processo, interessado e o prazo legal como vencimento do card.',
    '2️⃣ "Distribuição": setor e servidor designados, com despacho registrado.',
    '3️⃣ "Análise Técnica": parecer com fundamentação legal. Precisa de diligência? o card já abre a exigência.',
    '4️⃣ "Diligência": prazo do interessado controlado — não atendeu, arquiva.',
    '5️⃣ "Decisão": deferido, parcial ou indeferido, sempre com fundamento escrito.',
    '📢 "Ciência e Publicação": forma da ciência e prazo de recurso registrados.',
    '📌 Marque "Prioridade Legal" para idoso, PCD e portador de doença grave — eles têm preferência por lei.',
  ],
  etapasDicas: [
    { quemFaz: 'Setor de Protocolo', objetivo: 'Autuar com número único e prazo legal já preenchido.' },
    { quemFaz: 'Chefia de Gabinete / Protocolo', objetivo: 'Distribuir rápido — o prazo já está correndo.' },
    { quemFaz: 'Servidor técnico do setor', objetivo: 'Analisar com fundamentação legal explícita.', automacoes: ['Já configurado: diligência abre a etapa de exigência'] },
    { quemFaz: 'Servidor responsável', objetivo: 'Notificar o interessado e controlar o prazo dele.' },
    { quemFaz: 'Autoridade competente', objetivo: 'Decidir com motivação — decisão sem fundamento é nula.' },
    { quemFaz: 'Protocolo / Comunicação', objetivo: 'Dar ciência formal e publicar quando exigido.' },
    { quemFaz: 'Arquivo', objetivo: 'Encerrar com termo e guardar pelo prazo da tabela de temporalidade.' },
  ],
};

const contratacaoPublica: DefaultTemplate = {
  id: 'tpl_contratacao_publica',
  name: 'Contratação Pública (Lei 14.133)',
  descricao: 'O rito completo da nova lei de licitações: DFD, ETP, termo de referência, pesquisa de preços, parecer jurídico, autorização do ordenador, publicação, sessão, homologação e contrato. Do lado de quem compra.',
  categoria: 'publico',
  icone: '🏛️',
  etiquetas: [
    { nome: 'Dispensa / Inexigibilidade', cor: COR.amarelo },
    { nome: 'Registro de Preços', cor: COR.ciano },
    { nome: 'Emergencial', cor: COR.vermelho },
  ],
  etapas: [
    {
      id: 'cp_e1', nome: 'DFD - Formalização da Demanda',
      tempoMaximoHoras: 120,
      camposVisiveisNoCard: ['cp_c1', 'cp_c2', 'cp_c5'],
      permiteMoverPara: ['cp_e2'],
      campos: [
        { id: 'cp_c1', nome: 'Objeto da Contratação', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        { id: 'cp_c2', nome: 'Setor Requisitante', tipo: 'texto', obrigatorio: true },
        { id: 'cp_c3', nome: 'Nº do Processo', tipo: 'texto', obrigatorio: false },
        { id: 'cp_c4', nome: 'Justificativa da Necessidade', tipo: 'texto_longo', obrigatorio: true },
        { id: 'cp_c5', nome: 'Valor Estimado', tipo: 'valor', obrigatorio: false },
        { id: 'cp_c6', nome: 'Previsto no PCA (plano de contratações)?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'cp_c7', nome: 'Data Desejada da Contratação', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'cp_c8', nome: 'DFD Assinado', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'cp_e2', nome: 'ETP e Termo de Referência',
      tempoMaximoHoras: 336,
      camposVisiveisNoCard: ['cp_c1', 'cp_c10', 'cp_c13'],
      permiteMoverPara: ['cp_e3', 'cp_e8'],
      permiteCriarCards: false,
      campos: [
        { id: 'cp_c9', nome: 'Modalidade Pretendida', tipo: 'lista', obrigatorio: true, opcoes: ['Pregão eletrônico', 'Concorrência', 'Concurso', 'Leilão', 'Diálogo competitivo', 'Dispensa de licitação', 'Inexigibilidade', 'Adesão a ata (carona)'], coresOpcoes: { 'Pregão eletrônico': COR.azul, 'Dispensa de licitação': COR.amarelo, 'Inexigibilidade': COR.amarelo } },
        { id: 'cp_c10', nome: 'Documentos da Fase Preparatória', tipo: 'checkbox_multi', obrigatorio: false, opcoes: ['ETP - Estudo Técnico Preliminar', 'Mapa de riscos', 'Termo de referência / Projeto básico', 'Anteprojeto', 'Planilha de composição de custos', 'Justificativa de dispensa'] },
        { id: 'cp_c11', nome: 'Sistema de Registro de Preços?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'cp_c12', nome: 'TR / Projeto Básico', tipo: 'anexo', obrigatorio: false },
        { id: 'cp_c13', nome: 'Fase preparatória concluída?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'cp_e3', nome: 'Pesquisa de Preços',
      tempoMaximoHoras: 240,
      camposVisiveisNoCard: ['cp_c1', 'cp_c15', 'cp_c16'],
      permiteMoverPara: ['cp_e4', 'cp_e8'],
      permiteCriarCards: false,
      campos: [
        { id: 'cp_c14', nome: 'Fontes Consultadas', tipo: 'checkbox_multi', obrigatorio: false, opcoes: ['Painel de Preços', 'Contratações similares de outros entes', 'Sites especializados', 'Fornecedores (cotação direta)', 'Tabela referencial (SINAPI/SICRO)', 'Notas fiscais de contratações anteriores'] },
        { id: 'cp_c15', nome: 'Valor Estimado Final', tipo: 'valor', obrigatorio: true },
        { id: 'cp_c16', nome: 'Método de Cálculo', tipo: 'lista', obrigatorio: false, opcoes: ['Média', 'Mediana', 'Menor preço', 'Preço de referência oficial'] },
        { id: 'cp_c17', nome: 'Mapa Comparativo', tipo: 'anexo', obrigatorio: false },
        { id: 'cp_c18', nome: 'Há dotação orçamentária?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'cp_c19', nome: 'Dotação Orçamentária', tipo: 'texto', obrigatorio: false },
      ],
    },
    {
      id: 'cp_e4', nome: 'Parecer Jurídico',
      tempoMaximoHoras: 240,
      camposVisiveisNoCard: ['cp_c1', 'cp_c20'],
      permiteMoverPara: ['cp_e5', 'cp_e2', 'cp_e8'],
      permiteCriarCards: false,
      campos: [
        { id: 'cp_c20', nome: 'Parecer', tipo: 'lista', obrigatorio: true, opcoes: ['Favorável', 'Favorável com ressalvas', 'Desfavorável'], coresOpcoes: { 'Favorável': COR.verde, 'Favorável com ressalvas': COR.amarelo, 'Desfavorável': COR.vermelho } },
        { id: 'cp_c21', nome: 'Ressalvas / Recomendações', tipo: 'texto_longo', obrigatorio: false },
        { id: 'cp_c22', nome: 'Procurador Responsável', tipo: 'texto', obrigatorio: false },
        { id: 'cp_c23', nome: 'Parecer (documento)', tipo: 'anexo', obrigatorio: false },
        { id: 'cp_c24', nome: 'Minuta do Edital', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'cp_e5', nome: 'Autorização e Publicação',
      tempoMaximoHoras: 168,
      camposVisiveisNoCard: ['cp_c1', 'cp_c26', 'cp_c28'],
      permiteMoverPara: ['cp_e6', 'cp_e8'],
      permiteCriarCards: false,
      campos: [
        { id: 'cp_c25', nome: 'Ordenador de Despesa', tipo: 'texto', obrigatorio: false },
        { id: 'cp_c26', nome: 'Autorizado?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'cp_c27', nome: 'Nº do Edital', tipo: 'texto', obrigatorio: false },
        { id: 'cp_c28', nome: 'Data da Sessão / Abertura', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'cp_c29', nome: 'Publicado no PNCP?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'cp_c30', nome: 'Edital Publicado', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'cp_e6', nome: 'Sessão e Julgamento',
      tempoMaximoHoras: 336,
      camposVisiveisNoCard: ['cp_c1', 'cp_c31', 'cp_c33'],
      permiteMoverPara: ['cp_e7', 'cp_e8'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'cp_c31', nome: 'Vencedor', tipo: 'texto', obrigatorio: false },
        { id: 'cp_c32', nome: 'Nº de Participantes', tipo: 'numero', obrigatorio: false },
        { id: 'cp_c33', nome: 'Valor Adjudicado', tipo: 'valor', obrigatorio: false },
        { id: 'cp_c34', nome: 'Economia Obtida (%)', tipo: 'numero', obrigatorio: false },
        { id: 'cp_c35', nome: 'Houve recurso?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'cp_c36', nome: 'Ata da Sessão', tipo: 'anexo', obrigatorio: false },
        { id: 'cp_c37', nome: 'Homologado?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'cp_e7', nome: 'Contrato / Ata Assinada',
      tempoMaximoHoras: 240,
      camposVisiveisNoCard: ['cp_c1', 'cp_c31', 'cp_c40'],
      permiteCriarCards: false,
      campos: [
        { id: 'cp_c38', nome: 'Nº do Contrato / Ata', tipo: 'texto', obrigatorio: false },
        { id: 'cp_c39', nome: 'Data da Assinatura', tipo: 'data', obrigatorio: false },
        { id: 'cp_c40', nome: 'Vigência até', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'cp_c41', nome: 'Fiscal do Contrato Designado', tipo: 'texto', obrigatorio: false },
        { id: 'cp_c42', nome: 'Garantia Contratual', tipo: 'lista', obrigatorio: false, opcoes: ['Seguro-garantia', 'Fiança bancária', 'Caução', 'Não exigida'] },
        { id: 'cp_c43', nome: 'Contrato Assinado', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'cp_e8', nome: 'Cancelado / Fracassado',
      camposVisiveisNoCard: ['cp_c1', 'cp_c44'],
      permiteMoverPara: ['cp_e1'],
      permiteCriarCards: false,
      campos: [
        { id: 'cp_c44', nome: 'Motivo', tipo: 'lista', obrigatorio: true, opcoes: ['Deserta (sem interessados)', 'Fracassada (todos inabilitados)', 'Revogada por interesse público', 'Anulada por ilegalidade', 'Sem dotação orçamentária', 'Parecer jurídico desfavorável', 'Demanda cancelada'], coresOpcoes: { 'Anulada por ilegalidade': COR.vermelho, 'Sem dotação orçamentária': COR.vermelho } },
        { id: 'cp_c45', nome: 'Detalhamento', tipo: 'texto_longo', obrigatorio: false },
        { id: 'cp_c46', nome: 'Vai repetir o certame?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'mover',
      nome: 'Fase preparatória pronta vai para pesquisa de preços',
      etapaOrigemId: 'cp_e2',
      condicao: { campoId: 'cp_c13', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'cp_e3',
    },
    {
      tipo: 'mover',
      nome: 'Com dotação segue para o jurídico',
      etapaOrigemId: 'cp_e3',
      condicao: { campoId: 'cp_c18', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'cp_e4',
      etapaDestinoSenaoId: 'cp_e8',
    },
    {
      tipo: 'mover',
      nome: 'Parecer desfavorável trava o processo',
      etapaOrigemId: 'cp_e4',
      condicao: { campoId: 'cp_c20', operador: 'igual', valor: 'Desfavorável' },
      etapaDestinoId: 'cp_e8',
      etapaDestinoSenaoId: 'cp_e5',
    },
    {
      tipo: 'mover',
      nome: 'Autorizado publica e abre a sessão',
      etapaOrigemId: 'cp_e5',
      condicao: { campoId: 'cp_c26', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'cp_e6',
      etapaDestinoSenaoId: 'cp_e8',
    },
    {
      tipo: 'mover',
      nome: 'Homologado vira contrato',
      etapaOrigemId: 'cp_e6',
      condicao: { campoId: 'cp_c37', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'cp_e7',
      etapaDestinoSenaoId: 'cp_e8',
    },
    {
      tipo: 'exibir_ocultar',
      nome: 'Dotação orçamentária quando existe',
      etapaOrigemId: 'cp_e3',
      condicao: { campoId: 'cp_c18', operador: 'igual', valor: 'Sim' },
      camposAfetadosIds: ['cp_c19'],
    },
  ],
  cards: [
    { id: 'cp_card1', titulo: 'Aquisição de merenda escolar - 2º semestre', etapaId: 'cp_e6', etiquetas: ['Registro de Preços'], valores: { cp_c1: 'Aquisição de merenda escolar - 2º semestre', cp_c2: 'Secretaria de Educação', cp_c3: '2026.0031200', cp_c9: 'Pregão eletrônico', cp_c11: 'Sim', cp_c15: 4200000, cp_c31: 'Alimentos Vale Ltda', cp_c32: 11, cp_c33: 3780000, cp_c34: 10 } },
    { id: 'cp_card2', titulo: 'Contratação de serviço de limpeza urbana', etapaId: 'cp_e4', valores: { cp_c1: 'Contratação de serviço de limpeza urbana', cp_c2: 'Secretaria de Obras', cp_c3: '2026.0031455', cp_c9: 'Concorrência', cp_c15: 18500000, cp_c20: 'Favorável com ressalvas', cp_c21: 'Ajustar cláusula de reajuste e detalhar critérios de medição.' } },
    { id: 'cp_card3', titulo: 'Locação de veículos para a frota administrativa', etapaId: 'cp_e3', valores: { cp_c1: 'Locação de veículos para a frota administrativa', cp_c2: 'Secretaria de Administração', cp_c9: 'Pregão eletrônico', cp_c15: 960000, cp_c16: 'Mediana', cp_c18: 'Sim', cp_c19: '04.122.0002.2010 - 3.3.90.39' } },
    { id: 'cp_card4', titulo: 'Medicamentos da farmácia básica', etapaId: 'cp_e7', etiquetas: ['Registro de Preços'], valores: { cp_c1: 'Medicamentos da farmácia básica', cp_c2: 'Secretaria de Saúde', cp_c9: 'Pregão eletrônico', cp_c15: 2700000, cp_c31: 'Distribuidora Farma Sul', cp_c38: 'ARP 018/2026', cp_c41: 'Servidora Cláudia Ramos' } },
    { id: 'cp_card5', titulo: 'Reforma da UBS Central', etapaId: 'cp_e2', valores: { cp_c1: 'Reforma da UBS Central', cp_c2: 'Secretaria de Saúde', cp_c9: 'Concorrência', cp_c15: 3100000, cp_c13: 'Não' } },
    { id: 'cp_card6', titulo: 'Software de gestão de protocolo', etapaId: 'cp_e1', etiquetas: ['Dispensa / Inexigibilidade'], valores: { cp_c1: 'Software de gestão de protocolo', cp_c2: 'Secretaria de Administração', cp_c4: 'Substituição do sistema legado sem suporte desde 2024.', cp_c5: 180000, cp_c6: 'Sim' } },
    { id: 'cp_card7', titulo: 'Aquisição de uniformes escolares', etapaId: 'cp_e8', valores: { cp_c1: 'Aquisição de uniformes escolares', cp_c2: 'Secretaria de Educação', cp_c9: 'Pregão eletrônico', cp_c44: 'Fracassada (todos inabilitados)', cp_c45: 'Nenhum licitante apresentou amostra conforme a especificação técnica.', cp_c46: 'Sim' } },
  ],
  tour: [
    '🏛️ O rito da Lei 14.133 em colunas — nenhuma fase pulada, tudo auditável.',
    '1️⃣ "DFD": a demanda formalizada pelo setor, com justificativa e checagem do plano de contratações.',
    '2️⃣ "ETP e TR": checklist da fase preparatória — é aqui que o TCE mais aponta falha.',
    '3️⃣ "Pesquisa de Preços": fontes consultadas e método de cálculo registrados.',
    '4️⃣ "Parecer Jurídico": desfavorável trava o processo automaticamente.',
    '5️⃣ "Autorização e Publicação": ordenador de despesa e publicação no PNCP.',
    '6️⃣ "Sessão": vencedor, participantes e economia obtida — número que vira indicador de gestão.',
    '📄 "Contrato": fiscal designado já no card, ligando ao fluxo de fiscalização.',
  ],
  etapasDicas: [
    { quemFaz: 'Setor requisitante', objetivo: 'Formalizar a demanda com justificativa real, não genérica.' },
    { quemFaz: 'Equipe de planejamento da contratação', objetivo: 'Fechar ETP e TR — a fase preparatória é a que mais gera apontamento de controle.' },
    { quemFaz: 'Setor de Compras', objetivo: 'Pesquisar preço em fontes válidas e registrar o método.', automacoes: ['Já configurado: sem dotação o processo é travado'] },
    { quemFaz: 'Procuradoria / Assessoria Jurídica', objetivo: 'Analisar minuta e apontar ressalvas antes da publicação.' },
    { quemFaz: 'Ordenador de Despesa', objetivo: 'Autorizar e garantir a publicidade no PNCP.' },
    { quemFaz: 'Pregoeiro / Comissão de Contratação', objetivo: 'Conduzir a sessão e registrar a economia obtida.' },
    { quemFaz: 'Setor de Contratos', objetivo: 'Assinar e designar o fiscal.', conexoes: ['Ligue à "Fiscalização de Contratos" e à "Execução da Despesa"'] },
    { quemFaz: 'Compras / Jurídico', objetivo: 'Registrar o motivo e decidir se repete o certame.' },
  ],
};

const execucaoDespesa: DefaultTemplate = {
  id: 'tpl_execucao_despesa',
  name: 'Execução da Despesa (Empenho, Liquidação e Pagamento)',
  descricao: 'As três fases da despesa pública em colunas: reserva e empenho prévios, entrega atestada pelo fiscal, liquidação com certidões, ordem de pagamento e quitação. Nada é pago sem passar pelas três.',
  categoria: 'publico',
  icone: '💳',
  etiquetas: [
    { nome: 'Restos a Pagar', cor: COR.amarelo },
    { nome: 'Convênio', cor: COR.roxo },
    { nome: 'Certidão Vencida', cor: COR.vermelho },
  ],
  etapas: [
    {
      id: 'ed_e1', nome: 'Solicitação de Despesa',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['ed_c1', 'ed_c3', 'ed_c5'],
      permiteMoverPara: ['ed_e2'],
      campos: [
        { id: 'ed_c1', nome: 'Objeto da Despesa', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        { id: 'ed_c2', nome: 'Credor / Fornecedor', tipo: 'texto', obrigatorio: true },
        { id: 'ed_c3', nome: 'Unidade Orçamentária', tipo: 'texto', obrigatorio: true },
        { id: 'ed_c4', nome: 'Contrato / Ata de Referência', tipo: 'texto', obrigatorio: false },
        { id: 'ed_c5', nome: 'Valor', tipo: 'valor', obrigatorio: true },
        { id: 'ed_c6', nome: 'Elemento de Despesa', tipo: 'texto', obrigatorio: false },
        { id: 'ed_c7', nome: 'Fonte de Recurso', tipo: 'lista', obrigatorio: false, opcoes: ['Recursos próprios', 'Convênio federal', 'Convênio estadual', 'Emenda parlamentar', 'Operação de crédito', 'Fundo específico'], coresOpcoes: { 'Convênio federal': COR.roxo, 'Emenda parlamentar': COR.ciano } },
        { id: 'ed_c8', nome: 'Documento de Origem', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'ed_e2', nome: 'Empenho',
      tempoMaximoHoras: 72,
      camposVisiveisNoCard: ['ed_c1', 'ed_c9', 'ed_c11'],
      permiteMoverPara: ['ed_e3', 'ed_e7'],
      permiteCriarCards: false,
      campos: [
        { id: 'ed_c9', nome: 'Nº da Nota de Empenho', tipo: 'texto', obrigatorio: false },
        { id: 'ed_c10', nome: 'Tipo de Empenho', tipo: 'lista', obrigatorio: false, opcoes: ['Ordinário', 'Estimativo', 'Global'] },
        { id: 'ed_c11', nome: 'Há saldo orçamentário?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'ed_c12', nome: 'Data do Empenho', tipo: 'data', obrigatorio: false },
        { id: 'ed_c13', nome: 'Nota de Empenho', tipo: 'anexo', obrigatorio: false },
        { id: 'ed_c14', nome: 'Valor Empenhado', tipo: 'valor', obrigatorio: false },
      ],
    },
    {
      id: 'ed_e3', nome: 'Recebimento e Atesto',
      tempoMaximoHoras: 240,
      camposVisiveisNoCard: ['ed_c1', 'ed_c16', 'ed_c17'],
      permiteMoverPara: ['ed_e4', 'ed_e7'],
      permiteCriarCards: false,
      campos: [
        { id: 'ed_c15', nome: 'Nº da Nota Fiscal', tipo: 'texto', obrigatorio: false },
        { id: 'ed_c16', nome: 'Objeto entregue conforme?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'ed_c17', nome: 'Fiscal / Responsável pelo Atesto', tipo: 'texto', obrigatorio: false },
        { id: 'ed_c18', nome: 'Divergência no Recebimento', tipo: 'texto_longo', obrigatorio: false },
        { id: 'ed_c19', nome: 'Nota Fiscal e Termo de Recebimento', tipo: 'anexo', obrigatorio: false },
        { id: 'ed_c20', nome: 'Data do Atesto', tipo: 'data', obrigatorio: false },
      ],
    },
    {
      id: 'ed_e4', nome: 'Liquidação',
      tempoMaximoHoras: 120,
      camposVisiveisNoCard: ['ed_c1', 'ed_c22', 'ed_c23'],
      permiteMoverPara: ['ed_e5', 'ed_e7'],
      permiteCriarCards: false,
      campos: [
        { id: 'ed_c21', nome: 'Certidões Verificadas', tipo: 'checkbox_multi', obrigatorio: false, opcoes: ['CND Federal', 'CRF / FGTS', 'CNDT Trabalhista', 'Certidão estadual', 'Certidão municipal', 'Consulta a sanções (CEIS/CNEP)'] },
        { id: 'ed_c22', nome: 'Regularidade fiscal em dia?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'ed_c23', nome: 'Valor Liquidado', tipo: 'valor', obrigatorio: false },
        { id: 'ed_c24', nome: 'Retenções (INSS, ISS, IR)', tipo: 'valor', obrigatorio: false },
        { id: 'ed_c25', nome: 'Nº da Nota de Liquidação', tipo: 'texto', obrigatorio: false },
        { id: 'ed_c26', nome: 'Certidões (arquivo)', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'ed_e5', nome: 'Ordem de Pagamento',
      tempoMaximoHoras: 168,
      camposVisiveisNoCard: ['ed_c1', 'ed_c27', 'ed_c29'],
      permiteMoverPara: ['ed_e6'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'ed_c27', nome: 'Valor Líquido a Pagar', tipo: 'valor', obrigatorio: false },
        { id: 'ed_c28', nome: 'Ordem Cronológica Respeitada?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'ed_c29', nome: 'Previsão de Pagamento', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'ed_c30', nome: 'Autorizado pelo ordenador?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'ed_c31', nome: 'Ordem Bancária', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'ed_e6', nome: 'Pago',
      camposVisiveisNoCard: ['ed_c1', 'ed_c27', 'ed_c32'],
      permiteCriarCards: false,
      campos: [
        { id: 'ed_c32', nome: 'Data do Pagamento', tipo: 'data', obrigatorio: false },
        { id: 'ed_c33', nome: 'Comprovante', tipo: 'anexo', obrigatorio: false },
        { id: 'ed_c34', nome: 'Publicado no portal da transparência?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'ed_e7', nome: 'Pendente / Devolvido',
      camposVisiveisNoCard: ['ed_c1', 'ed_c35', 'ed_c36'],
      permiteMoverPara: ['ed_e2', 'ed_e3', 'ed_e4'],
      permiteCriarCards: false,
      campos: [
        { id: 'ed_c35', nome: 'Motivo', tipo: 'lista', obrigatorio: true, opcoes: ['Sem saldo orçamentário', 'Objeto não conforme', 'Certidão vencida', 'Nota fiscal incorreta', 'Falta de documento', 'Aguardando repasse de convênio'], coresOpcoes: { 'Certidão vencida': COR.vermelho, 'Sem saldo orçamentário': COR.vermelho } },
        { id: 'ed_c36', nome: 'Prazo para Regularizar', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'ed_c37', nome: 'Detalhamento', tipo: 'texto_longo', obrigatorio: false },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'mover',
      nome: 'Com saldo o empenho segue para recebimento',
      etapaOrigemId: 'ed_e2',
      condicao: { campoId: 'ed_c11', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'ed_e3',
      etapaDestinoSenaoId: 'ed_e7',
    },
    {
      tipo: 'mover',
      nome: 'Objeto atestado vai para liquidação',
      etapaOrigemId: 'ed_e3',
      condicao: { campoId: 'ed_c16', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'ed_e4',
      etapaDestinoSenaoId: 'ed_e7',
    },
    {
      tipo: 'mover',
      nome: 'Regularidade fiscal libera o pagamento',
      etapaOrigemId: 'ed_e4',
      condicao: { campoId: 'ed_c22', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'ed_e5',
      etapaDestinoSenaoId: 'ed_e7',
    },
    {
      tipo: 'mover',
      nome: 'Ordem autorizada efetiva o pagamento',
      etapaOrigemId: 'ed_e5',
      condicao: { campoId: 'ed_c30', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'ed_e6',
    },
    {
      tipo: 'exibir_ocultar',
      nome: 'Divergência quando o objeto não confere',
      etapaOrigemId: 'ed_e3',
      condicao: { campoId: 'ed_c16', operador: 'igual', valor: 'Não' },
      camposAfetadosIds: ['ed_c18'],
    },
  ],
  cards: [
    { id: 'ed_card1', titulo: 'Merenda escolar - entrega de agosto', etapaId: 'ed_e4', etiquetas: ['Certidão Vencida'], valores: { ed_c1: 'Merenda escolar - entrega de agosto', ed_c2: 'Alimentos Vale Ltda', ed_c3: 'Secretaria de Educação', ed_c5: 315000, ed_c22: 'Não', ed_c9: '2026NE001842' } },
    { id: 'ed_card2', titulo: 'Limpeza urbana - medição de julho', etapaId: 'ed_e5', valores: { ed_c1: 'Limpeza urbana - medição de julho', ed_c2: 'Ambiental Serviços', ed_c3: 'Secretaria de Obras', ed_c5: 1540000, ed_c23: 1540000, ed_c24: 96000, ed_c27: 1444000, ed_c28: 'Sim' } },
    { id: 'ed_card3', titulo: 'Medicamentos - pedido 04', etapaId: 'ed_e6', valores: { ed_c1: 'Medicamentos - pedido 04', ed_c2: 'Distribuidora Farma Sul', ed_c3: 'Secretaria de Saúde', ed_c5: 224000, ed_c27: 218000, ed_c34: 'Sim' } },
    { id: 'ed_card4', titulo: 'Reforma da UBS - 1ª medição', etapaId: 'ed_e3', etiquetas: ['Convênio'], valores: { ed_c1: 'Reforma da UBS - 1ª medição', ed_c2: 'Construtora Pilar', ed_c3: 'Secretaria de Saúde', ed_c5: 480000, ed_c7: 'Convênio federal', ed_c17: 'Eng. Ricardo Alves' } },
    { id: 'ed_card5', titulo: 'Locação de veículos - agosto', etapaId: 'ed_e2', valores: { ed_c1: 'Locação de veículos - agosto', ed_c2: 'LocaFrota', ed_c3: 'Secretaria de Administração', ed_c5: 78000, ed_c10: 'Estimativo' } },
    { id: 'ed_card6', titulo: 'Material de escritório - pedido 12', etapaId: 'ed_e7', valores: { ed_c1: 'Material de escritório - pedido 12', ed_c2: 'Papelaria Central', ed_c3: 'Secretaria de Administração', ed_c5: 18400, ed_c35: 'Nota fiscal incorreta', ed_c37: 'NF emitida com CNPJ do fundo e não da prefeitura.' } },
  ],
  tour: [
    '💳 Empenho, liquidação e pagamento — as três fases da despesa pública, cada uma sendo uma coluna.',
    '1️⃣ "Solicitação": credor, unidade orçamentária e fonte de recurso.',
    '2️⃣ "Empenho": sem saldo, o card cai em pendência automaticamente.',
    '3️⃣ "Recebimento e Atesto": o fiscal atesta que o objeto foi entregue — sem isso não liquida.',
    '4️⃣ "Liquidação": checklist de certidões e consulta a sanções (CEIS/CNEP).',
    '5️⃣ "Ordem de Pagamento": campo de ordem cronológica, exigência da lei.',
    '✅ "Pago": com marcação de publicação no portal da transparência.',
  ],
  etapasDicas: [
    { quemFaz: 'Setor requisitante', objetivo: 'Vincular a despesa ao contrato e à fonte correta.' },
    { quemFaz: 'Contabilidade / Orçamento', objetivo: 'Empenhar antes da entrega — empenho posterior é irregularidade.', automacoes: ['Já configurado: sem saldo vai para pendência'] },
    { quemFaz: 'Fiscal do contrato', objetivo: 'Atestar só o que foi realmente entregue.', conexoes: ['Ligue à "Fiscalização de Contratos"'] },
    { quemFaz: 'Contabilidade', objetivo: 'Conferir regularidade fiscal na data do pagamento, não na da contratação.' },
    { quemFaz: 'Tesouraria', objetivo: 'Respeitar a ordem cronológica de pagamentos.' },
    { quemFaz: 'Tesouraria / Transparência', objetivo: 'Publicar e guardar o comprovante.' },
    { quemFaz: 'Contabilidade', objetivo: 'Dar prazo ao credor para regularizar a pendência.' },
  ],
};

const ouvidoria: DefaultTemplate = {
  id: 'tpl_ouvidoria',
  name: 'Ouvidoria e Manifestações',
  descricao: 'Denúncia, reclamação, sugestão, elogio e solicitação no rito da Lei 13.460/2017: 30 dias para responder, prorrogáveis por mais 30 com justificativa. Com sigilo do denunciante e pesquisa de satisfação.',
  categoria: 'publico',
  icone: '📢',
  etiquetas: [
    { nome: 'Denúncia', cor: COR.vermelho },
    { nome: 'Identidade Preservada', cor: COR.cinza },
    { nome: 'Prorrogada', cor: COR.amarelo },
    { nome: 'Reincidente', cor: COR.roxo },
  ],
  etapas: [
    {
      id: 'ou_e1', nome: 'Manifestação Recebida',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['ou_c1', 'ou_c2', 'ou_c8'],
      permiteMoverPara: ['ou_e2'],
      campos: [
        { id: 'ou_c1', nome: 'Resumo da Manifestação', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        { id: 'ou_c2', nome: 'Tipo', tipo: 'lista', obrigatorio: true, opcoes: ['Denúncia', 'Reclamação', 'Solicitação', 'Sugestão', 'Elogio', 'Simplificação'], coresOpcoes: { 'Denúncia': COR.vermelho, 'Reclamação': COR.amarelo, 'Elogio': COR.verde } },
        { id: 'ou_c3', nome: 'Protocolo', tipo: 'texto', obrigatorio: false },
        { id: 'ou_c4', nome: 'Canal de Entrada', tipo: 'lista', obrigatorio: false, opcoes: ['Portal / Fala.BR', 'Telefone', 'Presencial', 'E-mail', 'Carta', 'Redes sociais'] },
        { id: 'ou_c5', nome: 'Manifestante', tipo: 'texto', obrigatorio: false },
        { id: 'ou_c6', nome: 'Contato', tipo: 'telefone', obrigatorio: false },
        { id: 'ou_c7', nome: 'Pedido de sigilo de identidade?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'ou_c8', nome: 'Prazo Legal (30 dias)', tipo: 'data_vencimento', obrigatorio: true },
        { id: 'ou_c9', nome: 'Relato Completo', tipo: 'texto_longo', obrigatorio: true },
        { id: 'ou_c10', nome: 'Anexos do Manifestante', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'ou_e2', nome: 'Triagem e Encaminhamento',
      tempoMaximoHoras: 72,
      camposVisiveisNoCard: ['ou_c1', 'ou_c11', 'ou_c8'],
      permiteMoverPara: ['ou_e3', 'ou_e6'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'ou_c11', nome: 'Órgão / Setor Responsável', tipo: 'texto', obrigatorio: true },
        { id: 'ou_c12', nome: 'Assunto', tipo: 'lista', obrigatorio: false, opcoes: ['Saúde', 'Educação', 'Obras e infraestrutura', 'Limpeza urbana', 'Iluminação', 'Trânsito', 'Meio ambiente', 'Servidor / conduta', 'Tributos', 'Assistência social'] },
        { id: 'ou_c13', nome: 'Manifestação admissível?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'ou_c14', nome: 'Motivo da Inadmissão', tipo: 'texto_longo', obrigatorio: false },
        { id: 'ou_c15', nome: 'Grau de Urgência', tipo: 'lista', obrigatorio: false, opcoes: ['Baixa', 'Média', 'Alta', 'Urgente'], coresOpcoes: CORES_PRIORIDADE },
      ],
    },
    {
      id: 'ou_e3', nome: 'Apuração no Setor',
      tempoMaximoHoras: 480,
      camposVisiveisNoCard: ['ou_c1', 'ou_c11', 'ou_c8'],
      permiteMoverPara: ['ou_e4', 'ou_e5'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'ou_c16', nome: 'Providências Adotadas', tipo: 'texto_longo', obrigatorio: false },
        { id: 'ou_c17', nome: 'Responsável pela Apuração', tipo: 'texto', obrigatorio: false },
        { id: 'ou_c18', nome: 'Documentos da Apuração', tipo: 'anexo', obrigatorio: false },
        { id: 'ou_c19', nome: 'Precisa prorrogar o prazo?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'ou_c20', nome: 'Apuração concluída?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'ou_e4', nome: 'Prorrogada',
      tempoMaximoHoras: 720,
      camposVisiveisNoCard: ['ou_c1', 'ou_c22'],
      permiteMoverPara: ['ou_e3', 'ou_e5'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'ou_c21', nome: 'Justificativa da Prorrogação', tipo: 'texto_longo', obrigatorio: true },
        { id: 'ou_c22', nome: 'Novo Prazo (mais 30 dias)', tipo: 'data_vencimento', obrigatorio: true },
        { id: 'ou_c23', nome: 'Manifestante comunicado?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'ou_e5', nome: 'Resposta ao Cidadão',
      tempoMaximoHoras: 72,
      camposVisiveisNoCard: ['ou_c1', 'ou_c24'],
      permiteMoverPara: ['ou_e6'],
      permiteCriarCards: false,
      campos: [
        { id: 'ou_c24', nome: 'Conclusão', tipo: 'lista', obrigatorio: true, opcoes: ['Procedente - resolvida', 'Procedente - em tratamento', 'Improcedente', 'Sem elementos para apurar'], coresOpcoes: { 'Procedente - resolvida': COR.verde, 'Improcedente': COR.cinza, 'Sem elementos para apurar': COR.cinza } },
        { id: 'ou_c25', nome: 'Resposta Enviada', tipo: 'texto_longo', obrigatorio: true },
        { id: 'ou_c26', nome: 'Data da Resposta', tipo: 'data', obrigatorio: false },
        { id: 'ou_c27', nome: 'Respondida dentro do prazo?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'ou_e6', nome: 'Encerrada',
      camposVisiveisNoCard: ['ou_c1', 'ou_c2', 'ou_c28'],
      permiteCriarCards: false,
      campos: [
        { id: 'ou_c28', nome: 'Satisfação do Manifestante', tipo: 'lista', obrigatorio: false, opcoes: ['Muito satisfeito', 'Satisfeito', 'Neutro', 'Insatisfeito', 'Não avaliou'], coresOpcoes: { 'Muito satisfeito': COR.verde, 'Satisfeito': COR.verde, 'Insatisfeito': COR.vermelho } },
        { id: 'ou_c29', nome: 'Data do Encerramento', tipo: 'data', obrigatorio: false },
        { id: 'ou_c30', nome: 'Gerou melhoria no serviço?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'ou_c31', nome: 'Observação Final', tipo: 'texto_longo', obrigatorio: false },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'mover',
      nome: 'Admissível segue para apuração',
      etapaOrigemId: 'ou_e2',
      condicao: { campoId: 'ou_c13', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'ou_e3',
      etapaDestinoSenaoId: 'ou_e6',
    },
    {
      tipo: 'mover',
      nome: 'Pedido de prorrogação move para o novo prazo',
      etapaOrigemId: 'ou_e3',
      condicao: { campoId: 'ou_c19', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'ou_e4',
    },
    {
      tipo: 'mover',
      nome: 'Apuração concluída vai para resposta',
      etapaOrigemId: 'ou_e3',
      condicao: { campoId: 'ou_c20', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'ou_e5',
    },
    {
      tipo: 'mover',
      nome: 'Resposta enviada encerra a manifestação',
      etapaOrigemId: 'ou_e5',
      condicao: { campoId: 'ou_c24', operador: 'preenchido' },
      etapaDestinoId: 'ou_e6',
    },
    {
      tipo: 'exibir_ocultar',
      nome: 'Motivo da inadmissão quando não admitir',
      etapaOrigemId: 'ou_e2',
      condicao: { campoId: 'ou_c13', operador: 'igual', valor: 'Não' },
      camposAfetadosIds: ['ou_c14'],
    },
  ],
  cards: [
    { id: 'ou_card1', titulo: 'Falta de medicamento na UBS Central', etapaId: 'ou_e3', etiquetas: ['Reincidente'], valores: { ou_c1: 'Falta de medicamento na UBS Central', ou_c2: 'Reclamação', ou_c3: 'OUV-2026-01822', ou_c4: 'Portal / Fala.BR', ou_c11: 'Secretaria de Saúde', ou_c12: 'Saúde', ou_c15: 'Alta' } },
    { id: 'ou_card2', titulo: 'Servidor cobrando taxa indevida no setor de tributos', etapaId: 'ou_e4', etiquetas: ['Denúncia', 'Identidade Preservada', 'Prorrogada'], valores: { ou_c1: 'Servidor cobrando taxa indevida no setor de tributos', ou_c2: 'Denúncia', ou_c4: 'Portal / Fala.BR', ou_c7: 'Sim', ou_c11: 'Corregedoria', ou_c12: 'Servidor / conduta', ou_c21: 'Necessária oitiva de testemunhas e análise de gravações.' } },
    { id: 'ou_card3', titulo: 'Buraco na Rua das Acácias há 3 meses', etapaId: 'ou_e5', valores: { ou_c1: 'Buraco na Rua das Acácias há 3 meses', ou_c2: 'Reclamação', ou_c11: 'Secretaria de Obras', ou_c12: 'Obras e infraestrutura', ou_c24: 'Procedente - resolvida' } },
    { id: 'ou_card4', titulo: 'Elogio ao atendimento da creche municipal', etapaId: 'ou_e6', valores: { ou_c1: 'Elogio ao atendimento da creche municipal', ou_c2: 'Elogio', ou_c11: 'Secretaria de Educação', ou_c12: 'Educação', ou_c28: 'Muito satisfeito' } },
    { id: 'ou_card5', titulo: 'Sugestão de agendamento online para o posto', etapaId: 'ou_e2', valores: { ou_c1: 'Sugestão de agendamento online para o posto', ou_c2: 'Sugestão', ou_c4: 'E-mail', ou_c11: 'Secretaria de Saúde', ou_c12: 'Saúde' } },
    { id: 'ou_card6', titulo: 'Reclamação sobre coleta de lixo no Bairro Sul', etapaId: 'ou_e1', valores: { ou_c1: 'Reclamação sobre coleta de lixo no Bairro Sul', ou_c2: 'Reclamação', ou_c4: 'Telefone', ou_c9: 'Coleta não passa há duas semanas nas ruas 12 a 18.' } },
  ],
  tour: [
    '📢 Lei 13.460/2017 no quadro: 30 dias para responder, prorrogáveis por mais 30 com justificativa.',
    '1️⃣ "Recebida": tipo da manifestação e o prazo legal já como vencimento do card.',
    '2️⃣ "Triagem": setor responsável e admissibilidade.',
    '3️⃣ "Apuração": providências registradas pelo setor.',
    '4️⃣ "Prorrogada": coluna própria — prorrogação sem justificativa escrita é irregular.',
    '5️⃣ "Resposta": conclusão e se foi respondida dentro do prazo (indicador da ouvidoria).',
    '📊 "Encerrada": satisfação do manifestante e se a manifestação gerou melhoria no serviço.',
    '🔒 Marque "Identidade Preservada" nas denúncias — o sigilo do denunciante é obrigação legal.',
  ],
  etapasDicas: [
    { quemFaz: 'Ouvidoria', objetivo: 'Registrar com protocolo e prazo legal desde o primeiro contato.' },
    { quemFaz: 'Ouvidor', objetivo: 'Encaminhar rápido — o prazo de 30 dias já está correndo.', automacoes: ['Já configurado: inadmissível encerra com o motivo'] },
    { quemFaz: 'Setor demandado', objetivo: 'Apurar e registrar providências concretas, não respostas genéricas.' },
    { quemFaz: 'Ouvidoria', objetivo: 'Prorrogar com justificativa e comunicar o manifestante.' },
    { quemFaz: 'Ouvidoria', objetivo: 'Responder em linguagem simples e cidadã.' },
    { quemFaz: 'Ouvidoria', objetivo: 'Medir satisfação e transformar reclamação recorrente em melhoria.', conexoes: ['Reclamações repetidas do mesmo assunto indicam falha de processo, não de pessoa'] },
  ],
};

const acessoInformacao: DefaultTemplate = {
  id: 'tpl_acesso_informacao',
  name: 'Acesso à Informação (LAI / e-SIC)',
  descricao: 'Pedidos de informação no rito da Lei 12.527/2011: 20 dias para responder, prorrogáveis por 10, com recurso em instâncias sucessivas. Controla também as negativas por sigilo, que são as que geram recurso.',
  categoria: 'publico',
  icone: '🔎',
  etiquetas: [
    { nome: 'Prorrogado', cor: COR.amarelo },
    { nome: 'Em Recurso', cor: COR.vermelho },
    { nome: 'Informação Sigilosa', cor: COR.cinza },
  ],
  etapas: [
    {
      id: 'ai_e1', nome: 'Pedido Recebido',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['ai_c1', 'ai_c3', 'ai_c6'],
      permiteMoverPara: ['ai_e2'],
      campos: [
        { id: 'ai_c1', nome: 'Objeto do Pedido', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        { id: 'ai_c2', nome: 'Protocolo', tipo: 'texto', obrigatorio: false },
        { id: 'ai_c3', nome: 'Solicitante', tipo: 'texto', obrigatorio: false },
        { id: 'ai_c4', nome: 'Canal', tipo: 'lista', obrigatorio: false, opcoes: ['e-SIC / Fala.BR', 'Presencial', 'E-mail', 'Carta', 'Telefone'] },
        { id: 'ai_c5', nome: 'Perfil do Solicitante', tipo: 'lista', obrigatorio: false, opcoes: ['Cidadão', 'Imprensa', 'Pesquisador', 'Empresa', 'Organização da sociedade civil', 'Outro órgão público'] },
        { id: 'ai_c6', nome: 'Prazo Legal (20 dias)', tipo: 'data_vencimento', obrigatorio: true },
        { id: 'ai_c7', nome: 'Teor do Pedido', tipo: 'texto_longo', obrigatorio: true },
        { id: 'ai_c8', nome: 'Forma de Resposta Preferida', tipo: 'lista', obrigatorio: false, opcoes: ['Sistema eletrônico', 'E-mail', 'Correio', 'Consulta presencial'] },
      ],
    },
    {
      id: 'ai_e2', nome: 'Busca da Informação',
      tempoMaximoHoras: 336,
      camposVisiveisNoCard: ['ai_c1', 'ai_c9', 'ai_c6'],
      permiteMoverPara: ['ai_e3', 'ai_e4'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'ai_c9', nome: 'Setor Detentor da Informação', tipo: 'texto', obrigatorio: true },
        { id: 'ai_c10', nome: 'Informação localizada?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'ai_c11', nome: 'Precisa prorrogar?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'ai_c12', nome: 'Documentos Reunidos', tipo: 'anexo', obrigatorio: false },
        { id: 'ai_c13', nome: 'Observação do Setor', tipo: 'texto_longo', obrigatorio: false },
      ],
    },
    {
      id: 'ai_e3', nome: 'Prorrogado (+10 dias)',
      tempoMaximoHoras: 240,
      camposVisiveisNoCard: ['ai_c1', 'ai_c15'],
      permiteMoverPara: ['ai_e2', 'ai_e4'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'ai_c14', nome: 'Justificativa da Prorrogação', tipo: 'texto_longo', obrigatorio: true },
        { id: 'ai_c15', nome: 'Novo Prazo', tipo: 'data_vencimento', obrigatorio: true },
        { id: 'ai_c16', nome: 'Solicitante comunicado?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'ai_e4', nome: 'Decisão e Resposta',
      tempoMaximoHoras: 72,
      camposVisiveisNoCard: ['ai_c1', 'ai_c17'],
      permiteMoverPara: ['ai_e5', 'ai_e6'],
      permiteCriarCards: false,
      campos: [
        { id: 'ai_c17', nome: 'Decisão', tipo: 'lista', obrigatorio: true, opcoes: ['Acesso concedido', 'Acesso parcial', 'Acesso negado', 'Informação inexistente', 'Órgão não competente'], coresOpcoes: { 'Acesso concedido': COR.verde, 'Acesso parcial': COR.amarelo, 'Acesso negado': COR.vermelho } },
        { id: 'ai_c18', nome: 'Fundamento da Negativa', tipo: 'lista', obrigatorio: false, opcoes: ['Informação sigilosa (art. 23)', 'Dado pessoal (LGPD)', 'Documento preparatório', 'Pedido genérico', 'Pedido desproporcional', 'Exige trabalho adicional'], coresOpcoes: { 'Informação sigilosa (art. 23)': COR.cinza, 'Dado pessoal (LGPD)': COR.cinza } },
        { id: 'ai_c19', nome: 'Resposta ao Solicitante', tipo: 'texto_longo', obrigatorio: true },
        { id: 'ai_c20', nome: 'Anexos da Resposta', tipo: 'anexo', obrigatorio: false },
        { id: 'ai_c21', nome: 'Data da Resposta', tipo: 'data', obrigatorio: false },
        { id: 'ai_c22', nome: 'Respondido no prazo?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'ai_e5', nome: 'Recurso',
      tempoMaximoHoras: 120,
      camposVisiveisNoCard: ['ai_c1', 'ai_c23', 'ai_c25'],
      permiteMoverPara: ['ai_e6', 'ai_e4'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'ai_c23', nome: 'Instância', tipo: 'lista', obrigatorio: true, opcoes: ['1ª instância (autoridade superior)', '2ª instância (dirigente máximo)', '3ª instância (CGU/controle)', 'Comissão Mista de Reavaliação'], coresOpcoes: { '3ª instância (CGU/controle)': COR.vermelho } },
        { id: 'ai_c24', nome: 'Razões do Recurso', tipo: 'texto_longo', obrigatorio: false },
        { id: 'ai_c25', nome: 'Prazo da Decisão do Recurso', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'ai_c26', nome: 'Resultado', tipo: 'lista', obrigatorio: false, opcoes: ['Provido (acesso concedido)', 'Parcialmente provido', 'Improvido (negativa mantida)'], coresOpcoes: { 'Provido (acesso concedido)': COR.verde, 'Improvido (negativa mantida)': COR.cinza } },
        { id: 'ai_c27', nome: 'Decisão do Recurso', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'ai_e6', nome: 'Concluído',
      camposVisiveisNoCard: ['ai_c1', 'ai_c17', 'ai_c28'],
      permiteCriarCards: false,
      campos: [
        { id: 'ai_c28', nome: 'Data do Encerramento', tipo: 'data', obrigatorio: false },
        { id: 'ai_c29', nome: 'Vai para transparência ativa?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'ai_c30', nome: 'Observação Final', tipo: 'texto_longo', obrigatorio: false },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'mover',
      nome: 'Prorrogação move para o novo prazo',
      etapaOrigemId: 'ai_e2',
      condicao: { campoId: 'ai_c11', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'ai_e3',
    },
    {
      tipo: 'mover',
      nome: 'Informação localizada vai para decisão',
      etapaOrigemId: 'ai_e2',
      condicao: { campoId: 'ai_c10', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'ai_e4',
    },
    {
      tipo: 'mover',
      nome: 'Acesso concedido conclui o pedido',
      etapaOrigemId: 'ai_e4',
      condicao: { campoId: 'ai_c17', operador: 'igual', valor: 'Acesso concedido' },
      etapaDestinoId: 'ai_e6',
      etapaDestinoSenaoId: 'ai_e5',
    },
    {
      tipo: 'exibir_ocultar',
      nome: 'Fundamento da negativa quando nega o acesso',
      etapaOrigemId: 'ai_e4',
      condicao: { campoId: 'ai_c17', operador: 'igual', valor: 'Acesso negado' },
      camposAfetadosIds: ['ai_c18'],
    },
  ],
  cards: [
    { id: 'ai_card1', titulo: 'Cópia dos contratos de limpeza urbana de 2025', etapaId: 'ai_e2', valores: { ai_c1: 'Cópia dos contratos de limpeza urbana de 2025', ai_c2: 'SIC-2026-00412', ai_c4: 'e-SIC / Fala.BR', ai_c5: 'Imprensa', ai_c9: 'Secretaria de Administração' } },
    { id: 'ai_card2', titulo: 'Folha de pagamento detalhada por servidor', etapaId: 'ai_e5', etiquetas: ['Em Recurso', 'Informação Sigilosa'], valores: { ai_c1: 'Folha de pagamento detalhada por servidor', ai_c2: 'SIC-2026-00388', ai_c5: 'Cidadão', ai_c17: 'Acesso parcial', ai_c18: 'Dado pessoal (LGPD)', ai_c23: '1ª instância (autoridade superior)' } },
    { id: 'ai_card3', titulo: 'Lista de espera de cirurgias eletivas', etapaId: 'ai_e3', etiquetas: ['Prorrogado'], valores: { ai_c1: 'Lista de espera de cirurgias eletivas', ai_c2: 'SIC-2026-00401', ai_c5: 'Organização da sociedade civil', ai_c9: 'Secretaria de Saúde', ai_c14: 'Necessária consolidação de dados de 14 unidades de saúde.' } },
    { id: 'ai_card4', titulo: 'Notas fiscais da merenda escolar de julho', etapaId: 'ai_e6', valores: { ai_c1: 'Notas fiscais da merenda escolar de julho', ai_c2: 'SIC-2026-00375', ai_c5: 'Cidadão', ai_c17: 'Acesso concedido', ai_c22: 'Sim', ai_c29: 'Sim' } },
    { id: 'ai_card5', titulo: 'Estudo de impacto do novo plano diretor', etapaId: 'ai_e4', valores: { ai_c1: 'Estudo de impacto do novo plano diretor', ai_c2: 'SIC-2026-00420', ai_c5: 'Pesquisador', ai_c17: 'Acesso concedido' } },
    { id: 'ai_card6', titulo: 'Relação de veículos da frota e consumo', etapaId: 'ai_e1', valores: { ai_c1: 'Relação de veículos da frota e consumo', ai_c2: 'SIC-2026-00431', ai_c4: 'e-SIC / Fala.BR', ai_c5: 'Cidadão', ai_c7: 'Solicito a relação de veículos oficiais e o consumo de combustível por veículo em 2026.' } },
  ],
  tour: [
    '🔎 LAI com prazo visível: 20 dias, prorrogáveis por 10, e recurso em instâncias.',
    '1️⃣ "Pedido Recebido": teor do pedido e prazo legal como vencimento.',
    '2️⃣ "Busca da Informação": setor detentor identificado.',
    '3️⃣ "Prorrogado": coluna própria com justificativa — prorrogar sem motivo escrito gera apontamento.',
    '4️⃣ "Decisão": negou? o fundamento legal aparece obrigatoriamente.',
    '5️⃣ "Recurso": instância e prazo controlados — negativa mal fundamentada volta como recurso.',
    '📊 "Concluído": marque o que deve virar transparência ativa — pedido repetido é sinal de dado que deveria estar publicado.',
  ],
  etapasDicas: [
    { quemFaz: 'SIC / Ouvidoria', objetivo: 'Registrar sem exigir motivação do pedido — a lei proíbe.' },
    { quemFaz: 'Setor detentor', objetivo: 'Localizar e reunir a informação dentro do prazo.', automacoes: ['Já configurado: pedido de prorrogação abre a coluna própria'] },
    { quemFaz: 'SIC', objetivo: 'Comunicar a prorrogação ao solicitante — não basta prorrogar internamente.' },
    { quemFaz: 'Autoridade / SIC', objetivo: 'Negativa exige fundamento legal expresso.', automacoes: ['Já configurado: o que não é concedido cai em recurso'] },
    { quemFaz: 'Autoridade superior', objetivo: 'Julgar o recurso dentro do prazo da instância.' },
    { quemFaz: 'SIC / Transparência', objetivo: 'Publicar proativamente o que é muito pedido.' },
  ],
};

const solicitacoesCidadao: DefaultTemplate = {
  id: 'tpl_solicitacoes_cidadao',
  name: 'Solicitações do Cidadão (Zeladoria)',
  descricao: 'Tapa-buraco, poda de árvore, lâmpada queimada, entulho, animal na via: o pedido do morador vira ordem de serviço com equipe, prazo e foto do antes e depois. O 156 da prefeitura em quadro.',
  categoria: 'publico',
  icone: '🛠️',
  etiquetas: [
    { nome: 'Risco à Segurança', cor: COR.vermelho },
    { nome: 'Reincidente', cor: COR.amarelo },
    { nome: 'Demanda de Vereador', cor: COR.roxo },
  ],
  etapas: [
    {
      id: 'sc_e1', nome: 'Solicitação Registrada',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['sc_c1', 'sc_c2', 'sc_c4'],
      permiteMoverPara: ['sc_e2'],
      campos: [
        { id: 'sc_c1', nome: 'Solicitação', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        { id: 'sc_c2', nome: 'Tipo de Serviço', tipo: 'lista', obrigatorio: true, opcoes: ['Tapa-buraco', 'Poda de árvore', 'Iluminação pública', 'Coleta de entulho', 'Limpeza de boca de lobo', 'Roçada de terreno', 'Sinalização de trânsito', 'Animal na via', 'Vazamento de água', 'Calçada danificada'], coresOpcoes: { 'Iluminação pública': COR.amarelo, 'Vazamento de água': COR.azul } },
        { id: 'sc_c3', nome: 'Endereço / Referência', tipo: 'texto', obrigatorio: true },
        { id: 'sc_c4', nome: 'Bairro', tipo: 'texto', obrigatorio: false },
        { id: 'sc_c5', nome: 'Solicitante', tipo: 'texto', obrigatorio: false },
        { id: 'sc_c6', nome: 'Telefone', tipo: 'telefone', obrigatorio: false },
        { id: 'sc_c7', nome: 'Canal', tipo: 'lista', obrigatorio: false, opcoes: ['Telefone 156', 'Aplicativo', 'Portal', 'Presencial', 'WhatsApp', 'Gabinete / vereador'] },
        { id: 'sc_c8', nome: 'Descrição', tipo: 'texto_longo', obrigatorio: false },
        { id: 'sc_c9', nome: 'Foto do Problema', tipo: 'anexo', obrigatorio: false },
        { id: 'sc_c10', nome: 'Protocolo', tipo: 'texto', obrigatorio: false },
      ],
    },
    {
      id: 'sc_e2', nome: 'Triagem e Vistoria',
      tempoMaximoHoras: 72,
      camposVisiveisNoCard: ['sc_c1', 'sc_c11', 'sc_c12'],
      permiteMoverPara: ['sc_e3', 'sc_e6'],
      permiteCriarCards: false,
      campos: [
        { id: 'sc_c11', nome: 'Secretaria Responsável', tipo: 'texto', obrigatorio: true },
        { id: 'sc_c12', nome: 'Prioridade', tipo: 'lista', obrigatorio: false, opcoes: ['Baixa', 'Média', 'Alta', 'Urgente'], coresOpcoes: CORES_PRIORIDADE },
        { id: 'sc_c13', nome: 'É competência do município?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'sc_c14', nome: 'Encaminhado para', tipo: 'texto', obrigatorio: false },
        { id: 'sc_c15', nome: 'Laudo da Vistoria', tipo: 'texto_longo', obrigatorio: false },
        { id: 'sc_c16', nome: 'Prazo de Atendimento', tipo: 'data_vencimento', obrigatorio: false },
      ],
    },
    {
      id: 'sc_e3', nome: 'Programado',
      camposVisiveisNoCard: ['sc_c1', 'sc_c17', 'sc_c16'],
      permiteMoverPara: ['sc_e4'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'sc_c17', nome: 'Equipe Designada', tipo: 'texto', obrigatorio: false },
        { id: 'sc_c18', nome: 'Data Programada', tipo: 'data', obrigatorio: false },
        { id: 'sc_c19', nome: 'Materiais Necessários', tipo: 'texto_longo', obrigatorio: false },
        { id: 'sc_c20', nome: 'Tem material e equipe?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'sc_e4', nome: 'Em Execução',
      tempoMaximoHoras: 120,
      camposVisiveisNoCard: ['sc_c1', 'sc_c17', 'sc_c21'],
      permiteMoverPara: ['sc_e5'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'sc_c21', nome: 'Data de Início', tipo: 'data', obrigatorio: false },
        { id: 'sc_c22', nome: 'Serviço Executado', tipo: 'texto_longo', obrigatorio: false },
        { id: 'sc_c23', nome: 'Foto Depois', tipo: 'anexo', obrigatorio: false },
        { id: 'sc_c24', nome: 'Custo Estimado', tipo: 'valor', obrigatorio: false },
        { id: 'sc_c25', nome: 'Serviço concluído?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'sc_e5', nome: 'Concluído',
      camposVisiveisNoCard: ['sc_c1', 'sc_c2', 'sc_c26'],
      permiteCriarCards: false,
      campos: [
        { id: 'sc_c26', nome: 'Data da Conclusão', tipo: 'data', obrigatorio: false },
        { id: 'sc_c27', nome: 'Cidadão comunicado?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'sc_c28', nome: 'Atendido no prazo?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'sc_c29', nome: 'Observação', tipo: 'texto_longo', obrigatorio: false },
      ],
    },
    {
      id: 'sc_e6', nome: 'Não Atendida',
      camposVisiveisNoCard: ['sc_c1', 'sc_c30'],
      permiteMoverPara: ['sc_e2'],
      permiteCriarCards: false,
      campos: [
        { id: 'sc_c30', nome: 'Motivo', tipo: 'lista', obrigatorio: true, opcoes: ['Não é competência do município', 'Área particular', 'Sem previsão orçamentária', 'Duplicidade de solicitação', 'Local não localizado', 'Já executado anteriormente'], coresOpcoes: { 'Não é competência do município': COR.cinza, 'Área particular': COR.cinza } },
        { id: 'sc_c31', nome: 'Orientação ao Cidadão', tipo: 'texto_longo', obrigatorio: false },
        { id: 'sc_c32', nome: 'Cidadão informado?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'mover',
      nome: 'Competência do município vira ordem de serviço',
      etapaOrigemId: 'sc_e2',
      condicao: { campoId: 'sc_c13', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'sc_e3',
      etapaDestinoSenaoId: 'sc_e6',
    },
    {
      tipo: 'mover',
      nome: 'Com equipe e material começa a execução',
      etapaOrigemId: 'sc_e3',
      condicao: { campoId: 'sc_c20', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'sc_e4',
    },
    {
      tipo: 'mover',
      nome: 'Serviço concluído fecha a solicitação',
      etapaOrigemId: 'sc_e4',
      condicao: { campoId: 'sc_c25', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'sc_e5',
    },
    {
      tipo: 'exibir_ocultar',
      nome: 'Encaminhamento quando não é competência municipal',
      etapaOrigemId: 'sc_e2',
      condicao: { campoId: 'sc_c13', operador: 'igual', valor: 'Não' },
      camposAfetadosIds: ['sc_c14'],
    },
  ],
  cards: [
    { id: 'sc_card1', titulo: 'Buraco na Rua das Acácias, 340', etapaId: 'sc_e4', etiquetas: ['Risco à Segurança', 'Reincidente'], valores: { sc_c1: 'Buraco na Rua das Acácias, 340', sc_c2: 'Tapa-buraco', sc_c3: 'Rua das Acácias, 340', sc_c4: 'Bairro Jardim', sc_c7: 'Telefone 156', sc_c11: 'Secretaria de Obras', sc_c12: 'Alta', sc_c17: 'Equipe de pavimentação 02' } },
    { id: 'sc_card2', titulo: 'Árvore com risco de queda na praça central', etapaId: 'sc_e3', etiquetas: ['Risco à Segurança'], valores: { sc_c1: 'Árvore com risco de queda na praça central', sc_c2: 'Poda de árvore', sc_c3: 'Praça Central', sc_c7: 'Aplicativo', sc_c11: 'Secretaria de Meio Ambiente', sc_c12: 'Urgente', sc_c17: 'Equipe de poda' } },
    { id: 'sc_card3', titulo: 'Três postes apagados na Av. Brasil', etapaId: 'sc_e5', valores: { sc_c1: 'Três postes apagados na Av. Brasil', sc_c2: 'Iluminação pública', sc_c3: 'Av. Brasil, entre 900 e 1200', sc_c11: 'Secretaria de Obras', sc_c27: 'Sim', sc_c28: 'Sim' } },
    { id: 'sc_card4', titulo: 'Entulho descartado na Rua Sete', etapaId: 'sc_e2', etiquetas: ['Demanda de Vereador'], valores: { sc_c1: 'Entulho descartado na Rua Sete', sc_c2: 'Coleta de entulho', sc_c3: 'Rua Sete, esquina com Rua XV', sc_c7: 'Gabinete / vereador', sc_c11: 'Secretaria de Serviços Urbanos' } },
    { id: 'sc_card5', titulo: 'Boca de lobo entupida no Bairro Sul', etapaId: 'sc_e1', valores: { sc_c1: 'Boca de lobo entupida no Bairro Sul', sc_c2: 'Limpeza de boca de lobo', sc_c3: 'Rua das Palmas, 88', sc_c4: 'Bairro Sul', sc_c7: 'WhatsApp' } },
    { id: 'sc_card6', titulo: 'Poda de árvore em quintal residencial', etapaId: 'sc_e6', valores: { sc_c1: 'Poda de árvore em quintal residencial', sc_c2: 'Poda de árvore', sc_c3: 'Rua das Flores, 21', sc_c30: 'Área particular', sc_c31: 'Poda em área particular é responsabilidade do proprietário. Orientado a solicitar autorização ambiental.' } },
  ],
  tour: [
    '🛠️ O 156 da prefeitura vira quadro: pedido do morador com endereço, foto e prazo.',
    '1️⃣ "Solicitação Registrada": endereço, bairro e foto do problema.',
    '2️⃣ "Triagem e Vistoria": não é competência do município? o card orienta o cidadão e encerra.',
    '3️⃣ "Programado": equipe designada e material conferido antes de sair.',
    '4️⃣ "Em Execução": foto do depois anexada — é o que responde o cidadão e a imprensa.',
    '✅ "Concluído": marca se foi atendido no prazo — indicador direto de gestão.',
    '📊 Filtre por bairro e tipo de serviço para ver onde a demanda se concentra.',
  ],
  etapasDicas: [
    { quemFaz: 'Central de atendimento / 156', objetivo: 'Registrar com endereço preciso — endereço vago não vira serviço.' },
    { quemFaz: 'Secretaria competente', objetivo: 'Vistoriar e priorizar por risco.', automacoes: ['Já configurado: fora da competência municipal orienta e encerra'] },
    { quemFaz: 'Coordenação de equipes', objetivo: 'Programar por região para economizar deslocamento.' },
    { quemFaz: 'Equipe de campo', objetivo: 'Registrar o antes e o depois com foto.' },
    { quemFaz: 'Central de atendimento', objetivo: 'Avisar o cidadão — atendimento não comunicado vira nova reclamação.', conexoes: ['Ligue à "Ouvidoria" quando o cidadão reclamar do prazo'] },
    { quemFaz: 'Central de atendimento', objetivo: 'Orientar o cidadão sobre quem resolve.' },
  ],
};

const conveniosTransferencias: DefaultTemplate = {
  id: 'tpl_convenios_transferencias',
  name: 'Convênios e Transferências Voluntárias',
  descricao: 'Captação de recurso de emenda ou ministério: proposta, plano de trabalho, celebração, liberação de parcelas, execução com contrapartida e prestação de contas. O que mais trava prefeitura é a prestação de contas — aqui ela tem prazo e coluna.',
  categoria: 'publico',
  icone: '🤝',
  etiquetas: [
    { nome: 'Emenda Parlamentar', cor: COR.ciano },
    { nome: 'Contrapartida Pendente', cor: COR.amarelo },
    { nome: 'Prazo de Prestação', cor: COR.vermelho },
  ],
  etapas: [
    {
      id: 'cv_e1', nome: 'Prospecção / Proposta',
      tempoMaximoHoras: 240,
      camposVisiveisNoCard: ['cv_c1', 'cv_c3', 'cv_c5'],
      permiteMoverPara: ['cv_e2'],
      campos: [
        { id: 'cv_c1', nome: 'Objeto do Convênio', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        { id: 'cv_c2', nome: 'Concedente / Órgão Repassador', tipo: 'texto', obrigatorio: true },
        { id: 'cv_c3', nome: 'Origem do Recurso', tipo: 'lista', obrigatorio: true, opcoes: ['Emenda parlamentar individual', 'Emenda de bancada', 'Programa federal', 'Programa estadual', 'Fundo setorial', 'Organismo internacional'], coresOpcoes: { 'Emenda parlamentar individual': COR.ciano, 'Programa federal': COR.azul } },
        { id: 'cv_c4', nome: 'Nº da Proposta / Transferegov', tipo: 'texto', obrigatorio: false },
        { id: 'cv_c5', nome: 'Valor Pleiteado', tipo: 'valor', obrigatorio: true },
        { id: 'cv_c6', nome: 'Contrapartida do Município', tipo: 'valor', obrigatorio: false },
        { id: 'cv_c7', nome: 'Secretaria Proponente', tipo: 'texto', obrigatorio: false },
        { id: 'cv_c8', nome: 'Prazo de Envio da Proposta', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'cv_c9', nome: 'Documentos da Proposta', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'cv_e2', nome: 'Plano de Trabalho',
      tempoMaximoHoras: 336,
      camposVisiveisNoCard: ['cv_c1', 'cv_c11'],
      permiteMoverPara: ['cv_e3', 'cv_e7'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'cv_c10', nome: 'Itens do Plano de Trabalho', tipo: 'checkbox_multi', obrigatorio: false, opcoes: ['Metas e etapas', 'Cronograma de execução', 'Plano de aplicação de recursos', 'Cronograma de desembolso', 'Projeto básico / TR', 'Declaração de contrapartida', 'Certidões do município', 'Comprovação de titularidade da área'] },
        { id: 'cv_c11', nome: 'Plano de trabalho aprovado?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'cv_c12', nome: 'Diligências do Concedente', tipo: 'texto_longo', obrigatorio: false },
        { id: 'cv_c13', nome: 'Prazo para Responder Diligência', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'cv_c14', nome: 'Plano de Trabalho (arquivo)', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'cv_e3', nome: 'Celebração',
      tempoMaximoHoras: 336,
      camposVisiveisNoCard: ['cv_c1', 'cv_c15', 'cv_c17'],
      permiteMoverPara: ['cv_e4'],
      permiteCriarCards: false,
      campos: [
        { id: 'cv_c15', nome: 'Nº do Convênio / Termo', tipo: 'texto', obrigatorio: false },
        { id: 'cv_c16', nome: 'Data da Assinatura', tipo: 'data', obrigatorio: false },
        { id: 'cv_c17', nome: 'Vigência até', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'cv_c18', nome: 'Valor Conveniado', tipo: 'valor', obrigatorio: false },
        { id: 'cv_c19', nome: 'Conta Bancária Específica Aberta', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'cv_c20', nome: 'Termo Assinado e Publicado', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'cv_e4', nome: 'Execução',
      camposVisiveisNoCard: ['cv_c1', 'cv_c21', 'cv_c17'],
      permiteMoverPara: ['cv_e5', 'cv_e7'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'cv_c21', nome: 'Execução Física (%)', tipo: 'progresso', obrigatorio: false },
        { id: 'cv_c22', nome: 'Valor Liberado até agora', tipo: 'valor', obrigatorio: false },
        { id: 'cv_c23', nome: 'Contrapartida Aportada', tipo: 'valor', obrigatorio: false },
        { id: 'cv_c24', nome: 'Parcelas Recebidas', tipo: 'numero', obrigatorio: false },
        { id: 'cv_c25', nome: 'Precisa de aditivo de prazo?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'cv_c26', nome: 'Execução concluída?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'cv_c27', nome: 'Relatórios de Execução', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'cv_e5', nome: 'Prestação de Contas',
      tempoMaximoHoras: 720,
      camposVisiveisNoCard: ['cv_c1', 'cv_c29', 'cv_c30'],
      permiteMoverPara: ['cv_e6', 'cv_e7'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'cv_c28', nome: 'Peças da Prestação', tipo: 'checkbox_multi', obrigatorio: false, opcoes: ['Relatório de cumprimento do objeto', 'Relação de pagamentos', 'Extrato bancário da conta específica', 'Comprovante de devolução de saldo', 'Notas fiscais', 'Relatório fotográfico', 'Termo de aceitação definitiva'] },
        { id: 'cv_c29', nome: 'Prazo de Prestação de Contas', tipo: 'data_vencimento', obrigatorio: true },
        { id: 'cv_c30', nome: 'Prestação enviada?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'cv_c31', nome: 'Saldo a Devolver', tipo: 'valor', obrigatorio: false },
        { id: 'cv_c32', nome: 'Prestação de Contas (arquivo)', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'cv_e6', nome: 'Aprovado / Encerrado',
      camposVisiveisNoCard: ['cv_c1', 'cv_c33', 'cv_c18'],
      permiteCriarCards: false,
      campos: [
        { id: 'cv_c33', nome: 'Resultado da Análise', tipo: 'lista', obrigatorio: false, opcoes: ['Aprovada', 'Aprovada com ressalvas', 'Aguardando análise do concedente'], coresOpcoes: { 'Aprovada': COR.verde, 'Aprovada com ressalvas': COR.amarelo } },
        { id: 'cv_c34', nome: 'Data do Encerramento', tipo: 'data', obrigatorio: false },
        { id: 'cv_c35', nome: 'Parecer do Concedente', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'cv_e7', nome: 'Pendência / Inadimplência',
      camposVisiveisNoCard: ['cv_c1', 'cv_c36', 'cv_c37'],
      permiteMoverPara: ['cv_e2', 'cv_e4', 'cv_e5'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'cv_c36', nome: 'Situação', tipo: 'lista', obrigatorio: true, opcoes: ['Proposta rejeitada', 'Diligência não respondida', 'Execução paralisada', 'Prestação de contas em atraso', 'Prestação reprovada', 'Município inadimplente (CAUC)', 'Recurso a devolver'], coresOpcoes: { 'Município inadimplente (CAUC)': COR.vermelho, 'Prestação reprovada': COR.vermelho, 'Recurso a devolver': COR.vermelho } },
        { id: 'cv_c37', nome: 'Prazo para Regularizar', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'cv_c38', nome: 'Providências', tipo: 'texto_longo', obrigatorio: false },
        { id: 'cv_c39', nome: 'Valor em Risco', tipo: 'valor', obrigatorio: false },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'mover',
      nome: 'Plano aprovado segue para celebração',
      etapaOrigemId: 'cv_e2',
      condicao: { campoId: 'cv_c11', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'cv_e3',
      etapaDestinoSenaoId: 'cv_e7',
    },
    {
      tipo: 'mover',
      nome: 'Execução concluída abre a prestação de contas',
      etapaOrigemId: 'cv_e4',
      condicao: { campoId: 'cv_c26', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'cv_e5',
    },
    {
      tipo: 'mover',
      nome: 'Prestação enviada encerra o convênio',
      etapaOrigemId: 'cv_e5',
      condicao: { campoId: 'cv_c30', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'cv_e6',
      etapaDestinoSenaoId: 'cv_e7',
    },
    {
      tipo: 'exibir_ocultar',
      nome: 'Diligências quando o plano não é aprovado',
      etapaOrigemId: 'cv_e2',
      condicao: { campoId: 'cv_c11', operador: 'igual', valor: 'Não' },
      camposAfetadosIds: ['cv_c12', 'cv_c13'],
    },
  ],
  cards: [
    { id: 'cv_card1', titulo: 'Aquisição de ambulância - emenda federal', etapaId: 'cv_e4', etiquetas: ['Emenda Parlamentar'], valores: { cv_c1: 'Aquisição de ambulância - emenda federal', cv_c2: 'Ministério da Saúde', cv_c3: 'Emenda parlamentar individual', cv_c4: '2026/0084412', cv_c5: 380000, cv_c6: 20000, cv_c15: 'TC 4412/2026', cv_c21: 40, cv_c22: 380000 } },
    { id: 'cv_card2', titulo: 'Pavimentação do Bairro Sul', etapaId: 'cv_e2', etiquetas: ['Contrapartida Pendente'], valores: { cv_c1: 'Pavimentação do Bairro Sul', cv_c2: 'Ministério das Cidades', cv_c3: 'Programa federal', cv_c5: 4200000, cv_c6: 420000, cv_c11: 'Não', cv_c12: 'Concedente pediu ART do projeto e comprovação de titularidade das vias.' } },
    { id: 'cv_card3', titulo: 'Reforma da quadra da Escola Central', etapaId: 'cv_e5', etiquetas: ['Prazo de Prestação'], valores: { cv_c1: 'Reforma da quadra da Escola Central', cv_c2: 'Secretaria Estadual de Educação', cv_c3: 'Programa estadual', cv_c5: 620000, cv_c18: 620000, cv_c29: '2026-09-15', cv_c30: 'Não' } },
    { id: 'cv_card4', titulo: 'Equipamentos para a UBS Central', etapaId: 'cv_e6', valores: { cv_c1: 'Equipamentos para a UBS Central', cv_c2: 'Ministério da Saúde', cv_c3: 'Emenda parlamentar individual', cv_c5: 250000, cv_c18: 250000, cv_c33: 'Aprovada' } },
    { id: 'cv_card5', titulo: 'Kits de material escolar', etapaId: 'cv_e1', valores: { cv_c1: 'Kits de material escolar', cv_c2: 'FNDE', cv_c3: 'Programa federal', cv_c5: 180000 } },
    { id: 'cv_card6', titulo: 'Praça e academia ao ar livre', etapaId: 'cv_e7', valores: { cv_c1: 'Praça e academia ao ar livre', cv_c2: 'Ministério do Esporte', cv_c3: 'Emenda de bancada', cv_c5: 900000, cv_c36: 'Prestação de contas em atraso', cv_c39: 900000, cv_c38: 'Prestação vencida há 40 dias. Risco de inscrição no CAUC e bloqueio de novos convênios.' } },
  ],
  tour: [
    '🤝 Convênio da proposta à prestação de contas — que é onde prefeitura costuma se perder.',
    '1️⃣ "Proposta": origem do recurso, valor pleiteado e contrapartida do município.',
    '2️⃣ "Plano de Trabalho": checklist das peças e prazo das diligências do concedente.',
    '3️⃣ "Celebração": vigência vira vencimento — convênio vencido sem aditivo é dinheiro devolvido.',
    '4️⃣ "Execução": percentual físico, parcelas recebidas e contrapartida aportada.',
    '5️⃣ "Prestação de Contas": prazo próprio e checklist das peças exigidas.',
    '⚠️ "Pendência / Inadimplência": com valor em risco visível — é o que impede novos convênios pelo CAUC.',
  ],
  etapasDicas: [
    { quemFaz: 'Captação de Recursos / Gabinete', objetivo: 'Registrar toda oportunidade de emenda e programa.' },
    { quemFaz: 'Secretaria proponente + Engenharia', objetivo: 'Montar plano de trabalho completo — diligência atrasa meses.', automacoes: ['Já configurado: plano reprovado vai para pendência'] },
    { quemFaz: 'Jurídico / Gabinete', objetivo: 'Celebrar e abrir conta específica do convênio.' },
    { quemFaz: 'Secretaria executora', objetivo: 'Executar dentro da vigência e aportar a contrapartida.', conexoes: ['Ligue à "Execução da Despesa" para os pagamentos do convênio'] },
    { quemFaz: 'Contabilidade / Convênios', objetivo: 'Prestar contas antes do prazo — atraso inscreve o município no CAUC.' },
    { quemFaz: 'Convênios', objetivo: 'Guardar o parecer de aprovação.' },
    { quemFaz: 'Convênios / Controle Interno', objetivo: 'Tratar pendência antes de virar devolução de recurso.' },
  ],
};

const fiscalizacaoContratos: DefaultTemplate = {
  id: 'tpl_fiscalizacao_contratos',
  name: 'Fiscalização de Contratos Públicos',
  descricao: 'A vida do contrato depois de assinado: designação do fiscal, medições e atestos, ocorrências e notificações ao contratado, sanções, aditivos e reajustes, até o encerramento com termo de recebimento definitivo.',
  categoria: 'publico',
  icone: '👁️',
  etiquetas: [
    { nome: 'Vigência Crítica', cor: COR.vermelho },
    { nome: 'Com Notificação', cor: COR.amarelo },
    { nome: 'Em Sanção', cor: COR.roxo },
  ],
  etapas: [
    {
      id: 'fc_e1', nome: 'Contrato Vigente',
      camposVisiveisNoCard: ['fc_c1', 'fc_c4', 'fc_c6'],
      permiteMoverPara: ['fc_e2', 'fc_e3', 'fc_e4', 'fc_e6'],
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'fc_c1', nome: 'Objeto do Contrato', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        { id: 'fc_c2', nome: 'Nº do Contrato', tipo: 'texto', obrigatorio: true },
        { id: 'fc_c3', nome: 'Contratado', tipo: 'texto', obrigatorio: true },
        { id: 'fc_c4', nome: 'Fiscal Designado', tipo: 'texto', obrigatorio: true },
        { id: 'fc_c5', nome: 'Gestor do Contrato', tipo: 'texto', obrigatorio: false },
        { id: 'fc_c6', nome: 'Vigência até', tipo: 'data_vencimento', obrigatorio: true },
        { id: 'fc_c7', nome: 'Valor Contratado', tipo: 'valor', obrigatorio: false },
        { id: 'fc_c8', nome: 'Valor Já Executado', tipo: 'valor', obrigatorio: false },
        { id: 'fc_c9', nome: 'Portaria de Designação', tipo: 'anexo', obrigatorio: false },
        { id: 'fc_c10', nome: 'Contrato e Aditivos', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'fc_e2', nome: 'Medição / Atesto',
      tempoMaximoHoras: 120,
      camposVisiveisNoCard: ['fc_c1', 'fc_c11', 'fc_c13'],
      permiteMoverPara: ['fc_e1', 'fc_e3'],
      permiteCriarCards: false,
      campos: [
        { id: 'fc_c11', nome: 'Período / Competência', tipo: 'texto', obrigatorio: false },
        { id: 'fc_c12', nome: 'Serviço entregue conforme?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'fc_c13', nome: 'Valor da Medição', tipo: 'valor', obrigatorio: false },
        { id: 'fc_c14', nome: 'Glosa Aplicada', tipo: 'valor', obrigatorio: false },
        { id: 'fc_c15', nome: 'Relatório de Fiscalização', tipo: 'anexo', obrigatorio: false },
        { id: 'fc_c16', nome: 'Data do Atesto', tipo: 'data', obrigatorio: false },
      ],
    },
    {
      id: 'fc_e3', nome: 'Ocorrência / Notificação',
      tempoMaximoHoras: 168,
      camposVisiveisNoCard: ['fc_c1', 'fc_c17', 'fc_c19'],
      permiteMoverPara: ['fc_e1', 'fc_e5'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'fc_c17', nome: 'Tipo de Ocorrência', tipo: 'lista', obrigatorio: true, opcoes: ['Atraso na execução', 'Qualidade insuficiente', 'Equipe insuficiente', 'Documentação trabalhista pendente', 'Descumprimento de cláusula', 'Recusa de execução'], coresOpcoes: { 'Recusa de execução': COR.vermelho, 'Documentação trabalhista pendente': COR.vermelho } },
        { id: 'fc_c18', nome: 'Descrição da Ocorrência', tipo: 'texto_longo', obrigatorio: true },
        { id: 'fc_c19', nome: 'Prazo de Defesa do Contratado', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'fc_c20', nome: 'Notificação Enviada', tipo: 'anexo', obrigatorio: false },
        { id: 'fc_c21', nome: 'Contratado regularizou?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'fc_c22', nome: 'Defesa do Contratado', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'fc_e4', nome: 'Aditivo / Reajuste',
      tempoMaximoHoras: 336,
      camposVisiveisNoCard: ['fc_c1', 'fc_c23', 'fc_c25'],
      permiteMoverPara: ['fc_e1'],
      permiteCriarCards: false,
      campos: [
        { id: 'fc_c23', nome: 'Tipo', tipo: 'lista', obrigatorio: true, opcoes: ['Prorrogação de prazo', 'Acréscimo de valor', 'Supressão', 'Reajuste / repactuação', 'Reequilíbrio econômico', 'Apostilamento'], coresOpcoes: { 'Prorrogação de prazo': COR.azul, 'Acréscimo de valor': COR.verde } },
        { id: 'fc_c24', nome: 'Justificativa', tipo: 'texto_longo', obrigatorio: true },
        { id: 'fc_c25', nome: 'Valor do Aditivo', tipo: 'valor', obrigatorio: false },
        { id: 'fc_c26', nome: '% sobre o Contrato Original', tipo: 'numero', obrigatorio: false },
        { id: 'fc_c27', nome: 'Nova Vigência', tipo: 'data', obrigatorio: false },
        { id: 'fc_c28', nome: 'Aditivo assinado?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'fc_c29', nome: 'Termo Aditivo', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'fc_e5', nome: 'Processo Sancionatório',
      tempoMaximoHoras: 480,
      camposVisiveisNoCard: ['fc_c1', 'fc_c30', 'fc_c32'],
      permiteMoverPara: ['fc_e1', 'fc_e6'],
      permiteCriarCards: false,
      campos: [
        { id: 'fc_c30', nome: 'Sanção Proposta', tipo: 'lista', obrigatorio: true, opcoes: ['Advertência', 'Multa', 'Impedimento de licitar', 'Declaração de inidoneidade', 'Rescisão contratual'], coresOpcoes: { 'Advertência': COR.amarelo, 'Multa': COR.amarelo, 'Declaração de inidoneidade': COR.vermelho, 'Rescisão contratual': COR.vermelho } },
        { id: 'fc_c31', nome: 'Valor da Multa', tipo: 'valor', obrigatorio: false },
        { id: 'fc_c32', nome: 'Prazo de Ampla Defesa', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'fc_c33', nome: 'Decisão da Autoridade', tipo: 'texto_longo', obrigatorio: false },
        { id: 'fc_c34', nome: 'Sanção aplicada?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'fc_c35', nome: 'Registrada no cadastro de sanções?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'fc_e6', nome: 'Encerrado',
      camposVisiveisNoCard: ['fc_c1', 'fc_c3', 'fc_c36'],
      permiteCriarCards: false,
      campos: [
        { id: 'fc_c36', nome: 'Forma de Encerramento', tipo: 'lista', obrigatorio: false, opcoes: ['Cumprido integralmente', 'Rescindido amigavelmente', 'Rescindido unilateralmente', 'Vigência expirada'], coresOpcoes: { 'Cumprido integralmente': COR.verde, 'Rescindido unilateralmente': COR.vermelho } },
        { id: 'fc_c37', nome: 'Data do Encerramento', tipo: 'data', obrigatorio: false },
        { id: 'fc_c38', nome: 'Termo de Recebimento Definitivo', tipo: 'anexo', obrigatorio: false },
        { id: 'fc_c39', nome: 'Garantia Devolvida?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'fc_c40', nome: 'Avaliação do Contratado', tipo: 'lista', obrigatorio: false, opcoes: ['Ótimo', 'Bom', 'Regular', 'Ruim'], coresOpcoes: { 'Ótimo': COR.verde, 'Bom': COR.verde, 'Ruim': COR.vermelho } },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'mover',
      nome: 'Medição não conforme abre ocorrência',
      etapaOrigemId: 'fc_e2',
      condicao: { campoId: 'fc_c12', operador: 'igual', valor: 'Não' },
      etapaDestinoId: 'fc_e3',
      etapaDestinoSenaoId: 'fc_e1',
    },
    {
      tipo: 'mover',
      nome: 'Regularizou volta ao acompanhamento normal',
      etapaOrigemId: 'fc_e3',
      condicao: { campoId: 'fc_c21', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'fc_e1',
      etapaDestinoSenaoId: 'fc_e5',
    },
    {
      tipo: 'mover',
      nome: 'Aditivo assinado devolve ao contrato vigente',
      etapaOrigemId: 'fc_e4',
      condicao: { campoId: 'fc_c28', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'fc_e1',
    },
    {
      tipo: 'exibir_ocultar',
      nome: 'Valor da multa só quando a sanção for multa',
      etapaOrigemId: 'fc_e5',
      condicao: { campoId: 'fc_c30', operador: 'igual', valor: 'Multa' },
      camposAfetadosIds: ['fc_c31'],
    },
  ],
  cards: [
    { id: 'fc_card1', titulo: 'Limpeza urbana - Contrato 044/2025', etapaId: 'fc_e3', etiquetas: ['Com Notificação'], valores: { fc_c1: 'Limpeza urbana - Contrato 044/2025', fc_c2: '044/2025', fc_c3: 'Ambiental Serviços', fc_c4: 'Servidor Paulo Menezes', fc_c7: 18500000, fc_c17: 'Equipe insuficiente', fc_c18: 'Contratado operando com 22 garis de um total de 34 previstos em contrato.' } },
    { id: 'fc_card2', titulo: 'Merenda escolar - ARP 018/2026', etapaId: 'fc_e2', valores: { fc_c1: 'Merenda escolar - ARP 018/2026', fc_c2: 'ARP 018/2026', fc_c3: 'Alimentos Vale Ltda', fc_c4: 'Servidora Cláudia Ramos', fc_c11: 'Agosto/2026', fc_c12: 'Sim', fc_c13: 315000 } },
    { id: 'fc_card3', titulo: 'Locação de veículos - Contrato 012/2026', etapaId: 'fc_e4', valores: { fc_c1: 'Locação de veículos - Contrato 012/2026', fc_c2: '012/2026', fc_c3: 'LocaFrota', fc_c4: 'Servidor Márcio Dias', fc_c23: 'Prorrogação de prazo', fc_c24: 'Serviço contínuo e essencial, com preços vantajosos frente ao mercado.', fc_c26: 0 } },
    { id: 'fc_card4', titulo: 'Reforma da UBS - Contrato 031/2026', etapaId: 'fc_e1', etiquetas: ['Vigência Crítica'], valores: { fc_c1: 'Reforma da UBS - Contrato 031/2026', fc_c2: '031/2026', fc_c3: 'Construtora Pilar', fc_c4: 'Eng. Ricardo Alves', fc_c7: 3100000, fc_c8: 1240000 } },
    { id: 'fc_card5', titulo: 'Manutenção de ar-condicionado - 008/2026', etapaId: 'fc_e5', etiquetas: ['Em Sanção'], valores: { fc_c1: 'Manutenção de ar-condicionado - 008/2026', fc_c2: '008/2026', fc_c3: 'ClimaTec Serviços', fc_c4: 'Servidor João Prado', fc_c30: 'Multa', fc_c31: 24000 } },
    { id: 'fc_card6', titulo: 'Material de escritório - Contrato 002/2025', etapaId: 'fc_e6', valores: { fc_c1: 'Material de escritório - Contrato 002/2025', fc_c2: '002/2025', fc_c3: 'Papelaria Central', fc_c4: 'Servidora Ana Beatriz', fc_c36: 'Cumprido integralmente', fc_c40: 'Bom' } },
  ],
  tour: [
    '👁️ O contrato depois de assinado — que é onde o TCE mais encontra problema.',
    '1️⃣ "Contrato Vigente": fiscal designado por portaria e vigência como vencimento do card.',
    '2️⃣ "Medição / Atesto": não conforme abre ocorrência automaticamente.',
    '3️⃣ "Ocorrência": notificação com prazo de defesa do contratado.',
    '4️⃣ "Aditivo / Reajuste": percentual sobre o contrato original visível — o limite legal é 25%.',
    '5️⃣ "Processo Sancionatório": ampla defesa com prazo e registro no cadastro de sanções.',
    '📁 "Encerrado": termo de recebimento definitivo, devolução da garantia e avaliação do contratado.',
  ],
  etapasDicas: [
    { quemFaz: 'Gestor de contratos', objetivo: 'Manter fiscal designado e vigência sob controle — contrato vencido executado é irregular.' },
    { quemFaz: 'Fiscal do contrato', objetivo: 'Atestar com relatório, não só assinar a nota.', conexoes: ['Ligue à "Execução da Despesa" — o atesto é o que permite liquidar'] },
    { quemFaz: 'Fiscal / Gestor', objetivo: 'Notificar formalmente; ocorrência não notificada não sustenta sanção depois.', automacoes: ['Já configurado: não regularizou abre processo sancionatório'] },
    { quemFaz: 'Setor de Contratos / Jurídico', objetivo: 'Controlar o limite de 25% de acréscimo.' },
    { quemFaz: 'Comissão / Autoridade', objetivo: 'Garantir ampla defesa antes de aplicar sanção.' },
    { quemFaz: 'Setor de Contratos', objetivo: 'Encerrar com avaliação — histórico do fornecedor vale para as próximas contratações.' },
  ],
};

// ============================================================
// PREFEITURA: FASE 1 (P1) E FASE 2 (P2) DO MAPA "Escalone na Prefeitura"
// (Jarvis\prefeitura\mapa-prefeitura-escalone.md, seção 11.3, 08/10/2026).
//
// P1: Câmara, MP/Judiciário, Atos/DOM, Memorando/Ofício/Circular e Agenda de
// Obrigações. São os mesmos de scripts_vps/prefeitura_fase1_fluxos.py, que
// monta a demonstração "Município Exemplo"; mude nos dois lugares.
// P2: TCE, pessoal (licenças, diárias, estágio probatório, PAD), patrimônio,
// frota, veículo, CRC, pagamento do fornecedor e Fazenda (fiscalização,
// cadastro imobiliário e dívida ativa).
// Prazos em horas corridas (dias × 24), como os demais modelos públicos.
// Cards de exemplo com nomes e documentos fictícios.
// ============================================================

// GAB-01 (P1)
const gabCamara: DefaultTemplate = {
  id: 'tpl_gab_camara',
  name: 'Câmara: Requerimentos, Indicações e Pedidos de Informação',
  descricao: 'Demandas do Legislativo com o prazo da Lei Orgânica: triagem do gabinete, elaboração da resposta na secretaria, revisão jurídica, assinatura do prefeito e protocolo na Câmara. Indicação sem resposta formal vai direto para registro da providência.',
  categoria: 'publico',
  icone: '🏛️',
  prazoProcesso: { horas: 720, ignorarFimDeSemana: false, etapasPausaIds: [] },
  etiquetas: [
    { nome: 'Pedido de Informação', cor: COR.vermelho },
    { nome: 'Indicação', cor: COR.azul },
    { nome: 'Reiteração', cor: COR.amarelo },
  ],
  etapas: [
    {
      id: 'cam_e1', nome: 'Recebido da Câmara',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['cam_c1', 'cam_c2', 'cam_prz'],
      permiteMoverPara: ['cam_e2'],
      campos: [
        { id: 'cam_c1', nome: 'Assunto', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        { id: 'cam_org', nome: 'Órgão de Origem', tipo: 'texto', obrigatorio: true },
        { id: 'cam_nof', nome: 'Nº do Ofício', tipo: 'texto', obrigatorio: true },
        { id: 'cam_dof', nome: 'Data do Recebimento', tipo: 'data', obrigatorio: true },
        { id: 'cam_prz', nome: 'Prazo de Resposta', tipo: 'data_vencimento', obrigatorio: true },
        { id: 'cam_arq', nome: 'Documento Recebido', tipo: 'anexo', obrigatorio: true },
        { id: 'cam_c2', nome: 'Tipo', tipo: 'lista', obrigatorio: true, opcoes: ['Pedido de Informação', 'Requerimento', 'Indicação', 'Moção', 'Convocação'], coresOpcoes: { 'Pedido de Informação': COR.vermelho, 'Indicação': COR.azul, 'Convocação': COR.roxo } },
        { id: 'cam_c3', nome: 'Vereador(es) Autor(es)', tipo: 'texto', obrigatorio: true },
        { id: 'cam_c4', nome: 'Nº da Proposição', tipo: 'texto', obrigatorio: true },
        { id: 'cam_c5', nome: 'Bairro / Local', tipo: 'texto', obrigatorio: false },
      ],
    },
    {
      id: 'cam_e2', nome: 'Triagem do Gabinete',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['cam_c1', 'cam_c3', 'cam_prz'],
      permiteMoverPara: ['cam_e3', 'cam_e6'],
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'cam_c6', nome: 'Secretaria Responsável', tipo: 'lista', obrigatorio: true, opcoes: ['Administração', 'Fazenda', 'Saúde', 'Educação', 'Assistência Social', 'Obras', 'Urbanismo', 'Meio Ambiente', 'Agricultura', 'Cultura', 'Procuradoria'] },
        { id: 'cam_c7', nome: 'Exige resposta formal?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'cam_c8', nome: 'Orientação do Gabinete', tipo: 'texto_longo', obrigatorio: false },
      ],
    },
    {
      id: 'cam_e3', nome: 'Em Elaboração na Secretaria',
      tempoMaximoHoras: 240,
      camposVisiveisNoCard: ['cam_c1', 'cam_c6', 'cam_prz'],
      permiteMoverPara: ['cam_e4', 'cam_e2'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'cam_c9', nome: 'Servidor Responsável', tipo: 'texto', obrigatorio: true },
        { id: 'cam_c10', nome: 'Informações / Minuta da Resposta', tipo: 'texto_longo', obrigatorio: true },
        { id: 'cam_c11', nome: 'Documentos de Apoio', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'cam_e4', nome: 'Revisão Jurídica / Gabinete',
      tempoMaximoHoras: 72,
      camposVisiveisNoCard: ['cam_c1', 'cam_c6', 'cam_c12'],
      permiteMoverPara: ['cam_e5', 'cam_e3'],
      permiteCriarCards: false,
      limparCamposAoRetornar: ['cam_c12', 'cam_c13'],
      campos: [
        { id: 'cam_c12', nome: 'Minuta aprovada?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'cam_c13', nome: 'Ajustes Pedidos', tipo: 'texto_longo', obrigatorio: false },
      ],
    },
    {
      id: 'cam_e5', nome: 'Assinatura e Envio',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['cam_c1', 'cam_c14', 'cam_prz'],
      permiteMoverPara: ['cam_e7'],
      permiteCriarCards: false,
      campos: [
        { id: 'cam_c14', nome: 'Nº do Ofício de Resposta', tipo: 'texto', obrigatorio: true },
        { id: 'cam_c15', nome: 'Ofício Assinado', tipo: 'anexo', obrigatorio: true },
        { id: 'cam_c16', nome: 'Data do Protocolo na Câmara', tipo: 'data', obrigatorio: true },
      ],
    },
    {
      id: 'cam_e6', nome: 'Indicação Registrada (sem resposta formal)',
      camposVisiveisNoCard: ['cam_c1', 'cam_c5', 'cam_c18'],
      permiteMoverPara: ['cam_e7'],
      permiteCriarCards: false,
      campos: [
        { id: 'cam_c17', nome: 'Providência Prevista', tipo: 'texto_longo', obrigatorio: false },
        { id: 'cam_c18', nome: 'Gerou demanda de Zeladoria/Obra?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'cam_e7', nome: 'Concluído',
      camposVisiveisNoCard: ['cam_c1', 'cam_c2', 'cam_c19'],
      permiteCriarCards: false,
      campos: [
        { id: 'cam_c19', nome: 'Respondido no prazo?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'cam_c20', nome: 'Observação', tipo: 'texto_longo', obrigatorio: false },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'mover',
      nome: 'Indicação sem resposta formal vai para registro; as demais vão à secretaria',
      etapaOrigemId: 'cam_e2',
      condicao: { campoId: 'cam_c7', operador: 'igual', valor: 'Não' },
      etapaDestinoId: 'cam_e6',
      etapaDestinoSenaoId: 'cam_e3',
    },
    {
      tipo: 'exibir_ocultar',
      nome: 'Ajustes só quando a minuta não foi aprovada',
      etapaOrigemId: 'cam_e4',
      condicao: { campoId: 'cam_c12', operador: 'igual', valor: 'Não' },
      camposAfetadosIds: ['cam_c13'],
    },
  ],
  cards: [
    { id: 'cam_card1', titulo: 'Pedido de informação sobre a fila de cirurgias eletivas', etapaId: 'cam_e3', etiquetas: ['Pedido de Informação'], valores: { cam_c1: 'Pedido de informação sobre a fila de cirurgias eletivas', cam_org: 'Câmara Municipal de Exemplo', cam_nof: 'Of. 214/2026-CME', cam_dof: '2026-09-22', cam_prz: '2026-10-22', cam_c2: 'Pedido de Informação', cam_c3: 'Ver. Marta Exemplo', cam_c4: 'PI 087/2026', cam_c6: 'Saúde', cam_c7: 'Sim', cam_c8: 'Informar o total por especialidade, sem nome de paciente (LGPD).', cam_c9: 'Servidor da Regulação 02', cam_c10: 'Fila atual: 412 pacientes; ortopedia (168) e oftalmologia (121) concentram 70% da espera.' } },
    { id: 'cam_card2', titulo: 'Indicação de redutor de velocidade na Rua das Acácias', etapaId: 'cam_e6', etiquetas: ['Indicação'], valores: { cam_c1: 'Indicação de redutor de velocidade na Rua das Acácias', cam_org: 'Câmara Municipal de Exemplo', cam_nof: 'Of. 220/2026-CME', cam_dof: '2026-09-25', cam_prz: '2026-10-25', cam_c2: 'Indicação', cam_c3: 'Ver. João Exemplo', cam_c4: 'IND 301/2026', cam_c5: 'Rua das Acácias, em frente à Escola Central', cam_c6: 'Obras', cam_c7: 'Não', cam_c17: 'Incluída no cronograma de sinalização de novembro.', cam_c18: 'Sim' } },
    { id: 'cam_card3', titulo: 'Requerimento sobre os contratos do transporte escolar', etapaId: 'cam_e4', etiquetas: ['Reiteração'], valores: { cam_c1: 'Requerimento sobre os contratos do transporte escolar', cam_org: 'Câmara Municipal de Exemplo', cam_nof: 'Of. 198/2026-CME', cam_dof: '2026-09-10', cam_prz: '2026-10-10', cam_c2: 'Requerimento', cam_c3: 'Ver. Paulo Exemplo e Ver. Rita Exemplo', cam_c4: 'REQ 112/2026', cam_c6: 'Educação', cam_c7: 'Sim', cam_c9: 'Coordenação do Transporte Escolar', cam_c10: 'Três contratos vigentes (rotas norte, sul e rural), com valor por km e vigência até 2027.', cam_c12: 'Não', cam_c13: 'Incluir a planilha de rotas e o valor por km de cada contrato vigente.' } },
    { id: 'cam_card4', titulo: 'Pedido de informação sobre horas-máquina da patrulha agrícola', etapaId: 'cam_e5', etiquetas: ['Pedido de Informação'], valores: { cam_c1: 'Pedido de informação sobre horas-máquina da patrulha agrícola', cam_org: 'Câmara Municipal de Exemplo', cam_nof: 'Of. 190/2026-CME', cam_dof: '2026-09-03', cam_prz: '2026-10-03', cam_c2: 'Pedido de Informação', cam_c3: 'Ver. Carlos Exemplo', cam_c4: 'PI 079/2026', cam_c6: 'Agricultura', cam_c7: 'Sim', cam_c9: 'Secretaria de Agricultura', cam_c10: '1.240 horas-máquina atendidas em 2026 para 186 produtores; fila atual de 23 pedidos.', cam_c12: 'Sim', cam_c14: 'Of. GAB 301/2026' } },
    { id: 'cam_card5', titulo: 'Pedido de informação sobre a iluminação do Bairro Sul', etapaId: 'cam_e2', etiquetas: ['Pedido de Informação'], valores: { cam_c1: 'Pedido de informação sobre a iluminação do Bairro Sul', cam_org: 'Câmara Municipal de Exemplo', cam_nof: 'Of. 231/2026-CME', cam_dof: '2026-10-06', cam_prz: '2026-11-05', cam_c2: 'Pedido de Informação', cam_c3: 'Ver. Marta Exemplo', cam_c4: 'PI 091/2026', cam_c5: 'Bairro Sul' } },
    { id: 'cam_card6', titulo: 'Requerimento sobre o consumo de combustível da frota', etapaId: 'cam_e7', valores: { cam_c1: 'Requerimento sobre o consumo de combustível da frota', cam_org: 'Câmara Municipal de Exemplo', cam_nof: 'Of. 160/2026-CME', cam_dof: '2026-08-12', cam_prz: '2026-09-11', cam_c2: 'Requerimento', cam_c3: 'Ver. João Exemplo', cam_c4: 'REQ 098/2026', cam_c6: 'Administração', cam_c7: 'Sim', cam_c12: 'Sim', cam_c14: 'Of. GAB 276/2026', cam_c16: '2026-09-04', cam_c19: 'Sim' } },
  ],
  tour: [
    '🏛️ Toda demanda da Câmara com número, secretaria responsável e o prazo da Lei Orgânica no card.',
    '1️⃣ "Recebido da Câmara": ofício, proposição, vereador autor e prazo de resposta.',
    '2️⃣ "Triagem do Gabinete": indicação sem resposta formal vai direto para registro da providência.',
    '3️⃣ "Em Elaboração": a secretaria escreve a minuta com os documentos de apoio.',
    '4️⃣ "Revisão Jurídica": minuta reprovada volta com os ajustes pedidos.',
    '✍️ "Assinatura e Envio": ofício assinado pelo prefeito e data do protocolo na Câmara.',
    '📊 Marque "Respondido no prazo?" ao concluir: é o indicador que o prefeito cobra.',
  ],
  etapasDicas: [
    { quemFaz: 'Gabinete / Relações com o Legislativo', objetivo: 'Registrar o ofício com o prazo da Lei Orgânica.' },
    { quemFaz: 'Chefe de Gabinete', objetivo: 'Definir a secretaria e se exige resposta formal.', automacoes: ['Já configurado: indicação sem resposta vai para registro'] },
    { quemFaz: 'Secretaria responsável', objetivo: 'Responder com dados, sem dado pessoal (LGPD).' },
    { quemFaz: 'Procuradoria / Gabinete', objetivo: 'Revisar forma e conteúdo antes da assinatura.', automacoes: ['Já configurado: ajustes aparecem quando reprova'] },
    { quemFaz: 'Prefeito / Gabinete', objetivo: 'Assinar e protocolar na Câmara dentro do prazo.' },
    { quemFaz: 'Secretaria responsável', objetivo: 'Registrar a providência da indicação.', conexoes: ['Ligue à "Zeladoria" quando a indicação virar serviço'] },
    { quemFaz: 'Gabinete', objetivo: 'Encerrar marcando se respondeu no prazo.' },
  ],
};

// GAB-02 (P1)
const gabMp: DefaultTemplate = {
  id: 'tpl_gab_mp',
  name: 'Requisições do Ministério Público e Ofícios Judiciais',
  descricao: 'Requisições, recomendações, notificações e ordens judiciais com prazo: análise da Procuradoria, coleta de informações nas secretarias, resposta assinada e acompanhamento de TAC e recomendação (requisição do MP: 10 dias úteis, Lei 7.347, art. 8º, § 1º, ou o prazo do ofício).',
  categoria: 'publico',
  icone: '⚖️',
  prazoProcesso: { horas: 240 },
  etiquetas: [
    { nome: 'Urgente', cor: COR.vermelho },
    { nome: 'Inquérito Civil', cor: COR.roxo },
    { nome: 'Ordem Judicial', cor: COR.vermelho },
    { nome: 'Sigiloso', cor: COR.cinza },
  ],
  etapas: [
    {
      id: 'mp_e1', nome: 'Recebido',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['mp_c1', 'mp_c2', 'mp_prz'],
      permiteMoverPara: ['mp_e2'],
      campos: [
        { id: 'mp_c1', nome: 'Assunto', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        { id: 'mp_org', nome: 'Órgão de Origem', tipo: 'texto', obrigatorio: true },
        { id: 'mp_nof', nome: 'Nº do Ofício', tipo: 'texto', obrigatorio: true },
        { id: 'mp_dof', nome: 'Data do Recebimento', tipo: 'data', obrigatorio: true },
        { id: 'mp_prz', nome: 'Prazo de Resposta', tipo: 'data_vencimento', obrigatorio: true },
        { id: 'mp_arq', nome: 'Documento Recebido', tipo: 'anexo', obrigatorio: true },
        { id: 'mp_c2', nome: 'Origem', tipo: 'lista', obrigatorio: true, opcoes: ['Ministério Público Estadual', 'Ministério Público Federal', 'Ministério Público de Contas', 'Poder Judiciário', 'Defensoria Pública', 'Polícia', 'Conselho Tutelar'], coresOpcoes: { 'Poder Judiciário': COR.vermelho, 'Ministério Público Estadual': COR.roxo } },
        { id: 'mp_c3', nome: 'Tipo', tipo: 'lista', obrigatorio: true, opcoes: ['Requisição de informação', 'Recomendação', 'Notificação', 'Intimação', 'Mandado / Ordem judicial', 'Audiência'], coresOpcoes: { 'Mandado / Ordem judicial': COR.vermelho } },
        { id: 'mp_c4', nome: 'Nº do Procedimento / Processo', tipo: 'texto', obrigatorio: true },
        { id: 'mp_c5', nome: 'Multa ou crime por descumprimento?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'mp_e2', nome: 'Análise da Procuradoria',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['mp_c1', 'mp_c6', 'mp_prz'],
      permiteMoverPara: ['mp_e3', 'mp_e5'],
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'mp_c6', nome: 'Procurador Responsável', tipo: 'texto', obrigatorio: true },
        { id: 'mp_c7', nome: 'Secretaria(s) que devem informar', tipo: 'checkbox_multi', obrigatorio: true, opcoes: ['Gabinete', 'Administração', 'Fazenda', 'Saúde', 'Educação', 'Assistência Social', 'Obras', 'Urbanismo', 'Meio Ambiente'] },
        { id: 'mp_c8', nome: 'Precisa pedir prorrogação?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'mp_c8b', nome: 'Prazo Prorrogado', tipo: 'data_vencimento', obrigatorio: false },
      ],
    },
    {
      id: 'mp_e3', nome: 'Coleta de Informações na Secretaria',
      tempoMaximoHoras: 120,
      camposVisiveisNoCard: ['mp_c1', 'mp_c7', 'mp_prz'],
      permiteMoverPara: ['mp_e4', 'mp_e2'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'mp_c9', nome: 'Informações Prestadas', tipo: 'texto_longo', obrigatorio: true },
        { id: 'mp_c10', nome: 'Documentos', tipo: 'anexo', obrigatorio: true },
      ],
    },
    {
      id: 'mp_e4', nome: 'Minuta e Assinatura',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['mp_c1', 'mp_c6', 'mp_prz'],
      permiteMoverPara: ['mp_e5'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'mp_c11', nome: 'Ofício de Resposta Assinado', tipo: 'anexo', obrigatorio: true },
        { id: 'mp_c12', nome: 'Nº do Ofício', tipo: 'texto', obrigatorio: true },
      ],
    },
    {
      id: 'mp_e5', nome: 'Respondido / Cumprido',
      camposVisiveisNoCard: ['mp_c1', 'mp_c13', 'mp_c15'],
      permiteMoverPara: ['mp_e6'],
      permiteCriarCards: false,
      campos: [
        { id: 'mp_c13', nome: 'Data do Envio', tipo: 'data', obrigatorio: true },
        { id: 'mp_c14', nome: 'Comprovante de Entrega', tipo: 'anexo', obrigatorio: false },
        { id: 'mp_c15', nome: 'Houve nova requisição?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'mp_e6', nome: 'Acompanhamento (TAC / Recomendação)',
      camposVisiveisNoCard: ['mp_c1', 'mp_c17', 'mp_c18'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'mp_c16', nome: 'Obrigações Assumidas', tipo: 'texto_longo', obrigatorio: false },
        { id: 'mp_c17', nome: 'Próximo Prazo', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'mp_c18', nome: 'Cumprido?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'exibir_ocultar',
      nome: 'Prazo prorrogado só quando pedir prorrogação',
      etapaOrigemId: 'mp_e2',
      condicao: { campoId: 'mp_c8', operador: 'igual', valor: 'Sim' },
      camposAfetadosIds: ['mp_c8b'],
    },
  ],
  cards: [
    { id: 'mp_card1', titulo: 'Requisição do MPSC sobre abastecimento de água no Bairro Sul', etapaId: 'mp_e3', etiquetas: ['Inquérito Civil'], valores: { mp_c1: 'Requisição do MPSC sobre abastecimento de água no Bairro Sul', mp_org: '2ª Promotoria de Justiça da Comarca de Exemplo', mp_nof: 'Of. 1123/2026/02PJ', mp_dof: '2026-10-01', mp_prz: '2026-10-15', mp_c2: 'Ministério Público Estadual', mp_c3: 'Requisição de informação', mp_c4: 'IC 06.2026.00004512-3', mp_c5: 'Sim', mp_c6: 'Procurador do Contencioso', mp_c7: ['Obras', 'Meio Ambiente'], mp_c8: 'Não' } },
    { id: 'mp_card2', titulo: 'Ordem judicial para fornecer medicamento de alto custo', etapaId: 'mp_e4', etiquetas: ['Ordem Judicial', 'Urgente'], valores: { mp_c1: 'Ordem judicial para fornecer medicamento de alto custo', mp_org: 'Vara da Fazenda Pública da Comarca de Exemplo', mp_nof: 'Mandado 3321/2026', mp_dof: '2026-10-02', mp_prz: '2026-10-09', mp_c2: 'Poder Judiciário', mp_c3: 'Mandado / Ordem judicial', mp_c4: '5001234-56.2026.8.24.0999', mp_c5: 'Sim', mp_c6: 'Procurador do Contencioso', mp_c7: ['Saúde'], mp_c8: 'Não', mp_c9: 'Medicamento comprado por dispensa emergencial; primeira entrega ao paciente em 06/10.' } },
    { id: 'mp_card3', titulo: 'Recomendação sobre acessibilidade nas escolas municipais', etapaId: 'mp_e6', valores: { mp_c1: 'Recomendação sobre acessibilidade nas escolas municipais', mp_org: '1ª Promotoria de Justiça da Comarca de Exemplo', mp_nof: 'Recomendação 07/2026/01PJ', mp_dof: '2026-07-14', mp_prz: '2026-08-13', mp_c2: 'Ministério Público Estadual', mp_c3: 'Recomendação', mp_c4: 'PA 09.2026.00001877-0', mp_c6: 'Procurador-Geral', mp_c7: ['Educação', 'Obras'], mp_c13: '2026-08-12', mp_c15: 'Não', mp_c16: 'Rampas e banheiros adaptados nas 4 escolas do centro até dezembro; demais no 1º semestre de 2027.', mp_c17: '2026-12-15' } },
    { id: 'mp_card4', titulo: 'Requisição do Conselho Tutelar: vaga em creche', etapaId: 'mp_e2', etiquetas: ['Sigiloso'], valores: { mp_c1: 'Requisição do Conselho Tutelar: vaga em creche', mp_org: 'Conselho Tutelar de Exemplo', mp_nof: 'Of. 455/2026-CT', mp_dof: '2026-10-07', mp_prz: '2026-10-17', mp_c2: 'Conselho Tutelar', mp_c3: 'Requisição de informação', mp_c4: 'CT 455/2026 (criança identificada só no ofício)' } },
    { id: 'mp_card5', titulo: 'Notificação do MP de Contas sobre o portal da transparência', etapaId: 'mp_e5', valores: { mp_c1: 'Notificação do MP de Contas sobre o portal da transparência', mp_org: 'Ministério Público junto ao TCE/SC', mp_nof: 'Of. MPC 2087/2026', mp_dof: '2026-09-15', mp_prz: '2026-09-30', mp_c2: 'Ministério Público de Contas', mp_c3: 'Notificação', mp_c4: '@APE 26/00123456', mp_c6: 'Procurador-Geral', mp_c7: ['Fazenda', 'Administração'], mp_c12: 'Of. PGM 118/2026', mp_c13: '2026-09-29', mp_c15: 'Não' } },
    { id: 'mp_card6', titulo: 'Intimação para audiência sobre corte de árvores na Av. Brasil', etapaId: 'mp_e1', valores: { mp_c1: 'Intimação para audiência sobre corte de árvores na Av. Brasil', mp_org: 'Juizado Especial Cível da Comarca de Exemplo', mp_nof: 'Intimação 7781/2026', mp_dof: '2026-10-08', mp_prz: '2026-10-28', mp_c2: 'Poder Judiciário', mp_c3: 'Audiência', mp_c4: '5009876-12.2026.8.24.0999' } },
  ],
  tour: [
    '⚖️ Requisição do MP, ordem judicial e recomendação com prazo vigiado: nada vence sem ninguém ver.',
    '1️⃣ "Recebido": origem, tipo, nº do procedimento e prazo (requisição do MP: 10 dias úteis).',
    '2️⃣ "Análise da Procuradoria": define quem informa e se precisa pedir prorrogação.',
    '3️⃣ "Coleta na Secretaria": informações e documentos que embasam a resposta.',
    '4️⃣ "Minuta e Assinatura": ofício assinado com número.',
    '📌 "Acompanhamento": TAC e recomendação ficam com obrigações e próximo prazo.',
  ],
  etapasDicas: [
    { quemFaz: 'Protocolo / Procuradoria', objetivo: 'Registrar no mesmo dia com o prazo do ofício.' },
    { quemFaz: 'Procurador responsável', objetivo: 'Distribuir às secretarias e avaliar prorrogação.', automacoes: ['Já configurado: prazo prorrogado aparece quando pede prorrogação'] },
    { quemFaz: 'Secretarias envolvidas', objetivo: 'Informar com documentos, sem atraso.' },
    { quemFaz: 'Procuradoria', objetivo: 'Redigir e colher a assinatura do ofício.' },
    { quemFaz: 'Procuradoria', objetivo: 'Guardar o comprovante de entrega.' },
    { quemFaz: 'Procuradoria / Controladoria', objetivo: 'Cumprir o TAC ou a recomendação no prazo.' },
  ],
};

// GAB-03 (P1)
const gabAtos: DefaultTemplate = {
  id: 'tpl_gab_atos',
  name: 'Atos Oficiais e Publicação no DOM',
  descricao: 'Decretos, portarias, leis sancionadas, vetos, editais e extratos: pedido da secretaria, revisão jurídica, numeração e assinatura, envio ao Diário Oficial dos Municípios e registro da edição publicada.',
  categoria: 'publico',
  icone: '📜',
  etapas: [
    {
      id: 'ato_e1', nome: 'Pedido do Ato',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['ato_c1', 'ato_c2', 'ato_c3'],
      permiteMoverPara: ['ato_e2'],
      campos: [
        { id: 'ato_c1', nome: 'Ementa', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        { id: 'ato_c2', nome: 'Tipo de Ato', tipo: 'lista', obrigatorio: true, opcoes: ['Decreto', 'Portaria', 'Lei (sanção)', 'Veto', 'Edital', 'Extrato de contrato', 'Aviso', 'Resolução'], coresOpcoes: { 'Decreto': COR.azul, 'Lei (sanção)': COR.verde, 'Veto': COR.vermelho } },
        { id: 'ato_c3', nome: 'Secretaria Solicitante', tipo: 'texto', obrigatorio: true },
        { id: 'ato_c4', nome: 'Minuta', tipo: 'anexo', obrigatorio: true },
        { id: 'ato_c5', nome: 'Justificativa', tipo: 'texto_longo', obrigatorio: false },
      ],
    },
    {
      id: 'ato_e2', nome: 'Revisão Jurídica',
      tempoMaximoHoras: 72,
      camposVisiveisNoCard: ['ato_c1', 'ato_c2', 'ato_c6'],
      permiteMoverPara: ['ato_e3', 'ato_e1'],
      permiteCriarCards: false,
      limparCamposAoRetornar: ['ato_c6', 'ato_c7'],
      campos: [
        { id: 'ato_c6', nome: 'Minuta aprovada?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'ato_c7', nome: 'Ajustes', tipo: 'texto_longo', obrigatorio: false },
      ],
    },
    {
      id: 'ato_e3', nome: 'Numeração e Assinatura',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['ato_c1', 'ato_c2', 'ato_c8'],
      permiteMoverPara: ['ato_e4'],
      permiteCriarCards: false,
      campos: [
        { id: 'ato_c8', nome: 'Número do Ato', tipo: 'texto', obrigatorio: true },
        { id: 'ato_c9', nome: 'Data do Ato', tipo: 'data', obrigatorio: true },
        { id: 'ato_c10', nome: 'Ato Assinado', tipo: 'anexo', obrigatorio: true },
      ],
    },
    {
      id: 'ato_e4', nome: 'Envio ao Diário Oficial',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['ato_c1', 'ato_c8', 'ato_c11'],
      permiteMoverPara: ['ato_e5'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'ato_c11', nome: 'Data Prevista de Publicação', tipo: 'data_vencimento', obrigatorio: true },
        { id: 'ato_c12', nome: 'Protocolo no DOM', tipo: 'texto', obrigatorio: false },
      ],
    },
    {
      id: 'ato_e5', nome: 'Publicado',
      camposVisiveisNoCard: ['ato_c1', 'ato_c8', 'ato_c13'],
      permiteCriarCards: false,
      campos: [
        { id: 'ato_c13', nome: 'Edição / Página do DOM', tipo: 'texto', obrigatorio: true },
        { id: 'ato_c14', nome: 'Link da Publicação', tipo: 'texto', obrigatorio: true },
        { id: 'ato_c15', nome: 'Publicado também no PNCP / Transparência?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'exibir_ocultar',
      nome: 'Ajustes só quando a minuta não foi aprovada',
      etapaOrigemId: 'ato_e2',
      condicao: { campoId: 'ato_c6', operador: 'igual', valor: 'Não' },
      camposAfetadosIds: ['ato_c7'],
    },
  ],
  cards: [
    { id: 'ato_card1', titulo: 'Decreto: ponto facultativo no Dia do Servidor Público (28/10)', etapaId: 'ato_e3', valores: { ato_c1: 'Decreto: ponto facultativo no Dia do Servidor Público (28/10)', ato_c2: 'Decreto', ato_c3: 'Administração', ato_c5: 'Mantém os serviços essenciais (saúde, coleta e Defesa Civil) em plantão.', ato_c6: 'Sim' } },
    { id: 'ato_card2', titulo: 'Portaria: nomeia a comissão do inventário patrimonial de 2026', etapaId: 'ato_e5', valores: { ato_c1: 'Portaria: nomeia a comissão do inventário patrimonial de 2026', ato_c2: 'Portaria', ato_c3: 'Administração', ato_c6: 'Sim', ato_c8: 'Portaria 512/2026', ato_c9: '2026-10-01', ato_c11: '2026-10-02', ato_c12: 'DOM 2026-58812', ato_c13: 'Edição de 02/10/2026, p. 214', ato_c14: 'https://diario.exemplo.gov.br/2026/10/02/portaria-512', ato_c15: 'Sim' } },
    { id: 'ato_card3', titulo: 'Extrato do contrato 045/2026: limpeza urbana', etapaId: 'ato_e4', valores: { ato_c1: 'Extrato do contrato 045/2026: limpeza urbana', ato_c2: 'Extrato de contrato', ato_c3: 'Compras e Licitações', ato_c6: 'Sim', ato_c8: 'Extrato 045/2026', ato_c9: '2026-10-07', ato_c11: '2026-10-10' } },
    { id: 'ato_card4', titulo: 'Lei: sanção do PL 032/2026, programa Bairro Limpo', etapaId: 'ato_e2', valores: { ato_c1: 'Lei: sanção do PL 032/2026, programa Bairro Limpo', ato_c2: 'Lei (sanção)', ato_c3: 'Gabinete do Prefeito', ato_c5: 'Autógrafo recebido da Câmara em 30/09; prazo de sanção de 15 dias úteis.', ato_c6: 'Não', ato_c7: 'Ajustar o art. 3º: a fonte de recurso precisa citar a dotação da LOA 2026.' } },
    { id: 'ato_card5', titulo: 'Edital: audiência pública da LOA 2027', etapaId: 'ato_e1', valores: { ato_c1: 'Edital: audiência pública da LOA 2027', ato_c2: 'Edital', ato_c3: 'Fazenda', ato_c5: 'Audiência na Câmara em 30/10, às 19h (LRF, art. 48, § 1º, I).' } },
  ],
  tour: [
    '📜 Decretos, portarias, leis e extratos do pedido à publicação no Diário Oficial dos Municípios.',
    '1️⃣ "Pedido do Ato": tipo, ementa e minuta da secretaria solicitante.',
    '2️⃣ "Revisão Jurídica": minuta reprovada volta com os ajustes.',
    '3️⃣ "Numeração e Assinatura": número oficial e ato assinado.',
    '4️⃣ "Envio ao DOM": data prevista e protocolo da publicação.',
    '✅ "Publicado": edição, página e link, mais o registro no PNCP / Transparência quando couber.',
  ],
  etapasDicas: [
    { quemFaz: 'Secretaria solicitante', objetivo: 'Enviar a minuta com justificativa.' },
    { quemFaz: 'Procuradoria', objetivo: 'Conferir legalidade e técnica legislativa.', automacoes: ['Já configurado: ajustes aparecem quando reprova'] },
    { quemFaz: 'Gabinete / Atos Oficiais', objetivo: 'Numerar em sequência e colher a assinatura.' },
    { quemFaz: 'Gabinete / Comunicação', objetivo: 'Enviar ao DOM respeitando o horário de corte.' },
    { quemFaz: 'Gabinete / Comunicação', objetivo: 'Registrar a edição publicada: a eficácia do ato depende dela.' },
  ],
};

// ADM-01 (P1)
const admMemo: DefaultTemplate = {
  id: 'tpl_adm_memo',
  name: 'Memorando, Ofício e Circular (Comunicação Interna)',
  descricao: 'Comunicação entre secretarias com recebimento confirmado e prazo de resposta: memorando, ofício, circular (um para vários setores) e despacho de encaminhamento. Quem recebe registra o aceite; se não precisa de resposta, o card encerra sozinho.',
  categoria: 'publico',
  icone: '✉️',
  prazoProcesso: { horas: 120, ignorarFimDeSemana: true },
  etiquetas: [
    { nome: 'Urgente', cor: COR.vermelho },
    { nome: 'Circular', cor: COR.roxo },
    { nome: 'Aguardando Outra Secretaria', cor: COR.amarelo },
  ],
  etapas: [
    {
      id: 'memo_e1', nome: 'Enviado',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['memo_c1', 'memo_c4', 'memo_c3', 'memo_c7'],
      permiteMoverPara: ['memo_e2'],
      campos: [
        { id: 'memo_c1', nome: 'Assunto', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        { id: 'memo_c4', nome: 'Tipo de Documento', tipo: 'lista', obrigatorio: true, opcoes: ['Memorando', 'Ofício', 'Circular', 'Despacho'], coresOpcoes: { 'Memorando': COR.azul, 'Ofício': COR.verde, 'Circular': COR.roxo, 'Despacho': COR.cinza } },
        { id: 'memo_num', nome: 'Nº do Documento', tipo: 'texto', obrigatorio: false },
        { id: 'memo_c4b', nome: 'Natureza', tipo: 'lista', obrigatorio: true, opcoes: ['Solicitação', 'Informação', 'Encaminhamento'] },
        { id: 'memo_c2', nome: 'Secretaria de Origem', tipo: 'texto', obrigatorio: true },
        { id: 'memo_c3', nome: 'Secretaria de Destino', tipo: 'lista', obrigatorio: true, opcoes: ['Todas as secretarias', 'Gabinete do Prefeito', 'Procuradoria-Geral', 'Controladoria-Geral', 'Ouvidoria-Geral', 'Comunicação Social', 'Administração', 'Fazenda', 'Compras e Licitações', 'Saúde', 'Educação', 'Assistência Social', 'Obras e Serviços Urbanos', 'Planejamento e Urbanismo', 'Meio Ambiente', 'Agricultura', 'Cultura, Esporte e Turismo', 'Defesa Civil', 'Órgão externo'] },
        { id: 'memo_c3b', nome: 'Destinos da Circular', tipo: 'checkbox_multi', obrigatorio: false, opcoes: ['Gabinete do Prefeito', 'Procuradoria-Geral', 'Controladoria-Geral', 'Ouvidoria-Geral', 'Comunicação Social', 'Administração', 'Fazenda', 'Compras e Licitações', 'Saúde', 'Educação', 'Assistência Social', 'Obras e Serviços Urbanos', 'Planejamento e Urbanismo', 'Meio Ambiente', 'Agricultura', 'Cultura, Esporte e Turismo', 'Defesa Civil'] },
        { id: 'memo_ext', nome: 'Destinatário Externo', tipo: 'texto', obrigatorio: false },
        { id: 'memo_c5', nome: 'Conteúdo', tipo: 'texto_longo', obrigatorio: true },
        { id: 'memo_c6', nome: 'Anexos', tipo: 'anexo', obrigatorio: false },
        { id: 'memo_c7', nome: 'Prazo de Resposta', tipo: 'data_vencimento', obrigatorio: false },
      ],
    },
    {
      id: 'memo_e2', nome: 'Recebido no Destino',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['memo_c1', 'memo_c3', 'memo_c7'],
      permiteMoverPara: ['memo_e3', 'memo_e4'],
      permiteCriarCards: false,
      ordenacaoCards: 'mais_antigo',
      campos: [
        { id: 'memo_c8', nome: 'Recebido por', tipo: 'texto', obrigatorio: true },
        { id: 'memo_c9', nome: 'Precisa de resposta?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'memo_e3', nome: 'Em Atendimento',
      tempoMaximoHoras: 96,
      camposVisiveisNoCard: ['memo_c1', 'memo_c3', 'memo_c7'],
      permiteMoverPara: ['memo_e4'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'memo_c10', nome: 'Resposta / Providência', tipo: 'texto_longo', obrigatorio: true },
        { id: 'memo_c11', nome: 'Documentos', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'memo_e4', nome: 'Concluído',
      camposVisiveisNoCard: ['memo_c1', 'memo_c4', 'memo_c12'],
      permiteCriarCards: false,
      campos: [
        { id: 'memo_c12', nome: 'Data da Conclusão', tipo: 'data', obrigatorio: false },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'mover',
      nome: 'Sem resposta exigida, encerra; com resposta, vai para atendimento',
      etapaOrigemId: 'memo_e2',
      condicao: { campoId: 'memo_c9', operador: 'igual', valor: 'Não' },
      etapaDestinoId: 'memo_e4',
      etapaDestinoSenaoId: 'memo_e3',
    },
    {
      tipo: 'exibir_ocultar',
      nome: 'Destinos da circular só quando o tipo é Circular',
      etapaOrigemId: 'memo_e1',
      condicao: { campoId: 'memo_c4', operador: 'igual', valor: 'Circular' },
      camposAfetadosIds: ['memo_c3b'],
    },
    {
      tipo: 'exibir_ocultar',
      nome: 'Destinatário externo só quando o destino é órgão externo',
      etapaOrigemId: 'memo_e1',
      condicao: { campoId: 'memo_c3', operador: 'igual', valor: 'Órgão externo' },
      camposAfetadosIds: ['memo_ext'],
    },
  ],
  cards: [
    { id: 'memo_card1', titulo: 'Circular: recesso de fim de ano e escala de plantões', etapaId: 'memo_e2', etiquetas: ['Circular'], valores: { memo_c1: 'Circular: recesso de fim de ano e escala de plantões', memo_c4: 'Circular', memo_num: 'Circular 012/2026-ADM', memo_c4b: 'Informação', memo_c2: 'Administração', memo_c3: 'Todas as secretarias', memo_c3b: ['Saúde', 'Educação', 'Obras e Serviços Urbanos', 'Assistência Social', 'Defesa Civil'], memo_c5: 'Cada secretaria deve enviar até 31/10 a escala de plantão de 24/12 a 02/01.', memo_c7: '2026-10-31' } },
    { id: 'memo_card2', titulo: 'Memorando: dois servidores para o mutirão do IPTU', etapaId: 'memo_e3', etiquetas: ['Urgente'], valores: { memo_c1: 'Memorando: dois servidores para o mutirão do IPTU', memo_c4: 'Memorando', memo_num: 'MEMO 087/2026-FAZ', memo_c4b: 'Solicitação', memo_c2: 'Fazenda', memo_c3: 'Administração', memo_c5: 'Pedimos a cessão temporária de dois servidores do Protocolo para o mutirão de 13 a 17/10.', memo_c7: '2026-10-07', memo_c8: 'Chefe do Protocolo', memo_c9: 'Sim' } },
    { id: 'memo_card3', titulo: 'Memorando: veículo para visita técnica às escolas rurais', etapaId: 'memo_e1', valores: { memo_c1: 'Memorando: veículo para visita técnica às escolas rurais', memo_c4: 'Memorando', memo_num: 'MEMO 041/2026-EDU', memo_c4b: 'Solicitação', memo_c2: 'Educação', memo_c3: 'Administração', memo_c5: 'Veículo com motorista para 15/10, das 7h às 17h, para vistoria das 3 escolas da linha rural.', memo_c7: '2026-10-13' } },
    { id: 'memo_card4', titulo: 'Ofício ao DER/SC: sinalização no trevo de acesso', etapaId: 'memo_e4', valores: { memo_c1: 'Ofício ao DER/SC: sinalização no trevo de acesso', memo_c4: 'Ofício', memo_num: 'Of. GAB 288/2026', memo_c4b: 'Solicitação', memo_c2: 'Gabinete do Prefeito', memo_c3: 'Órgão externo', memo_ext: 'DER/SC, Superintendência Regional', memo_c5: 'Solicita a revisão da sinalização horizontal e vertical do trevo de acesso ao Centro.', memo_c8: 'Protocolo do DER/SC', memo_c9: 'Não', memo_c12: '2026-09-26' } },
    { id: 'memo_card5', titulo: 'Despacho: processo de isenção de IPTU para parecer jurídico', etapaId: 'memo_e3', etiquetas: ['Aguardando Outra Secretaria'], valores: { memo_c1: 'Despacho: processo de isenção de IPTU para parecer jurídico', memo_c4: 'Despacho', memo_num: 'Despacho 233/2026-FAZ', memo_c4b: 'Encaminhamento', memo_c2: 'Fazenda', memo_c3: 'Procuradoria-Geral', memo_c5: 'Encaminho o processo 2026/IPTU-000377 para parecer sobre a imunidade de entidade assistencial.', memo_c7: '2026-10-16', memo_c8: 'Procurador-Geral', memo_c9: 'Sim' } },
  ],
  tour: [
    '✉️ Memorando, ofício, circular e despacho com recebimento confirmado e prazo de resposta.',
    '1️⃣ "Enviado": tipo, origem, destino, conteúdo e prazo. Circular mostra os setores de destino.',
    '2️⃣ "Recebido no Destino": quem recebeu e se precisa de resposta; sem resposta, encerra sozinho.',
    '3️⃣ "Em Atendimento": resposta ou providência com documentos.',
    '📊 Filtre por secretaria de destino para ver quem está segurando os pedidos.',
  ],
  etapasDicas: [
    { quemFaz: 'Secretaria de origem', objetivo: 'Escrever um pedido claro, com prazo.', automacoes: ['Já configurado: destinos da circular e destinatário externo aparecem conforme o tipo'] },
    { quemFaz: 'Secretaria de destino', objetivo: 'Confirmar o recebimento no mesmo dia.', automacoes: ['Já configurado: sem resposta exigida, encerra'] },
    { quemFaz: 'Secretaria de destino', objetivo: 'Responder dentro do prazo.' },
    { quemFaz: 'Secretaria de origem', objetivo: 'Registrar a conclusão.' },
  ],
};

// CGM-01 (P1)
const cgmObrigacoes: DefaultTemplate = {
  id: 'tpl_cgm_obrigacoes',
  name: 'Agenda de Obrigações Legais (TCE, STN, SIOPS, SIOPE)',
  descricao: 'Uma obrigação por card (e-Sfinge, RREO, RGF, MSC, SIOPS, SIOPE, CAUC...), com prazo legal, sistema que gera, responsável, conferência da Controladoria e comprovante de envio. O Escalone não gera o arquivo: controla prazo, responsável e evidência.',
  categoria: 'publico',
  icone: '📅',
  etiquetas: [
    { nome: 'Bloqueia Convênio', cor: COR.vermelho },
    { nome: 'Multa TCE', cor: COR.amarelo },
  ],
  etapas: [
    {
      id: 'obr_e1', nome: 'A Fazer',
      camposVisiveisNoCard: ['obr_c1', 'obr_c2', 'obr_c4'],
      permiteMoverPara: ['obr_e2'],
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'obr_c1', nome: 'Obrigação', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        { id: 'obr_c2', nome: 'Órgão', tipo: 'lista', obrigatorio: true, opcoes: ['TCE/SC e-Sfinge', 'STN SICONFI', 'SIOPS', 'SIOPE', 'PNCP', 'Transferegov', 'eSocial / EFD-Reinf', 'Censo Escolar', 'RAIS/DIRF', 'CAUC', 'Câmara', 'Conselho'], coresOpcoes: { 'TCE/SC e-Sfinge': COR.amarelo, 'STN SICONFI': COR.azul, 'CAUC': COR.vermelho } },
        { id: 'obr_c3', nome: 'Período de Referência', tipo: 'texto', obrigatorio: true },
        { id: 'obr_c4', nome: 'Prazo Legal', tipo: 'data_vencimento', obrigatorio: true },
        { id: 'obr_c5', nome: 'Sistema que Gera', tipo: 'lista', obrigatorio: false, opcoes: ['SIAFIC', 'Tributário', 'Folha', 'Saúde', 'Educação', 'Manual'] },
        { id: 'obr_c6', nome: 'Responsável pela Geração', tipo: 'texto', obrigatorio: true },
      ],
    },
    {
      id: 'obr_e2', nome: 'Em Preparação',
      tempoMaximoHoras: 120,
      camposVisiveisNoCard: ['obr_c1', 'obr_c4', 'obr_c7'],
      permiteMoverPara: ['obr_e3'],
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'obr_c7', nome: 'Progresso', tipo: 'progresso', obrigatorio: false },
        { id: 'obr_c8', nome: 'Pendências Encontradas', tipo: 'texto_longo', obrigatorio: false },
      ],
    },
    {
      id: 'obr_e3', nome: 'Conferência da Controladoria',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['obr_c1', 'obr_c4', 'obr_c9'],
      permiteMoverPara: ['obr_e4', 'obr_e2'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      limparCamposAoRetornar: ['obr_c9', 'obr_c10'],
      campos: [
        { id: 'obr_c9', nome: 'Conferido?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'obr_c10', nome: 'Ajustes', tipo: 'texto_longo', obrigatorio: false },
      ],
    },
    {
      id: 'obr_e4', nome: 'Enviado',
      camposVisiveisNoCard: ['obr_c1', 'obr_c11', 'obr_c13'],
      permiteMoverPara: ['obr_e5'],
      permiteCriarCards: false,
      campos: [
        { id: 'obr_c11', nome: 'Data do Envio', tipo: 'data', obrigatorio: true },
        { id: 'obr_c12', nome: 'Recibo / Comprovante', tipo: 'anexo', obrigatorio: true },
        { id: 'obr_c13', nome: 'Enviado no prazo?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'obr_e5', nome: 'Retificação / Pendência no Órgão',
      tempoMaximoHoras: 120,
      camposVisiveisNoCard: ['obr_c1', 'obr_c2', 'obr_c15'],
      permiteMoverPara: ['obr_e4'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'obr_c14', nome: 'Motivo', tipo: 'texto_longo', obrigatorio: true },
        { id: 'obr_c15', nome: 'Novo Prazo', tipo: 'data_vencimento', obrigatorio: false },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'mover',
      nome: 'Conferido pela Controladoria segue para envio',
      etapaOrigemId: 'obr_e3',
      condicao: { campoId: 'obr_c9', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'obr_e4',
    },
    {
      tipo: 'exibir_ocultar',
      nome: 'Ajustes só quando a conferência reprova',
      etapaOrigemId: 'obr_e3',
      condicao: { campoId: 'obr_c9', operador: 'igual', valor: 'Não' },
      camposAfetadosIds: ['obr_c10'],
    },
  ],
  cards: [
    { id: 'obr_card1', titulo: 'RREO do 4º bimestre de 2026', etapaId: 'obr_e4', valores: { obr_c1: 'RREO do 4º bimestre de 2026', obr_c2: 'STN SICONFI', obr_c3: 'jul-ago/2026', obr_c4: '2026-09-30', obr_c5: 'SIAFIC', obr_c6: 'Contador-Geral', obr_c7: 100, obr_c9: 'Sim', obr_c11: '2026-09-28', obr_c13: 'Sim' } },
    { id: 'obr_card2', titulo: 'RGF do 2º quadrimestre de 2026', etapaId: 'obr_e5', etiquetas: ['Bloqueia Convênio'], valores: { obr_c1: 'RGF do 2º quadrimestre de 2026', obr_c2: 'STN SICONFI', obr_c3: 'mai-ago/2026', obr_c4: '2026-09-30', obr_c5: 'SIAFIC', obr_c6: 'Contador-Geral', obr_c7: 100, obr_c9: 'Sim', obr_c11: '2026-09-29', obr_c13: 'Sim', obr_c14: 'Despesa com pessoal do RGF diverge da MSC de agosto; retificar o anexo 1 e reenviar.', obr_c15: '2026-10-15' } },
    { id: 'obr_card3', titulo: 'e-Sfinge: remessa do 4º bimestre de 2026', etapaId: 'obr_e3', etiquetas: ['Multa TCE'], valores: { obr_c1: 'e-Sfinge: remessa do 4º bimestre de 2026', obr_c2: 'TCE/SC e-Sfinge', obr_c3: 'jul-ago/2026', obr_c4: '2026-10-30', obr_c5: 'SIAFIC', obr_c6: 'Contador-Geral e Chefe da Folha', obr_c7: 90, obr_c8: 'Atos de pessoal de agosto chegaram sem o número da portaria de 3 nomeações.' } },
    { id: 'obr_card4', titulo: 'SIOPS do 4º bimestre de 2026', etapaId: 'obr_e2', etiquetas: ['Bloqueia Convênio'], valores: { obr_c1: 'SIOPS do 4º bimestre de 2026', obr_c2: 'SIOPS', obr_c3: 'jul-ago/2026', obr_c4: '2026-09-30', obr_c5: 'Saúde', obr_c6: 'Fundo Municipal de Saúde', obr_c7: 60, obr_c8: 'Falta conciliar as despesas do consórcio intermunicipal de saúde.' } },
    { id: 'obr_card5', titulo: 'MSC de setembro de 2026', etapaId: 'obr_e2', valores: { obr_c1: 'MSC de setembro de 2026', obr_c2: 'STN SICONFI', obr_c3: 'set/2026', obr_c4: '2026-10-31', obr_c5: 'SIAFIC', obr_c6: 'Contador-Geral', obr_c7: 40 } },
    { id: 'obr_card6', titulo: 'SIOPE do 5º bimestre de 2026', etapaId: 'obr_e1', valores: { obr_c1: 'SIOPE do 5º bimestre de 2026', obr_c2: 'SIOPE', obr_c3: 'set-out/2026', obr_c4: '2026-11-30', obr_c5: 'Educação', obr_c6: 'Secretaria de Educação' } },
    { id: 'obr_card7', titulo: 'CAUC: conferência mensal de regularidade', etapaId: 'obr_e1', etiquetas: ['Bloqueia Convênio'], valores: { obr_c1: 'CAUC: conferência mensal de regularidade', obr_c2: 'CAUC', obr_c3: 'out/2026', obr_c4: '2026-10-20', obr_c5: 'Manual', obr_c6: 'Controlador-Geral' } },
  ],
  tour: [
    '📅 Uma obrigação legal por card: e-Sfinge, RREO, RGF, MSC, SIOPS, SIOPE, CAUC...',
    '1️⃣ "A Fazer": órgão, período, prazo legal, sistema que gera e responsável.',
    '2️⃣ "Em Preparação": progresso e pendências encontradas.',
    '3️⃣ "Conferência da Controladoria": conferido segue para envio; reprovado volta com ajustes.',
    '4️⃣ "Enviado": recibo anexado e se saiu no prazo.',
    '🔁 Use os modelos de card recorrente para criar a remessa de cada bimestre, quadrimestre e mês.',
  ],
  etapasDicas: [
    { quemFaz: 'Controladoria', objetivo: 'Abrir a obrigação com o prazo legal.' },
    { quemFaz: 'Contabilidade / setor que gera', objetivo: 'Gerar o arquivo no sistema legal e apontar pendências.' },
    { quemFaz: 'Controladoria', objetivo: 'Conferir antes de enviar.', automacoes: ['Já configurado: conferido segue para envio; ajustes aparecem quando reprova'] },
    { quemFaz: 'Setor que gera', objetivo: 'Anexar o recibo do órgão.' },
    { quemFaz: 'Setor que gera', objetivo: 'Retificar e reenviar até o novo prazo.' },
  ],
};

// CGM-02 (P2)
const cgmTce: DefaultTemplate = {
  id: 'tpl_cgm_tce',
  name: 'Diligências e Auditorias do TCE',
  descricao: 'Diligência, auditoria, alerta da LRF, citação e audiência do Tribunal de Contas: distribuição às secretarias, levantamento e defesa, revisão do Controle Interno e da Procuradoria, envio no prazo e plano de ação para as determinações. Prazo perdido aqui vira multa ao gestor.',
  categoria: 'publico',
  icone: '🔍',
  etiquetas: [
    { nome: 'Multa ao Gestor', cor: COR.vermelho },
    { nome: 'Alerta LRF', cor: COR.amarelo },
    { nome: 'Prorrogado', cor: COR.roxo },
  ],
  etapas: [
    {
      id: 'tce_e1', nome: 'Recebida',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['tce_c1', 'tce_c2', 'tce_prz'],
      permiteMoverPara: ['tce_e2'],
      campos: [
        { id: 'tce_c1', nome: 'Assunto', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        { id: 'tce_org', nome: 'Órgão de Origem', tipo: 'texto', obrigatorio: true },
        { id: 'tce_nof', nome: 'Nº do Ofício', tipo: 'texto', obrigatorio: true },
        { id: 'tce_dof', nome: 'Data do Recebimento', tipo: 'data', obrigatorio: true },
        { id: 'tce_prz', nome: 'Prazo de Resposta', tipo: 'data_vencimento', obrigatorio: true },
        { id: 'tce_arq', nome: 'Documento Recebido', tipo: 'anexo', obrigatorio: true },
        { id: 'tce_c2', nome: 'Tipo', tipo: 'lista', obrigatorio: true, opcoes: ['Diligência', 'Auditoria', 'Alerta LRF', 'Citação', 'Audiência', 'Decisão / Determinação'], coresOpcoes: { 'Citação': COR.vermelho, 'Audiência': COR.vermelho, 'Alerta LRF': COR.amarelo } },
        { id: 'tce_c3', nome: 'Nº do Processo no TCE', tipo: 'texto', obrigatorio: true },
        { id: 'tce_c4', nome: 'Risco de Multa ao Gestor?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'tce_e2', nome: 'Distribuição',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['tce_c1', 'tce_c6', 'tce_prz'],
      permiteMoverPara: ['tce_e3'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'tce_c5', nome: 'Secretarias Envolvidas', tipo: 'checkbox_multi', obrigatorio: true, opcoes: ['Gabinete', 'Administração', 'Fazenda', 'Compras e Licitações', 'Saúde', 'Educação', 'Assistência Social', 'Obras', 'Urbanismo', 'Meio Ambiente', 'Procuradoria'] },
        { id: 'tce_c6', nome: 'Coordenador da Resposta', tipo: 'texto', obrigatorio: true },
      ],
    },
    {
      id: 'tce_e3', nome: 'Levantamento e Defesa',
      tempoMaximoHoras: 240,
      camposVisiveisNoCard: ['tce_c1', 'tce_c6', 'tce_prz'],
      permiteMoverPara: ['tce_e4'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'tce_c7', nome: 'Esclarecimentos', tipo: 'texto_longo', obrigatorio: true },
        { id: 'tce_c8', nome: 'Documentos', tipo: 'anexo', obrigatorio: true },
      ],
    },
    {
      id: 'tce_e4', nome: 'Revisão do Controle Interno e da PGM',
      tempoMaximoHoras: 72,
      camposVisiveisNoCard: ['tce_c1', 'tce_c9', 'tce_prz'],
      permiteMoverPara: ['tce_e5', 'tce_e3'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      limparCamposAoRetornar: ['tce_c9', 'tce_c10'],
      campos: [
        { id: 'tce_c9', nome: 'Aprovado?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'tce_c10', nome: 'Ajustes', tipo: 'texto_longo', obrigatorio: false },
      ],
    },
    {
      id: 'tce_e5', nome: 'Enviado ao TCE',
      camposVisiveisNoCard: ['tce_c1', 'tce_c3', 'tce_c11'],
      permiteMoverPara: ['tce_e6'],
      permiteCriarCards: false,
      campos: [
        { id: 'tce_c11', nome: 'Data do Envio', tipo: 'data', obrigatorio: true },
        { id: 'tce_c12', nome: 'Protocolo no TCE', tipo: 'anexo', obrigatorio: true },
      ],
    },
    {
      id: 'tce_e6', nome: 'Decisão e Plano de Ação',
      camposVisiveisNoCard: ['tce_c1', 'tce_c13', 'tce_c15'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'tce_c13', nome: 'Resultado', tipo: 'lista', obrigatorio: false, opcoes: ['Regular', 'Regular com ressalvas', 'Irregular', 'Multa aplicada'], coresOpcoes: { 'Regular': COR.verde, 'Regular com ressalvas': COR.amarelo, 'Irregular': COR.vermelho, 'Multa aplicada': COR.vermelho } },
        { id: 'tce_c14', nome: 'Determinações a Cumprir', tipo: 'texto_longo', obrigatorio: false },
        { id: 'tce_c15', nome: 'Prazo das Determinações', tipo: 'data_vencimento', obrigatorio: false },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'mover',
      nome: 'Aprovado na revisão vai para envio; reprovado volta ao levantamento',
      etapaOrigemId: 'tce_e4',
      condicao: { campoId: 'tce_c9', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'tce_e5',
      etapaDestinoSenaoId: 'tce_e3',
    },
    {
      tipo: 'exibir_ocultar',
      nome: 'Ajustes só quando a revisão reprova',
      etapaOrigemId: 'tce_e4',
      condicao: { campoId: 'tce_c9', operador: 'igual', valor: 'Não' },
      camposAfetadosIds: ['tce_c10'],
    },
  ],
  cards: [
    { id: 'tce_card1', titulo: 'Diligência sobre o contrato de transporte escolar', etapaId: 'tce_e3', etiquetas: ['Multa ao Gestor'], valores: { tce_c1: 'Diligência sobre o contrato de transporte escolar', tce_org: 'Tribunal de Contas do Estado de Santa Catarina', tce_nof: 'Of. TCE/SC/DLC 8812/2026', tce_dof: '2026-09-29', tce_prz: '2026-10-29', tce_c2: 'Diligência', tce_c3: '@LCC 26/00456789', tce_c4: 'Sim', tce_c5: ['Educação', 'Compras e Licitações'], tce_c6: 'Controlador-Geral' } },
    { id: 'tce_card2', titulo: 'Alerta LRF: despesa com pessoal acima de 90% do limite', etapaId: 'tce_e2', etiquetas: ['Alerta LRF'], valores: { tce_c1: 'Alerta LRF: despesa com pessoal acima de 90% do limite', tce_org: 'Tribunal de Contas do Estado de Santa Catarina', tce_nof: 'Alerta 1543/2026', tce_dof: '2026-10-05', tce_prz: '2026-11-04', tce_c2: 'Alerta LRF', tce_c3: '@ALE 26/00011223' } },
    { id: 'tce_card3', titulo: 'Auditoria de obras: pavimentação da Rua XV', etapaId: 'tce_e4', etiquetas: ['Prorrogado'], valores: { tce_c1: 'Auditoria de obras: pavimentação da Rua XV', tce_org: 'Tribunal de Contas do Estado de Santa Catarina', tce_nof: 'Of. TCE/SC/DLC 7790/2026', tce_dof: '2026-08-20', tce_prz: '2026-10-20', tce_c2: 'Auditoria', tce_c3: '@RLA 26/00334455', tce_c5: ['Obras', 'Fazenda'], tce_c6: 'Engenheiro Fiscal das Obras', tce_c7: 'Medições 3 a 5 com memória de cálculo, diário de obra e ensaios de compactação.', tce_c9: 'Não', tce_c10: 'Anexar a ART do fiscal e explicar a diferença de 42 m² na medição 4.' } },
    { id: 'tce_card4', titulo: 'Citação: pagamento de diárias sem prestação de contas', etapaId: 'tce_e6', valores: { tce_c1: 'Citação: pagamento de diárias sem prestação de contas', tce_org: 'Tribunal de Contas do Estado de Santa Catarina', tce_nof: 'Citação 211/2026', tce_dof: '2026-05-12', tce_prz: '2026-06-11', tce_c2: 'Citação', tce_c3: '@TCE 25/00987654', tce_c4: 'Sim', tce_c5: ['Administração'], tce_c6: 'Controlador-Geral', tce_c9: 'Sim', tce_c11: '2026-06-09', tce_c13: 'Regular com ressalvas', tce_c14: 'Regulamentar as diárias por decreto e exigir relatório de viagem em 5 dias.', tce_c15: '2026-12-31' } },
  ],
  tour: [
    '🔍 Diligência, auditoria, alerta da LRF e citação do TCE com prazo e coordenador da resposta.',
    '1️⃣ "Recebida": tipo, nº do processo no TCE, prazo e risco de multa ao gestor.',
    '2️⃣ "Distribuição": secretarias envolvidas e coordenador.',
    '3️⃣ "Levantamento e Defesa": esclarecimentos e documentos.',
    '4️⃣ "Revisão": Controle Interno e PGM aprovam; reprovado volta ao levantamento.',
    '📌 "Decisão e Plano de Ação": determinações do Tribunal com prazo de cumprimento.',
  ],
  etapasDicas: [
    { quemFaz: 'Controladoria', objetivo: 'Registrar no dia com o prazo do Tribunal.' },
    { quemFaz: 'Controlador-Geral', objetivo: 'Escolher o coordenador e as secretarias.' },
    { quemFaz: 'Secretarias envolvidas', objetivo: 'Reunir provas e esclarecimentos.' },
    { quemFaz: 'Controle Interno / Procuradoria', objetivo: 'Revisar a defesa antes do envio.', automacoes: ['Já configurado: aprovado segue; reprovado volta com ajustes'] },
    { quemFaz: 'Controladoria', objetivo: 'Guardar o protocolo do envio.' },
    { quemFaz: 'Controladoria', objetivo: 'Acompanhar as determinações até cumprir.' },
  ],
};

// RH-01 (P2)
const rhLicencas: DefaultTemplate = {
  id: 'tpl_rh_licencas',
  name: 'Licenças e Afastamentos do Servidor',
  descricao: 'Licença para tratamento de saúde, maternidade, paternidade, prêmio, interesse particular e demais afastamentos do estatuto: chefia imediata, análise do RH com a base legal, perícia médica quando couber, portaria e lançamento na folha.',
  categoria: 'publico',
  icone: '🩺',
  etiquetas: [
    { nome: 'Perícia', cor: COR.roxo },
    { nome: 'Prorrogação', cor: COR.amarelo },
    { nome: 'Sem Substituto', cor: COR.vermelho },
  ],
  etapas: [
    {
      id: 'lic_e1', nome: 'Pedido',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['lic_c1', 'lic_c4', 'lic_c5'],
      permiteMoverPara: ['lic_e2'],
      campos: [
        { id: 'lic_c1', nome: 'Servidor', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        { id: 'lic_c2', nome: 'Matrícula', tipo: 'texto', obrigatorio: true },
        { id: 'lic_c3', nome: 'Lotação', tipo: 'texto', obrigatorio: true },
        { id: 'lic_c4', nome: 'Tipo', tipo: 'lista', obrigatorio: true, opcoes: ['Saúde', 'Acompanhar familiar', 'Maternidade', 'Paternidade', 'Prêmio', 'Interesse particular', 'Capacitação', 'Mandato classista', 'Gala', 'Nojo'], coresOpcoes: { 'Saúde': COR.roxo, 'Maternidade': COR.rosa, 'Interesse particular': COR.cinza } },
        { id: 'lic_c5', nome: 'Início', tipo: 'data', obrigatorio: true },
        { id: 'lic_c6', nome: 'Fim', tipo: 'data', obrigatorio: true },
        { id: 'lic_c7', nome: 'Atestado / Documentos', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'lic_e2', nome: 'Chefia Imediata',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['lic_c1', 'lic_c3', 'lic_c4'],
      permiteMoverPara: ['lic_e3', 'lic_e6'],
      permiteCriarCards: false,
      campos: [
        { id: 'lic_c8', nome: 'Ciente e de acordo?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'lic_c9', nome: 'Substituto', tipo: 'texto', obrigatorio: false },
      ],
    },
    {
      id: 'lic_e3', nome: 'Análise do RH',
      tempoMaximoHoras: 72,
      camposVisiveisNoCard: ['lic_c1', 'lic_c4', 'lic_c11'],
      permiteMoverPara: ['lic_e4', 'lic_e5', 'lic_e6'],
      permiteCriarCards: false,
      campos: [
        { id: 'lic_c10', nome: 'Requisitos legais atendidos?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'lic_c11', nome: 'Precisa de perícia?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'lic_c12', nome: 'Base Legal', tipo: 'texto', obrigatorio: false },
      ],
    },
    {
      id: 'lic_e4', nome: 'Perícia Médica',
      tempoMaximoHoras: 120,
      camposVisiveisNoCard: ['lic_c1', 'lic_c13', 'lic_c14'],
      permiteMoverPara: ['lic_e5', 'lic_e6'],
      permiteCriarCards: false,
      campos: [
        { id: 'lic_c13', nome: 'Data da Perícia', tipo: 'data', obrigatorio: true },
        { id: 'lic_c14', nome: 'Dias Concedidos', tipo: 'numero', obrigatorio: false },
        { id: 'lic_c15', nome: 'Laudo', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'lic_e5', nome: 'Concedida / Lançada na Folha',
      camposVisiveisNoCard: ['lic_c1', 'lic_c4', 'lic_c18'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'lic_c16', nome: 'Portaria', tipo: 'texto', obrigatorio: false },
        { id: 'lic_c17', nome: 'Lançado na folha?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'lic_c18', nome: 'Retorno Previsto', tipo: 'data_vencimento', obrigatorio: false },
      ],
    },
    {
      id: 'lic_e6', nome: 'Indeferida',
      camposVisiveisNoCard: ['lic_c1', 'lic_c4', 'lic_c20'],
      permiteCriarCards: false,
      campos: [
        { id: 'lic_c19', nome: 'Motivo', tipo: 'texto_longo', obrigatorio: true },
        { id: 'lic_c20', nome: 'Prazo para Recurso', tipo: 'data_vencimento', obrigatorio: false },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'mover',
      nome: 'Com perícia vai para a perícia médica; sem perícia, é concedida',
      etapaOrigemId: 'lic_e3',
      condicao: { campoId: 'lic_c11', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'lic_e4',
      etapaDestinoSenaoId: 'lic_e5',
    },
  ],
  cards: [
    { id: 'lic_card1', titulo: 'Ana Exemplo: licença-maternidade', etapaId: 'lic_e5', valores: { lic_c1: 'Ana Exemplo: licença-maternidade', lic_c2: '10.482', lic_c3: 'Educação: EMEF Central', lic_c4: 'Maternidade', lic_c5: '2026-09-15', lic_c6: '2027-03-13', lic_c8: 'Sim', lic_c9: 'Professora ACT da lista de 2026', lic_c10: 'Sim', lic_c11: 'Não', lic_c12: 'Estatuto, art. 98 (180 dias)', lic_c16: 'Portaria 498/2026', lic_c17: 'Sim', lic_c18: '2027-03-14' } },
    { id: 'lic_card2', titulo: 'Bruno Exemplo: tratamento de saúde, 30 dias', etapaId: 'lic_e4', etiquetas: ['Perícia'], valores: { lic_c1: 'Bruno Exemplo: tratamento de saúde, 30 dias', lic_c2: '08.117', lic_c3: 'Obras: equipe de pavimentação', lic_c4: 'Saúde', lic_c5: '2026-10-01', lic_c6: '2026-10-30', lic_c8: 'Sim', lic_c10: 'Sim', lic_c11: 'Sim', lic_c12: 'Estatuto, art. 92', lic_c13: '2026-10-09' } },
    { id: 'lic_card3', titulo: 'Carla Exemplo: licença-prêmio de 3 meses', etapaId: 'lic_e2', etiquetas: ['Sem Substituto'], valores: { lic_c1: 'Carla Exemplo: licença-prêmio de 3 meses', lic_c2: '05.330', lic_c3: 'Fazenda: Tributos', lic_c4: 'Prêmio', lic_c5: '2026-11-03', lic_c6: '2027-01-31' } },
    { id: 'lic_card4', titulo: 'Diego Exemplo: interesse particular, 2 anos', etapaId: 'lic_e6', valores: { lic_c1: 'Diego Exemplo: interesse particular, 2 anos', lic_c2: '12.004', lic_c3: 'Saúde: UBS Norte', lic_c4: 'Interesse particular', lic_c5: '2026-10-15', lic_c6: '2028-10-14', lic_c8: 'Não', lic_c19: 'Servidor em estágio probatório: o estatuto não permite a licença antes da estabilidade.', lic_c20: '2026-10-23' } },
    { id: 'lic_card5', titulo: 'Elisa Exemplo: paternidade (adoção)', etapaId: 'lic_e1', valores: { lic_c1: 'Elisa Exemplo: paternidade (adoção)', lic_c2: '11.276', lic_c3: 'Assistência Social: CRAS', lic_c4: 'Paternidade', lic_c5: '2026-10-10', lic_c6: '2026-10-29' } },
  ],
  tour: [
    '🩺 Licenças e afastamentos do estatuto com chefia, RH, perícia e lançamento na folha.',
    '1️⃣ "Pedido": servidor, tipo, período e atestado.',
    '2️⃣ "Chefia Imediata": ciência e substituto.',
    '3️⃣ "Análise do RH": requisitos e base legal; com perícia, o card vai para a perícia.',
    '✅ "Concedida": portaria, lançamento na folha e retorno previsto como vencimento.',
  ],
  etapasDicas: [
    { quemFaz: 'Servidor / RH', objetivo: 'Pedir com o atestado ou documento.' },
    { quemFaz: 'Chefia imediata', objetivo: 'Dar ciência e indicar substituto.' },
    { quemFaz: 'RH', objetivo: 'Conferir requisitos e base legal.', automacoes: ['Já configurado: com perícia vai para a perícia; sem perícia é concedida'] },
    { quemFaz: 'Junta médica', objetivo: 'Realizar a perícia e registrar os dias.' },
    { quemFaz: 'RH / Folha', objetivo: 'Publicar a portaria e lançar na folha.' },
    { quemFaz: 'RH', objetivo: 'Fundamentar o indeferimento e abrir prazo de recurso.' },
  ],
};

// RH-02 (P2)
const rhDiarias: DefaultTemplate = {
  id: 'tpl_rh_diarias',
  name: 'Diárias e Adiantamentos',
  descricao: 'Diária de viagem do servidor do pedido à prestação de contas: autorização do secretário, empenho e pagamento, viagem e relatório com comprovantes em até 5 dias da volta. Diária sem prestação de contas é apontamento certo do Tribunal de Contas.',
  categoria: 'publico',
  icone: '🧳',
  etiquetas: [
    { nome: 'Prestação Atrasada', cor: COR.vermelho },
    { nome: 'Adiantamento', cor: COR.azul },
  ],
  etapas: [
    {
      id: 'dia_e1', nome: 'Solicitação',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['dia_c1', 'dia_c5', 'dia_c8'],
      permiteMoverPara: ['dia_e2'],
      campos: [
        { id: 'dia_c1', nome: 'Servidor / Destino', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        { id: 'dia_c2', nome: 'Cargo', tipo: 'texto', obrigatorio: true },
        { id: 'dia_c3', nome: 'Destino', tipo: 'texto', obrigatorio: true },
        { id: 'dia_c4', nome: 'Objetivo da Viagem', tipo: 'texto_longo', obrigatorio: true },
        { id: 'dia_c5', nome: 'Saída', tipo: 'data', obrigatorio: true },
        { id: 'dia_c6', nome: 'Retorno', tipo: 'data', obrigatorio: true },
        { id: 'dia_c7', nome: 'Nº de Diárias', tipo: 'numero', obrigatorio: true },
        { id: 'dia_c8', nome: 'Valor Total', tipo: 'valor', obrigatorio: true },
        { id: 'dia_c9', nome: 'Convocação / Convite', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'dia_e2', nome: 'Autorização do Secretário',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['dia_c1', 'dia_c5', 'dia_c8'],
      permiteMoverPara: ['dia_e3', 'dia_e7'],
      permiteCriarCards: false,
      campos: [
        { id: 'dia_c10', nome: 'Autorizado?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'dia_e3', nome: 'Empenho e Pagamento',
      tempoMaximoHoras: 72,
      camposVisiveisNoCard: ['dia_c1', 'dia_c5', 'dia_c11'],
      permiteMoverPara: ['dia_e4'],
      permiteCriarCards: false,
      campos: [
        { id: 'dia_c11', nome: 'Nº do Empenho', tipo: 'texto', obrigatorio: true },
        { id: 'dia_c12', nome: 'Data do Pagamento', tipo: 'data', obrigatorio: true },
      ],
    },
    {
      id: 'dia_e4', nome: 'Viagem Realizada',
      camposVisiveisNoCard: ['dia_c1', 'dia_c6', 'dia_c13'],
      permiteMoverPara: ['dia_e5'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'dia_c13', nome: 'Prazo da Prestação de Contas', tipo: 'data_vencimento', obrigatorio: true },
      ],
    },
    {
      id: 'dia_e5', nome: 'Prestação de Contas',
      tempoMaximoHoras: 120,
      camposVisiveisNoCard: ['dia_c1', 'dia_c13', 'dia_c16'],
      permiteMoverPara: ['dia_e6', 'dia_e4'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'dia_c14', nome: 'Relatório de Viagem', tipo: 'texto_longo', obrigatorio: true },
        { id: 'dia_c15', nome: 'Comprovantes', tipo: 'anexo', obrigatorio: true },
        { id: 'dia_c16', nome: 'Saldo a Devolver', tipo: 'valor', obrigatorio: false },
      ],
    },
    {
      id: 'dia_e6', nome: 'Aprovada / Encerrada',
      camposVisiveisNoCard: ['dia_c1', 'dia_c8', 'dia_c17'],
      permiteCriarCards: false,
      campos: [
        { id: 'dia_c17', nome: 'Aprovada pelo controle?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'dia_e7', nome: 'Negada',
      camposVisiveisNoCard: ['dia_c1', 'dia_c18'],
      permiteCriarCards: false,
      campos: [
        { id: 'dia_c18', nome: 'Motivo', tipo: 'texto_longo', obrigatorio: true },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'mover',
      nome: 'Autorizada vai para empenho; negada encerra',
      etapaOrigemId: 'dia_e2',
      condicao: { campoId: 'dia_c10', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'dia_e3',
      etapaDestinoSenaoId: 'dia_e7',
    },
  ],
  cards: [
    { id: 'dia_card1', titulo: 'Secretário de Saúde: reunião na SES em Florianópolis', etapaId: 'dia_e5', etiquetas: ['Prestação Atrasada'], valores: { dia_c1: 'Secretário de Saúde: reunião na SES em Florianópolis', dia_c2: 'Secretário de Saúde', dia_c3: 'Florianópolis/SC', dia_c4: 'Reunião da CIB sobre o teto da média e alta complexidade.', dia_c5: '2026-09-30', dia_c6: '2026-10-01', dia_c7: 1.5, dia_c8: 585, dia_c10: 'Sim', dia_c11: '2026/3312', dia_c12: '2026-09-26', dia_c13: '2026-10-06', dia_c14: 'Participação na CIB; ata e lista de presença anexadas.' } },
    { id: 'dia_card2', titulo: 'Fiscal de tributos: curso de NFS-e nacional', etapaId: 'dia_e3', valores: { dia_c1: 'Fiscal de tributos: curso de NFS-e nacional', dia_c2: 'Auditor Fiscal', dia_c3: 'Blumenau/SC', dia_c4: 'Curso da FECAM sobre o emissor nacional e IBS/CBS.', dia_c5: '2026-10-20', dia_c6: '2026-10-21', dia_c7: 2, dia_c8: 640, dia_c10: 'Sim' } },
    { id: 'dia_card3', titulo: 'Motorista da Saúde: TFD para Joinville', etapaId: 'dia_e1', etiquetas: ['Adiantamento'], valores: { dia_c1: 'Motorista da Saúde: TFD para Joinville', dia_c2: 'Motorista', dia_c3: 'Joinville/SC', dia_c4: 'Transporte de 3 pacientes para consultas no hospital regional.', dia_c5: '2026-10-14', dia_c6: '2026-10-14', dia_c7: 0.5, dia_c8: 95 } },
    { id: 'dia_card4', titulo: 'Vereador convidado: evento em Brasília', etapaId: 'dia_e7', valores: { dia_c1: 'Vereador convidado: evento em Brasília', dia_c2: 'Assessor do Gabinete', dia_c3: 'Brasília/DF', dia_c4: 'Marcha dos prefeitos.', dia_c5: '2026-10-05', dia_c6: '2026-10-08', dia_c7: 3.5, dia_c8: 2450, dia_c10: 'Não', dia_c18: 'Sem dotação no elemento 3.3.90.14 do Gabinete neste exercício.' } },
  ],
  tour: [
    '🧳 Diária do pedido à prestação de contas, sem apontamento do Tribunal de Contas.',
    '1️⃣ "Solicitação": destino, objetivo, datas, nº de diárias e valor.',
    '2️⃣ "Autorização": autorizada vai para empenho; negada encerra com motivo.',
    '3️⃣ "Empenho e Pagamento": nº do empenho e data do pagamento.',
    '4️⃣ "Prestação de Contas": relatório e comprovantes em até 5 dias da volta.',
  ],
  etapasDicas: [
    { quemFaz: 'Servidor', objetivo: 'Pedir com antecedência e convocação anexada.' },
    { quemFaz: 'Secretário', objetivo: 'Autorizar conforme o interesse público.', automacoes: ['Já configurado: autorizada vai para empenho; negada encerra'] },
    { quemFaz: 'Contabilidade / Tesouraria', objetivo: 'Empenhar e pagar antes da viagem.' },
    { quemFaz: 'Servidor', objetivo: 'Viajar e preparar a prestação de contas.' },
    { quemFaz: 'Servidor', objetivo: 'Prestar contas no prazo, devolvendo saldo.' },
    { quemFaz: 'Controle Interno', objetivo: 'Aprovar a prestação.' },
    { quemFaz: 'Secretário', objetivo: 'Registrar o motivo da negativa.' },
  ],
};

// RH-03 (P2)
const rhProbatorio: DefaultTemplate = {
  id: 'tpl_rh_probatorio',
  name: 'Estágio Probatório',
  descricao: 'Avaliação do servidor nomeado em concurso durante os 3 anos do estágio (CF, art. 41): avaliação de cada período pela chefia, ciência do servidor, comissão de avaliação e portaria de estabilidade ou exoneração com direito de defesa. Use card pai para o servidor e filhos para cada período.',
  categoria: 'publico',
  icone: '📋',
  etiquetas: [
    { nome: 'Nota Abaixo da Média', cor: COR.vermelho },
    { nome: 'Recurso', cor: COR.amarelo },
  ],
  etapas: [
    {
      id: 'ep_e1', nome: 'Servidor em Estágio',
      camposVisiveisNoCard: ['ep_c1', 'ep_c3', 'ep_c5'],
      permiteMoverPara: ['ep_e2'],
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'ep_c1', nome: 'Servidor', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        { id: 'ep_c2', nome: 'Matrícula', tipo: 'texto', obrigatorio: true },
        { id: 'ep_c3', nome: 'Cargo', tipo: 'texto', obrigatorio: true },
        { id: 'ep_c4', nome: 'Data de Exercício', tipo: 'data', obrigatorio: true },
        { id: 'ep_c5', nome: 'Fim do Estágio', tipo: 'data_vencimento', obrigatorio: true },
      ],
    },
    {
      id: 'ep_e2', nome: 'Avaliação do Período',
      tempoMaximoHoras: 240,
      camposVisiveisNoCard: ['ep_c1', 'ep_c6', 'ep_c5'],
      permiteMoverPara: ['ep_e3'],
      permiteCriarCards: false,
      campos: [
        { id: 'ep_c6', nome: 'Período', tipo: 'lista', obrigatorio: true, opcoes: ['1º semestre', '2º semestre', '3º semestre', '4º semestre', '5º semestre', '6º semestre'] },
        { id: 'ep_c7', nome: 'Assiduidade', tipo: 'numero', obrigatorio: false, valorMinimo: 0, valorMaximo: 10 },
        { id: 'ep_c8', nome: 'Disciplina', tipo: 'numero', obrigatorio: false, valorMinimo: 0, valorMaximo: 10 },
        { id: 'ep_c9', nome: 'Produtividade', tipo: 'numero', obrigatorio: false, valorMinimo: 0, valorMaximo: 10 },
        { id: 'ep_c10', nome: 'Responsabilidade', tipo: 'numero', obrigatorio: false, valorMinimo: 0, valorMaximo: 10 },
        { id: 'ep_c11', nome: 'Iniciativa', tipo: 'numero', obrigatorio: false, valorMinimo: 0, valorMaximo: 10 },
        { id: 'ep_c12', nome: 'Ficha Assinada', tipo: 'anexo', obrigatorio: true },
      ],
    },
    {
      id: 'ep_e3', nome: 'Ciência do Servidor',
      tempoMaximoHoras: 120,
      camposVisiveisNoCard: ['ep_c1', 'ep_c6', 'ep_c13'],
      permiteMoverPara: ['ep_e4', 'ep_e1'],
      permiteCriarCards: false,
      campos: [
        { id: 'ep_c13', nome: 'Servidor ciente?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'ep_c14', nome: 'Recurso do Servidor', tipo: 'texto_longo', obrigatorio: false },
      ],
    },
    {
      id: 'ep_e4', nome: 'Comissão de Avaliação',
      tempoMaximoHoras: 240,
      camposVisiveisNoCard: ['ep_c1', 'ep_c15', 'ep_c16'],
      permiteMoverPara: ['ep_e5', 'ep_e6'],
      permiteCriarCards: false,
      campos: [
        { id: 'ep_c15', nome: 'Média Final', tipo: 'numero', obrigatorio: true, valorMinimo: 0, valorMaximo: 10 },
        { id: 'ep_c16', nome: 'Parecer da Comissão', tipo: 'lista', obrigatorio: true, opcoes: ['Aprovado', 'Reprovado'], coresOpcoes: { 'Aprovado': COR.verde, 'Reprovado': COR.vermelho } },
      ],
    },
    {
      id: 'ep_e5', nome: 'Estável',
      camposVisiveisNoCard: ['ep_c1', 'ep_c3', 'ep_c17'],
      permiteCriarCards: false,
      campos: [
        { id: 'ep_c17', nome: 'Portaria de Estabilidade', tipo: 'texto', obrigatorio: true },
      ],
    },
    {
      id: 'ep_e6', nome: 'Exoneração / Recurso',
      camposVisiveisNoCard: ['ep_c1', 'ep_c19'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'ep_c18', nome: 'Fundamentação', tipo: 'texto_longo', obrigatorio: true },
        { id: 'ep_c19', nome: 'Prazo de Defesa', tipo: 'data_vencimento', obrigatorio: true },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'mover',
      nome: 'Aprovado pela comissão fica estável; reprovado vai para exoneração com defesa',
      etapaOrigemId: 'ep_e4',
      condicao: { campoId: 'ep_c16', operador: 'igual', valor: 'Aprovado' },
      etapaDestinoId: 'ep_e5',
      etapaDestinoSenaoId: 'ep_e6',
    },
    {
      tipo: 'exibir_ocultar',
      nome: 'Recurso só quando o servidor não dá ciência',
      etapaOrigemId: 'ep_e3',
      condicao: { campoId: 'ep_c13', operador: 'igual', valor: 'Não' },
      camposAfetadosIds: ['ep_c14'],
    },
  ],
  cards: [
    { id: 'ep_card1', titulo: 'Fernanda Exemplo: Professora de Educação Infantil', etapaId: 'ep_e2', valores: { ep_c1: 'Fernanda Exemplo: Professora de Educação Infantil', ep_c2: '12.310', ep_c3: 'Professor de Educação Infantil', ep_c4: '2025-02-03', ep_c5: '2028-02-03', ep_c6: '3º semestre', ep_c7: 9, ep_c8: 10, ep_c9: 8, ep_c10: 9, ep_c11: 8 } },
    { id: 'ep_card2', titulo: 'Gustavo Exemplo: Agente Administrativo', etapaId: 'ep_e4', valores: { ep_c1: 'Gustavo Exemplo: Agente Administrativo', ep_c2: '10.977', ep_c3: 'Agente Administrativo', ep_c4: '2023-10-16', ep_c5: '2026-10-16', ep_c6: '6º semestre', ep_c13: 'Sim', ep_c15: 8.6 } },
    { id: 'ep_card3', titulo: 'Helena Exemplo: Enfermeira', etapaId: 'ep_e3', etiquetas: ['Nota Abaixo da Média', 'Recurso'], valores: { ep_c1: 'Helena Exemplo: Enfermeira', ep_c2: '11.802', ep_c3: 'Enfermeiro', ep_c4: '2024-08-01', ep_c5: '2027-08-01', ep_c6: '4º semestre', ep_c7: 5, ep_c8: 7, ep_c9: 6, ep_c10: 6, ep_c11: 7, ep_c13: 'Não', ep_c14: 'As faltas de agosto foram por atestado médico já entregue ao RH.' } },
    { id: 'ep_card4', titulo: 'Igor Exemplo: Engenheiro Civil', etapaId: 'ep_e5', valores: { ep_c1: 'Igor Exemplo: Engenheiro Civil', ep_c2: '10.455', ep_c3: 'Engenheiro Civil', ep_c4: '2023-06-01', ep_c5: '2026-06-01', ep_c6: '6º semestre', ep_c13: 'Sim', ep_c15: 9.1, ep_c16: 'Aprovado', ep_c17: 'Portaria 402/2026' } },
  ],
  tour: [
    '📋 Estágio probatório de 3 anos (CF, art. 41) com avaliação de cada período.',
    '1️⃣ "Servidor em Estágio": card pai do servidor, com o fim do estágio como vencimento.',
    '2️⃣ "Avaliação do Período": notas de 0 a 10 e ficha assinada pela chefia.',
    '3️⃣ "Ciência": sem ciência, o servidor registra o recurso.',
    '4️⃣ "Comissão": aprovado fica estável; reprovado vai para exoneração com prazo de defesa.',
  ],
  etapasDicas: [
    { quemFaz: 'RH', objetivo: 'Cadastrar o servidor na posse.' },
    { quemFaz: 'Chefia imediata', objetivo: 'Avaliar com fatos, não impressão.' },
    { quemFaz: 'Servidor', objetivo: 'Dar ciência ou recorrer.' },
    { quemFaz: 'Comissão de avaliação', objetivo: 'Fechar a média e o parecer.', automacoes: ['Já configurado: aprovado fica estável; reprovado vai para exoneração'] },
    { quemFaz: 'RH', objetivo: 'Publicar a portaria de estabilidade.' },
    { quemFaz: 'RH / Procuradoria', objetivo: 'Garantir defesa antes de exonerar.' },
  ],
};

// RH-04 (P2)
const rhPad: DefaultTemplate = {
  id: 'tpl_rh_pad',
  name: 'Sindicância e Processo Administrativo Disciplinar (PAD)',
  descricao: 'Notícia de irregularidade, juízo de admissibilidade, sindicância, instauração do PAD, instrução com citação e defesa, relatório da comissão e julgamento. Processo SIGILOSO: libere o fluxo só para a comissão e a Procuradoria (participantes restritos por etapa).',
  categoria: 'publico',
  icone: '🔒',
  prazoProcesso: { horas: 1440 },
  etiquetas: [
    { nome: 'Sigiloso', cor: COR.cinza },
    { nome: 'Prescrição Próxima', cor: COR.vermelho },
    { nome: 'Prorrogado', cor: COR.amarelo },
  ],
  etapas: [
    {
      id: 'pad_e1', nome: 'Notícia da Irregularidade',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['pad_c1', 'pad_c2'],
      permiteMoverPara: ['pad_e2'],
      campos: [
        { id: 'pad_c1', nome: 'Referência', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        { id: 'pad_c2', nome: 'Origem', tipo: 'lista', obrigatorio: true, opcoes: ['Chefia', 'Ouvidoria', 'Controladoria', 'MP', 'TCE', 'Denúncia'] },
        { id: 'pad_c3', nome: 'Fatos', tipo: 'texto_longo', obrigatorio: true },
        { id: 'pad_c4', nome: 'Provas Iniciais', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'pad_e2', nome: 'Juízo de Admissibilidade',
      tempoMaximoHoras: 120,
      camposVisiveisNoCard: ['pad_c1', 'pad_c5'],
      permiteMoverPara: ['pad_e3', 'pad_e4', 'pad_e8'],
      permiteCriarCards: false,
      campos: [
        { id: 'pad_c5', nome: 'Decisão', tipo: 'lista', obrigatorio: true, opcoes: ['Instaurar sindicância', 'Instaurar PAD', 'Arquivar'], coresOpcoes: { 'Instaurar PAD': COR.vermelho, 'Instaurar sindicância': COR.amarelo, 'Arquivar': COR.cinza } },
      ],
    },
    {
      id: 'pad_e3', nome: 'Sindicância',
      tempoMaximoHoras: 720,
      camposVisiveisNoCard: ['pad_c1', 'pad_c6'],
      permiteMoverPara: ['pad_e4', 'pad_e8'],
      permiteCriarCards: false,
      campos: [
        { id: 'pad_c6', nome: 'Portaria de Instauração', tipo: 'texto', obrigatorio: true },
        { id: 'pad_c7', nome: 'Relatório da Sindicância', tipo: 'anexo', obrigatorio: true },
      ],
    },
    {
      id: 'pad_e4', nome: 'Instauração do PAD',
      tempoMaximoHoras: 72,
      camposVisiveisNoCard: ['pad_c1', 'pad_c8'],
      permiteMoverPara: ['pad_e5'],
      permiteCriarCards: false,
      campos: [
        { id: 'pad_c8', nome: 'Portaria do PAD', tipo: 'texto', obrigatorio: true },
        { id: 'pad_c9', nome: 'Comissão', tipo: 'texto', obrigatorio: true },
        { id: 'pad_c10', nome: 'Servidor Acusado', tipo: 'texto', obrigatorio: true },
      ],
    },
    {
      id: 'pad_e5', nome: 'Instrução (defesa e provas)',
      tempoMaximoHoras: 1440,
      camposVisiveisNoCard: ['pad_c1', 'pad_c8', 'pad_c12'],
      permiteMoverPara: ['pad_e6'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'pad_c11', nome: 'Citação Realizada', tipo: 'data', obrigatorio: true },
        { id: 'pad_c12', nome: 'Prazo da Defesa', tipo: 'data_vencimento', obrigatorio: true },
        { id: 'pad_c13', nome: 'Termos e Depoimentos', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'pad_e6', nome: 'Relatório da Comissão',
      tempoMaximoHoras: 240,
      camposVisiveisNoCard: ['pad_c1', 'pad_c14'],
      permiteMoverPara: ['pad_e7'],
      permiteCriarCards: false,
      campos: [
        { id: 'pad_c14', nome: 'Conclusão', tipo: 'lista', obrigatorio: true, opcoes: ['Absolvição', 'Advertência', 'Suspensão', 'Demissão'], coresOpcoes: { 'Absolvição': COR.verde, 'Advertência': COR.amarelo, 'Suspensão': COR.amarelo, 'Demissão': COR.vermelho } },
        { id: 'pad_c15', nome: 'Relatório Final', tipo: 'anexo', obrigatorio: true },
      ],
    },
    {
      id: 'pad_e7', nome: 'Julgamento',
      tempoMaximoHoras: 480,
      camposVisiveisNoCard: ['pad_c1', 'pad_c14', 'pad_c16'],
      permiteMoverPara: ['pad_e8'],
      permiteCriarCards: false,
      campos: [
        { id: 'pad_c16', nome: 'Decisão da Autoridade', tipo: 'lista', obrigatorio: true, opcoes: ['Absolvição', 'Advertência', 'Suspensão', 'Demissão', 'Arquivamento'], coresOpcoes: { 'Absolvição': COR.verde, 'Advertência': COR.amarelo, 'Suspensão': COR.amarelo, 'Demissão': COR.vermelho, 'Arquivamento': COR.cinza } },
        { id: 'pad_c17', nome: 'Ato de Julgamento', tipo: 'anexo', obrigatorio: true },
      ],
    },
    {
      id: 'pad_e8', nome: 'Arquivado / Encerrado',
      camposVisiveisNoCard: ['pad_c1', 'pad_c16', 'pad_c18'],
      permiteCriarCards: false,
      campos: [
        { id: 'pad_c18', nome: 'Publicação', tipo: 'texto', obrigatorio: false },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'mover',
      nome: 'Admissibilidade: sindicância',
      etapaOrigemId: 'pad_e2',
      condicao: { campoId: 'pad_c5', operador: 'igual', valor: 'Instaurar sindicância' },
      etapaDestinoId: 'pad_e3',
    },
    {
      tipo: 'mover',
      nome: 'Admissibilidade: PAD',
      etapaOrigemId: 'pad_e2',
      condicao: { campoId: 'pad_c5', operador: 'igual', valor: 'Instaurar PAD' },
      etapaDestinoId: 'pad_e4',
    },
    {
      tipo: 'mover',
      nome: 'Admissibilidade: arquivar',
      etapaOrigemId: 'pad_e2',
      condicao: { campoId: 'pad_c5', operador: 'igual', valor: 'Arquivar' },
      etapaDestinoId: 'pad_e8',
    },
  ],
  cards: [
    { id: 'pad_card1', titulo: 'PAD 004/2026: abandono de cargo', etapaId: 'pad_e5', etiquetas: ['Sigiloso'], valores: { pad_c1: 'PAD 004/2026: abandono de cargo', pad_c2: 'Chefia', pad_c3: 'Servidor sem comparecer desde 02/06/2026, sem justificativa.', pad_c5: 'Instaurar PAD', pad_c8: 'Portaria 455/2026', pad_c9: 'Comissão Permanente de PAD', pad_c10: 'Servidor A (sigiloso)', pad_c11: '2026-09-25', pad_c12: '2026-10-05' } },
    { id: 'pad_card2', titulo: 'Sindicância 011/2026: uso de veículo fora do serviço', etapaId: 'pad_e3', etiquetas: ['Sigiloso'], valores: { pad_c1: 'Sindicância 011/2026: uso de veículo fora do serviço', pad_c2: 'Ouvidoria', pad_c3: 'Denúncia com foto de veículo oficial em área de lazer num domingo.', pad_c5: 'Instaurar sindicância', pad_c6: 'Portaria 470/2026' } },
    { id: 'pad_card3', titulo: 'Notícia: cobrança indevida no setor de tributos', etapaId: 'pad_e2', etiquetas: ['Sigiloso'], valores: { pad_c1: 'Notícia: cobrança indevida no setor de tributos', pad_c2: 'Ouvidoria', pad_c3: 'Manifestação OUV-2026-01844 relata cobrança de "taxa de agilidade" no balcão.' } },
    { id: 'pad_card4', titulo: 'PAD 002/2026: assédio moral', etapaId: 'pad_e7', etiquetas: ['Sigiloso', 'Prescrição Próxima'], valores: { pad_c1: 'PAD 002/2026: assédio moral', pad_c2: 'Denúncia', pad_c3: 'Três servidores relatam humilhações públicas pela chefia.', pad_c5: 'Instaurar PAD', pad_c8: 'Portaria 210/2026', pad_c9: 'Comissão Permanente de PAD', pad_c10: 'Servidor B (sigiloso)', pad_c11: '2026-04-10', pad_c12: '2026-04-20', pad_c14: 'Suspensão' } },
  ],
  tour: [
    '🔒 Sindicância e PAD com prazos e trilha. Libere o fluxo só para a comissão e a Procuradoria.',
    '1️⃣ "Notícia da Irregularidade": origem, fatos e provas iniciais.',
    '2️⃣ "Admissibilidade": a decisão já leva o card para sindicância, PAD ou arquivo.',
    '3️⃣ "Instrução": citação e prazo de defesa como vencimento.',
    '⚖️ "Julgamento": decisão da autoridade e ato anexado.',
  ],
  etapasDicas: [
    { quemFaz: 'Chefia / Corregedoria', objetivo: 'Registrar os fatos sem expor nomes fora do fluxo.' },
    { quemFaz: 'Autoridade competente', objetivo: 'Decidir sindicância, PAD ou arquivo.', automacoes: ['Já configurado: a decisão move o card'] },
    { quemFaz: 'Comissão de sindicância', objetivo: 'Apurar e relatar.' },
    { quemFaz: 'Autoridade competente', objetivo: 'Instaurar por portaria com a comissão.' },
    { quemFaz: 'Comissão do PAD', objetivo: 'Citar, ouvir e garantir a defesa.' },
    { quemFaz: 'Comissão do PAD', objetivo: 'Relatar com conclusão fundamentada.' },
    { quemFaz: 'Autoridade competente', objetivo: 'Julgar e publicar.' },
    { quemFaz: 'Corregedoria', objetivo: 'Arquivar com a publicação.' },
  ],
};

// ADM-02 (P2)
const admPatrimonio: DefaultTemplate = {
  id: 'tpl_adm_patrimonio',
  name: 'Patrimônio: Transferência, Baixa e Inventário',
  descricao: 'Movimentação de bens patrimoniais: transferência entre setores, conserto, cessão, incorporação e baixa com comissão (leilão, doação ou descarte), termo de responsabilidade e registro no sistema patrimonial ligado à contabilidade.',
  categoria: 'publico',
  icone: '🏷️',
  etiquetas: [
    { nome: 'Inventário Anual', cor: COR.azul },
    { nome: 'Bem Não Localizado', cor: COR.vermelho },
  ],
  etapas: [
    {
      id: 'pat_e1', nome: 'Solicitação',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['pat_c1', 'pat_c2', 'pat_c3'],
      permiteMoverPara: ['pat_e2'],
      campos: [
        { id: 'pat_c1', nome: 'Bem / Plaqueta', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        { id: 'pat_c2', nome: 'Tipo', tipo: 'lista', obrigatorio: true, opcoes: ['Transferência', 'Baixa', 'Cessão', 'Conserto', 'Incorporação'], coresOpcoes: { 'Baixa': COR.vermelho, 'Transferência': COR.azul, 'Incorporação': COR.verde } },
        { id: 'pat_c3', nome: 'Setor de Origem', tipo: 'texto', obrigatorio: true },
        { id: 'pat_c4', nome: 'Setor de Destino', tipo: 'texto', obrigatorio: false },
        { id: 'pat_c5', nome: 'Estado do Bem', tipo: 'lista', obrigatorio: false, opcoes: ['Bom', 'Regular', 'Inservível', 'Sucata'], coresOpcoes: { 'Bom': COR.verde, 'Regular': COR.amarelo, 'Inservível': COR.vermelho, 'Sucata': COR.cinza } },
        { id: 'pat_c6', nome: 'Fotos', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'pat_e2', nome: 'Análise do Patrimônio',
      tempoMaximoHoras: 72,
      camposVisiveisNoCard: ['pat_c1', 'pat_c2', 'pat_c7'],
      permiteMoverPara: ['pat_e3', 'pat_e4'],
      permiteCriarCards: false,
      campos: [
        { id: 'pat_c7', nome: 'Vistoria realizada?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'pat_c8', nome: 'Laudo', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'pat_e3', nome: 'Comissão de Baixa / Leilão',
      tempoMaximoHoras: 240,
      camposVisiveisNoCard: ['pat_c1', 'pat_c9'],
      permiteMoverPara: ['pat_e4'],
      permiteCriarCards: false,
      campos: [
        { id: 'pat_c9', nome: 'Destino', tipo: 'lista', obrigatorio: true, opcoes: ['Leilão', 'Doação', 'Descarte', 'Reaproveitamento'] },
        { id: 'pat_c10', nome: 'Ata da Comissão', tipo: 'anexo', obrigatorio: true },
      ],
    },
    {
      id: 'pat_e4', nome: 'Termo de Responsabilidade',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['pat_c1', 'pat_c2', 'pat_c4'],
      permiteMoverPara: ['pat_e5'],
      permiteCriarCards: false,
      campos: [
        { id: 'pat_c11', nome: 'Termo Assinado', tipo: 'anexo', obrigatorio: true },
      ],
    },
    {
      id: 'pat_e5', nome: 'Registrado no Sistema Patrimonial',
      camposVisiveisNoCard: ['pat_c1', 'pat_c2', 'pat_c12'],
      permiteCriarCards: false,
      campos: [
        { id: 'pat_c12', nome: 'Lançado no sistema (SIAFIC)?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'exibir_ocultar',
      nome: 'Setor de destino só na transferência',
      etapaOrigemId: 'pat_e1',
      condicao: { campoId: 'pat_c2', operador: 'igual', valor: 'Transferência' },
      camposAfetadosIds: ['pat_c4'],
    },
  ],
  cards: [
    { id: 'pat_card1', titulo: 'PAT 004512: notebook da Fazenda para o Protocolo', etapaId: 'pat_e4', valores: { pat_c1: 'PAT 004512: notebook da Fazenda para o Protocolo', pat_c2: 'Transferência', pat_c3: 'Fazenda: Tributos', pat_c4: 'Administração: Protocolo Geral', pat_c5: 'Bom', pat_c7: 'Sim' } },
    { id: 'pat_card2', titulo: 'Lote de 38 cadeiras escolares inservíveis', etapaId: 'pat_e3', etiquetas: ['Inventário Anual'], valores: { pat_c1: 'Lote de 38 cadeiras escolares inservíveis', pat_c2: 'Baixa', pat_c3: 'Educação: EMEF Central', pat_c5: 'Inservível', pat_c7: 'Sim', pat_c9: 'Leilão' } },
    { id: 'pat_card3', titulo: 'PAT 001877: roçadeira costal sumida do almoxarifado', etapaId: 'pat_e2', etiquetas: ['Bem Não Localizado'], valores: { pat_c1: 'PAT 001877: roçadeira costal sumida do almoxarifado', pat_c2: 'Baixa', pat_c3: 'Obras: Almoxarifado' } },
    { id: 'pat_card4', titulo: 'Doação de ambulância pelo Estado', etapaId: 'pat_e5', valores: { pat_c1: 'Doação de ambulância pelo Estado', pat_c2: 'Incorporação', pat_c3: 'Governo do Estado', pat_c4: 'Saúde: Transporte Sanitário', pat_c5: 'Bom', pat_c7: 'Sim', pat_c12: 'Sim' } },
  ],
  tour: [
    '🏷️ Transferência, baixa, cessão, conserto e incorporação de bens com termo e registro.',
    '1️⃣ "Solicitação": plaqueta, tipo, setores e estado do bem.',
    '2️⃣ "Análise": vistoria e laudo.',
    '3️⃣ "Comissão de Baixa": destino (leilão, doação, descarte) com ata.',
    '✅ "Registrado": lançado no sistema patrimonial ligado à contabilidade.',
  ],
  etapasDicas: [
    { quemFaz: 'Setor detentor', objetivo: 'Pedir com foto e estado do bem.', automacoes: ['Já configurado: setor de destino aparece na transferência'] },
    { quemFaz: 'Patrimônio', objetivo: 'Vistoriar e laudar.' },
    { quemFaz: 'Comissão de baixa', objetivo: 'Decidir o destino com ata.' },
    { quemFaz: 'Setor que recebe', objetivo: 'Assinar o termo de responsabilidade.' },
    { quemFaz: 'Patrimônio', objetivo: 'Lançar no sistema patrimonial.' },
  ],
};

// ADM-03 (P2)
const admFrota: DefaultTemplate = {
  id: 'tpl_adm_frota',
  name: 'Frota: Manutenção, Abastecimento e Multas',
  descricao: 'Ocorrências da frota oficial: manutenção corretiva e preventiva, pneus, sinistro e multa. Orçamento com oficina credenciada, ordem de serviço, nota fiscal para a execução da despesa e prazo de 30 dias para indicar o condutor da multa.',
  categoria: 'publico',
  icone: '🚐',
  etiquetas: [
    { nome: 'Multa', cor: COR.vermelho },
    { nome: 'Preventiva', cor: COR.azul },
    { nome: 'Veículo Parado', cor: COR.amarelo },
  ],
  etapas: [
    {
      id: 'frt_e1', nome: 'Ocorrência',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['frt_c1', 'frt_c2', 'frt_c3'],
      permiteMoverPara: ['frt_e2', 'frt_e5'],
      campos: [
        { id: 'frt_c1', nome: 'Veículo / Placa', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        { id: 'frt_c2', nome: 'Secretaria', tipo: 'texto', obrigatorio: true },
        { id: 'frt_c3', nome: 'Tipo', tipo: 'lista', obrigatorio: true, opcoes: ['Manutenção corretiva', 'Preventiva', 'Pneus', 'Sinistro', 'Multa'], coresOpcoes: { 'Multa': COR.vermelho, 'Sinistro': COR.vermelho, 'Preventiva': COR.azul } },
        { id: 'frt_c4', nome: 'Hodômetro', tipo: 'numero', obrigatorio: false },
        { id: 'frt_c5', nome: 'Descrição', tipo: 'texto_longo', obrigatorio: true },
        { id: 'frt_c6', nome: 'Fotos / Auto da Multa', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'frt_e2', nome: 'Orçamento',
      tempoMaximoHoras: 120,
      camposVisiveisNoCard: ['frt_c1', 'frt_c7', 'frt_c8'],
      permiteMoverPara: ['frt_e3'],
      permiteCriarCards: false,
      campos: [
        { id: 'frt_c7', nome: 'Oficina Credenciada / Contrato', tipo: 'texto', obrigatorio: true },
        { id: 'frt_c8', nome: 'Valor', tipo: 'valor', obrigatorio: true },
        { id: 'frt_c9', nome: 'Orçamento', tipo: 'anexo', obrigatorio: true },
      ],
    },
    {
      id: 'frt_e3', nome: 'Autorizado / Em Serviço',
      tempoMaximoHoras: 240,
      camposVisiveisNoCard: ['frt_c1', 'frt_c10', 'frt_c11'],
      permiteMoverPara: ['frt_e4'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'frt_c10', nome: 'Nº da Ordem de Serviço', tipo: 'texto', obrigatorio: true },
        { id: 'frt_c11', nome: 'Previsão de Entrega', tipo: 'data_vencimento', obrigatorio: false },
      ],
    },
    {
      id: 'frt_e4', nome: 'Concluído',
      camposVisiveisNoCard: ['frt_c1', 'frt_c3', 'frt_c13'],
      permiteCriarCards: false,
      campos: [
        { id: 'frt_c12', nome: 'Nota Fiscal', tipo: 'anexo', obrigatorio: true },
        { id: 'frt_c13', nome: 'Enviado à Execução da Despesa?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'frt_e5', nome: 'Multa: Identificar Condutor',
      tempoMaximoHoras: 360,
      camposVisiveisNoCard: ['frt_c1', 'frt_c14', 'frt_c15'],
      permiteMoverPara: ['frt_e4'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'frt_c14', nome: 'Prazo para Indicar Condutor', tipo: 'data_vencimento', obrigatorio: true },
        { id: 'frt_c15', nome: 'Condutor', tipo: 'texto', obrigatorio: false },
        { id: 'frt_c16', nome: 'Recurso apresentado?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
  ],
  cards: [
    { id: 'frt_card1', titulo: 'MEX-1A23: troca de pastilhas e discos', etapaId: 'frt_e3', etiquetas: ['Veículo Parado'], valores: { frt_c1: 'MEX-1A23: troca de pastilhas e discos', frt_c2: 'Saúde', frt_c3: 'Manutenção corretiva', frt_c4: 148220, frt_c5: 'Ruído ao frear e trepidação no pedal.', frt_c7: 'Oficina credenciada (Contrato 031/2026)', frt_c8: 1840, frt_c10: 'OS 2026/0912', frt_c11: '2026-10-10' } },
    { id: 'frt_card2', titulo: 'QWE-4B56: multa por excesso de velocidade na SC-401', etapaId: 'frt_e5', etiquetas: ['Multa'], valores: { frt_c1: 'QWE-4B56: multa por excesso de velocidade na SC-401', frt_c2: 'Educação', frt_c3: 'Multa', frt_c5: 'Auto de infração recebido pelo correio; 68 km/h em via de 60 km/h.', frt_c14: '2026-10-30' } },
    { id: 'frt_card3', titulo: 'RTY-7C89: revisão de 60.000 km', etapaId: 'frt_e2', etiquetas: ['Preventiva'], valores: { frt_c1: 'RTY-7C89: revisão de 60.000 km', frt_c2: 'Administração', frt_c3: 'Preventiva', frt_c4: 60120, frt_c5: 'Revisão do plano do fabricante.', frt_c7: 'Concessionária (Ata de Registro de Preços 012/2026)', frt_c8: 1320 } },
    { id: 'frt_card4', titulo: 'Retroescavadeira 03: pneu dianteiro furado', etapaId: 'frt_e1', valores: { frt_c1: 'Retroescavadeira 03: pneu dianteiro furado', frt_c2: 'Obras e Serviços Urbanos', frt_c3: 'Pneus', frt_c4: 8450, frt_c5: 'Pneu dianteiro esquerdo cortado na obra da Rua XV.' } },
  ],
  tour: [
    '🚐 Manutenção, pneus, sinistro e multa da frota oficial com orçamento e ordem de serviço.',
    '1️⃣ "Ocorrência": placa, secretaria, tipo, hodômetro e fotos.',
    '2️⃣ "Orçamento": oficina credenciada e valor.',
    '3️⃣ "Em Serviço": OS e previsão de entrega.',
    '🚨 "Multa": 30 dias para indicar o condutor; o prazo vira o vencimento do card.',
  ],
  etapasDicas: [
    { quemFaz: 'Motorista / secretaria', objetivo: 'Registrar no dia com foto.' },
    { quemFaz: 'Gestão da frota', objetivo: 'Orçar na oficina do contrato.' },
    { quemFaz: 'Gestão da frota', objetivo: 'Emitir a OS e cobrar a entrega.' },
    { quemFaz: 'Gestão da frota', objetivo: 'Anexar a NF e mandar para a despesa.', conexoes: ['Ligue ao fluxo "Execução da Despesa"'] },
    { quemFaz: 'Gestão da frota', objetivo: 'Indicar o condutor antes do prazo.' },
  ],
};

// ADM-04 (P2)
const admVeiculo: DefaultTemplate = {
  id: 'tpl_adm_veiculo',
  name: 'Solicitação de Veículo',
  descricao: 'Pedido de veículo oficial pelas secretarias: agendamento com veículo e motorista, saída e chegada com quilometragem e ocorrências. Substitui o caderno da garagem e mostra o uso da frota por secretaria.',
  categoria: 'publico',
  icone: '🚗',
  etiquetas: [
    { nome: 'Viagem Intermunicipal', cor: COR.roxo },
    { nome: 'Urgente', cor: COR.vermelho },
  ],
  etapas: [
    {
      id: 'vei_e1', nome: 'Pedido',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['vei_c1', 'vei_c2', 'vei_c3'],
      permiteMoverPara: ['vei_e2', 'vei_e5'],
      ordenacaoCards: 'mais_antigo',
      campos: [
        { id: 'vei_c1', nome: 'Finalidade', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        { id: 'vei_c2', nome: 'Secretaria', tipo: 'texto', obrigatorio: true },
        { id: 'vei_c3', nome: 'Data e Hora', tipo: 'data', obrigatorio: true },
        { id: 'vei_c4', nome: 'Destino', tipo: 'texto', obrigatorio: true },
        { id: 'vei_c5', nome: 'Passageiros', tipo: 'numero', obrigatorio: false },
        { id: 'vei_c6', nome: 'Precisa de motorista?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'vei_e2', nome: 'Agendado',
      camposVisiveisNoCard: ['vei_c1', 'vei_c3', 'vei_c7'],
      permiteMoverPara: ['vei_e3', 'vei_e5'],
      permiteCriarCards: false,
      ordenacaoCards: 'mais_antigo',
      campos: [
        { id: 'vei_c7', nome: 'Veículo', tipo: 'texto', obrigatorio: true },
        { id: 'vei_c8', nome: 'Motorista', tipo: 'texto', obrigatorio: false },
      ],
    },
    {
      id: 'vei_e3', nome: 'Em Uso',
      camposVisiveisNoCard: ['vei_c1', 'vei_c7', 'vei_c8'],
      permiteMoverPara: ['vei_e4'],
      permiteCriarCards: false,
      campos: [
        { id: 'vei_c9', nome: 'Km de Saída', tipo: 'numero', obrigatorio: true },
      ],
    },
    {
      id: 'vei_e4', nome: 'Devolvido',
      camposVisiveisNoCard: ['vei_c1', 'vei_c7', 'vei_c10'],
      permiteCriarCards: false,
      campos: [
        { id: 'vei_c10', nome: 'Km de Chegada', tipo: 'numero', obrigatorio: true },
        { id: 'vei_c11', nome: 'Ocorrências', tipo: 'texto_longo', obrigatorio: false },
      ],
    },
    {
      id: 'vei_e5', nome: 'Não Atendido',
      camposVisiveisNoCard: ['vei_c1', 'vei_c12'],
      permiteCriarCards: false,
      campos: [
        { id: 'vei_c12', nome: 'Motivo', tipo: 'texto_longo', obrigatorio: true },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'exibir_ocultar',
      nome: 'Motorista só quando o pedido precisa de motorista',
      etapaOrigemId: 'vei_e2',
      condicao: { campoId: 'vei_c6', operador: 'igual', valor: 'Sim' },
      camposAfetadosIds: ['vei_c8'],
    },
  ],
  cards: [
    { id: 'vei_card1', titulo: 'Visita técnica às escolas rurais', etapaId: 'vei_e2', valores: { vei_c1: 'Visita técnica às escolas rurais', vei_c2: 'Educação', vei_c3: '2026-10-15', vei_c4: 'Linha rural (3 escolas)', vei_c5: 3, vei_c6: 'Sim', vei_c7: 'Van MEX-2D45', vei_c8: 'Motorista da Garagem 02' } },
    { id: 'vei_card2', titulo: 'Reunião na AMFRI em Itajaí', etapaId: 'vei_e3', etiquetas: ['Viagem Intermunicipal'], valores: { vei_c1: 'Reunião na AMFRI em Itajaí', vei_c2: 'Gabinete do Prefeito', vei_c3: '2026-10-08', vei_c4: 'Itajaí/SC', vei_c5: 2, vei_c6: 'Não', vei_c7: 'Sedan MEX-9F01', vei_c9: 88412 } },
    { id: 'vei_card3', titulo: 'Entrega de cestas básicas no Bairro Sul', etapaId: 'vei_e1', etiquetas: ['Urgente'], valores: { vei_c1: 'Entrega de cestas básicas no Bairro Sul', vei_c2: 'Assistência Social', vei_c3: '2026-10-09', vei_c4: 'Bairro Sul', vei_c5: 2, vei_c6: 'Sim' } },
    { id: 'vei_card4', titulo: 'Vistoria de alvará sanitário', etapaId: 'vei_e4', valores: { vei_c1: 'Vistoria de alvará sanitário', vei_c2: 'Saúde', vei_c3: '2026-10-06', vei_c4: 'Centro', vei_c5: 2, vei_c6: 'Não', vei_c7: 'Hatch MEX-3E22', vei_c9: 40211, vei_c10: 40236 } },
  ],
  tour: [
    '🚗 Pedido de veículo oficial com agenda, motorista e quilometragem.',
    '1️⃣ "Pedido": finalidade, data, destino e passageiros.',
    '2️⃣ "Agendado": veículo e motorista.',
    '3️⃣ "Em Uso" e "Devolvido": km de saída e de chegada.',
    '📊 Filtre por secretaria para ver o uso da frota.',
  ],
  etapasDicas: [
    { quemFaz: 'Secretaria', objetivo: 'Pedir com antecedência.' },
    { quemFaz: 'Garagem', objetivo: 'Agendar veículo e motorista.', automacoes: ['Já configurado: motorista aparece quando o pedido precisa'] },
    { quemFaz: 'Motorista', objetivo: 'Anotar o km de saída.' },
    { quemFaz: 'Motorista', objetivo: 'Anotar o km de chegada e ocorrências.' },
    { quemFaz: 'Garagem', objetivo: 'Explicar por que não atendeu.' },
  ],
};

// CMP-01 (P2)
const cmpCrc: DefaultTemplate = {
  id: 'tpl_cmp_crc',
  name: 'Cadastro de Fornecedores (CRC)',
  descricao: 'Certificado de Registro Cadastral do fornecedor: pedido com documentos de habilitação (ideal pelo portal), conferência documental, pendências, emissão do CRC com validade e renovação das certidões antes de vencer.',
  categoria: 'publico',
  icone: '🪪',
  etiquetas: [
    { nome: 'ME/EPP', cor: COR.verde },
    { nome: 'Certidão Vencida', cor: COR.vermelho },
  ],
  etapas: [
    {
      id: 'crc_e1', nome: 'Pedido de Cadastro',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['crc_c1', 'crc_c2', 'crc_c5'],
      permiteMoverPara: ['crc_e2'],
      campos: [
        { id: 'crc_c1', nome: 'Razão Social', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        { id: 'crc_c2', nome: 'CNPJ', tipo: 'texto', obrigatorio: true },
        { id: 'crc_c3', nome: 'E-mail', tipo: 'texto', obrigatorio: true },
        { id: 'crc_c4', nome: 'WhatsApp', tipo: 'telefone', obrigatorio: false },
        { id: 'crc_c5', nome: 'Ramo / CNAE', tipo: 'texto', obrigatorio: true },
        { id: 'crc_c6', nome: 'Porte', tipo: 'lista', obrigatorio: false, opcoes: ['MEI', 'ME', 'EPP', 'Demais'] },
        { id: 'crc_c7', nome: 'Documentos de Habilitação', tipo: 'anexo', obrigatorio: true },
      ],
    },
    {
      id: 'crc_e2', nome: 'Conferência Documental',
      tempoMaximoHoras: 120,
      camposVisiveisNoCard: ['crc_c1', 'crc_c9'],
      permiteMoverPara: ['crc_e3', 'crc_e4'],
      permiteCriarCards: false,
      campos: [
        { id: 'crc_c8', nome: 'Checklist', tipo: 'checkbox_multi', obrigatorio: true, opcoes: ['Contrato social', 'CND Federal', 'CND Estadual', 'CND Municipal', 'FGTS', 'CNDT', 'Balanço', 'Atestados'] },
        { id: 'crc_c9', nome: 'Documentação completa?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'crc_e3', nome: 'Pendência do Fornecedor',
      tempoMaximoHoras: 240,
      camposVisiveisNoCard: ['crc_c1', 'crc_c10'],
      permiteMoverPara: ['crc_e2'],
      permiteCriarCards: false,
      campos: [
        { id: 'crc_c10', nome: 'Exigência', tipo: 'texto_longo', obrigatorio: true },
      ],
    },
    {
      id: 'crc_e4', nome: 'Cadastrado (CRC Emitido)',
      camposVisiveisNoCard: ['crc_c1', 'crc_c11', 'crc_c13'],
      permiteMoverPara: ['crc_e5'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'crc_c11', nome: 'Nº do CRC', tipo: 'texto', obrigatorio: true },
        { id: 'crc_c12', nome: 'Validade', tipo: 'data_vencimento', obrigatorio: true },
        { id: 'crc_c13', nome: 'Vencimento da 1ª Certidão', tipo: 'data_vencimento', obrigatorio: true },
      ],
    },
    {
      id: 'crc_e5', nome: 'Renovação',
      tempoMaximoHoras: 240,
      camposVisiveisNoCard: ['crc_c1', 'crc_c13'],
      permiteMoverPara: ['crc_e4'],
      permiteCriarCards: false,
      campos: [
        { id: 'crc_c14', nome: 'Certidões Atualizadas', tipo: 'anexo', obrigatorio: true },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'mover',
      nome: 'Documentação completa emite o CRC; incompleta abre pendência',
      etapaOrigemId: 'crc_e2',
      condicao: { campoId: 'crc_c9', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'crc_e4',
      etapaDestinoSenaoId: 'crc_e3',
    },
  ],
  cards: [
    { id: 'crc_card1', titulo: 'Construtora Exemplo Ltda', etapaId: 'crc_e4', valores: { crc_c1: 'Construtora Exemplo Ltda', crc_c2: '12.345.678/0001-90', crc_c3: 'licita@construtoraexemplo.com.br', crc_c5: '4211-1/01 Construção de rodovias', crc_c6: 'Demais', crc_c8: ['Contrato social', 'CND Federal', 'CND Estadual', 'CND Municipal', 'FGTS', 'CNDT', 'Balanço', 'Atestados'], crc_c9: 'Sim', crc_c11: 'CRC 087/2026', crc_c12: '2027-09-30', crc_c13: '2026-10-28' } },
    { id: 'crc_card2', titulo: 'Papelaria Exemplo ME', etapaId: 'crc_e3', etiquetas: ['ME/EPP', 'Certidão Vencida'], valores: { crc_c1: 'Papelaria Exemplo ME', crc_c2: '23.456.789/0001-01', crc_c3: 'contato@papelariaexemplo.com.br', crc_c5: '4761-0/03 Comércio de papelaria', crc_c6: 'ME', crc_c8: ['Contrato social', 'CND Federal', 'FGTS'], crc_c9: 'Não', crc_c10: 'Enviar a CND Estadual e a CNDT; a CND Municipal venceu em 30/09.' } },
    { id: 'crc_card3', titulo: 'Gráfica Exemplo EPP', etapaId: 'crc_e1', etiquetas: ['ME/EPP'], valores: { crc_c1: 'Gráfica Exemplo EPP', crc_c2: '34.567.890/0001-12', crc_c3: 'comercial@graficaexemplo.com.br', crc_c5: '1813-0/01 Impressão de material publicitário', crc_c6: 'EPP' } },
    { id: 'crc_card4', titulo: 'Transportes Exemplo Ltda', etapaId: 'crc_e5', etiquetas: ['Certidão Vencida'], valores: { crc_c1: 'Transportes Exemplo Ltda', crc_c2: '45.678.901/0001-23', crc_c3: 'adm@transportesexemplo.com.br', crc_c5: '4924-8/00 Transporte escolar', crc_c6: 'Demais', crc_c9: 'Sim', crc_c11: 'CRC 041/2026', crc_c12: '2027-03-31', crc_c13: '2026-10-05' } },
  ],
  tour: [
    '🪪 Cadastro de fornecedores com certidões e validade do CRC.',
    '1️⃣ "Pedido de Cadastro": razão social, CNPJ, ramo e documentos (ideal pelo portal).',
    '2️⃣ "Conferência": checklist; completa emite o CRC, incompleta abre pendência.',
    '3️⃣ "CRC Emitido": validade e vencimento da 1ª certidão como alerta.',
    '🔁 Ligue um e-mail ao fornecedor 15 dias antes do vencimento da certidão.',
  ],
  etapasDicas: [
    { quemFaz: 'Fornecedor', objetivo: 'Enviar todos os documentos de habilitação.' },
    { quemFaz: 'Compras', objetivo: 'Conferir o checklist.', automacoes: ['Já configurado: completa emite o CRC; incompleta abre pendência'] },
    { quemFaz: 'Fornecedor', objetivo: 'Corrigir a pendência.' },
    { quemFaz: 'Compras', objetivo: 'Emitir o CRC com a validade.' },
    { quemFaz: 'Fornecedor', objetivo: 'Atualizar as certidões antes de vencer.' },
  ],
};

// CMP-02 (P2)
const cmpPagamento: DefaultTemplate = {
  id: 'tpl_cmp_pagamento',
  name: 'Pedido de Pagamento do Fornecedor (NF e Medição)',
  descricao: 'O fornecedor envia a nota fiscal, a medição e as certidões (ideal pelo portal); o fiscal atesta (integral, com glosa ou não), a contabilidade liquida e a tesouraria paga na ordem cronológica da Lei 14.133, art. 141. Pendência do fornecedor pausa o prazo.',
  categoria: 'publico',
  icone: '🧾',
  prazoProcesso: { horas: 720, etapasPausaIds: ['pgf_e3'] },
  etiquetas: [
    { nome: 'Ordem Cronológica', cor: COR.azul },
    { nome: 'Glosa', cor: COR.vermelho },
    { nome: 'Certidão Vencida', cor: COR.amarelo },
  ],
  etapas: [
    {
      id: 'pgf_e1', nome: 'NF / Medição Recebida',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['pgf_c1', 'pgf_c3', 'pgf_c5'],
      permiteMoverPara: ['pgf_e2'],
      ordenacaoCards: 'mais_antigo',
      campos: [
        { id: 'pgf_c1', nome: 'Fornecedor / NF', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        { id: 'pgf_c2', nome: 'CNPJ', tipo: 'texto', obrigatorio: true },
        { id: 'pgf_c3', nome: 'Nº do Contrato / Empenho', tipo: 'texto', obrigatorio: true },
        { id: 'pgf_c4', nome: 'Nº da NF', tipo: 'texto', obrigatorio: true },
        { id: 'pgf_c5', nome: 'Valor', tipo: 'valor', obrigatorio: true },
        { id: 'pgf_c6', nome: 'NF', tipo: 'anexo', obrigatorio: true },
        { id: 'pgf_c7', nome: 'Medição / Relatório', tipo: 'anexo', obrigatorio: false },
        { id: 'pgf_c8', nome: 'Certidões de Regularidade', tipo: 'anexo', obrigatorio: true },
      ],
    },
    {
      id: 'pgf_e2', nome: 'Atesto do Fiscal',
      tempoMaximoHoras: 120,
      camposVisiveisNoCard: ['pgf_c1', 'pgf_c9', 'pgf_c10'],
      permiteMoverPara: ['pgf_e4', 'pgf_e3'],
      permiteCriarCards: false,
      ordenacaoCards: 'mais_antigo',
      limparCamposAoRetornar: ['pgf_c10', 'pgf_c11'],
      campos: [
        { id: 'pgf_c9', nome: 'Fiscal do Contrato', tipo: 'texto', obrigatorio: true },
        { id: 'pgf_c10', nome: 'Atesto', tipo: 'lista', obrigatorio: true, opcoes: ['Atestado integral', 'Atestado com glosa', 'Não atestado'], coresOpcoes: { 'Atestado integral': COR.verde, 'Atestado com glosa': COR.amarelo, 'Não atestado': COR.vermelho } },
        { id: 'pgf_c11', nome: 'Valor Glosado', tipo: 'valor', obrigatorio: false },
        { id: 'pgf_c12', nome: 'Termo de Recebimento', tipo: 'anexo', obrigatorio: true },
      ],
    },
    {
      id: 'pgf_e3', nome: 'Pendência do Fornecedor',
      tempoMaximoHoras: 240,
      camposVisiveisNoCard: ['pgf_c1', 'pgf_c13'],
      permiteMoverPara: ['pgf_e2'],
      permiteCriarCards: false,
      campos: [
        { id: 'pgf_c13', nome: 'O que corrigir', tipo: 'texto_longo', obrigatorio: true },
      ],
    },
    {
      id: 'pgf_e4', nome: 'Liquidação (Contabilidade)',
      tempoMaximoHoras: 72,
      camposVisiveisNoCard: ['pgf_c1', 'pgf_c5', 'pgf_c14'],
      permiteMoverPara: ['pgf_e5'],
      permiteCriarCards: false,
      ordenacaoCards: 'mais_antigo',
      campos: [
        { id: 'pgf_c14', nome: 'Nº da Liquidação', tipo: 'texto', obrigatorio: true },
        { id: 'pgf_c15', nome: 'Retenções (ISS/IR/INSS)', tipo: 'valor', obrigatorio: false },
      ],
    },
    {
      id: 'pgf_e5', nome: 'Ordem de Pagamento (Tesouraria)',
      tempoMaximoHoras: 120,
      camposVisiveisNoCard: ['pgf_c1', 'pgf_c16', 'pgf_c17'],
      permiteMoverPara: ['pgf_e6'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'pgf_c16', nome: 'Posição na Ordem Cronológica', tipo: 'numero', obrigatorio: false },
        { id: 'pgf_c17', nome: 'Previsão de Pagamento', tipo: 'data_vencimento', obrigatorio: true },
      ],
    },
    {
      id: 'pgf_e6', nome: 'Pago',
      camposVisiveisNoCard: ['pgf_c1', 'pgf_c5', 'pgf_c18'],
      permiteCriarCards: false,
      campos: [
        { id: 'pgf_c18', nome: 'Data do Pagamento', tipo: 'data', obrigatorio: true },
        { id: 'pgf_c19', nome: 'Comprovante', tipo: 'anexo', obrigatorio: true },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'mover',
      nome: 'Não atestado volta ao fornecedor; atestado segue para liquidação',
      etapaOrigemId: 'pgf_e2',
      condicao: { campoId: 'pgf_c10', operador: 'igual', valor: 'Não atestado' },
      etapaDestinoId: 'pgf_e3',
      etapaDestinoSenaoId: 'pgf_e4',
    },
    {
      tipo: 'exibir_ocultar',
      nome: 'Valor glosado só no atesto com glosa',
      etapaOrigemId: 'pgf_e2',
      condicao: { campoId: 'pgf_c10', operador: 'igual', valor: 'Atestado com glosa' },
      camposAfetadosIds: ['pgf_c11'],
    },
  ],
  cards: [
    { id: 'pgf_card1', titulo: 'Limpeza Exemplo: NF 2231, setembro/2026', etapaId: 'pgf_e4', etiquetas: ['Glosa', 'Ordem Cronológica'], valores: { pgf_c1: 'Limpeza Exemplo: NF 2231, setembro/2026', pgf_c2: '56.789.012/0001-34', pgf_c3: 'Contrato 045/2026 / Empenho 2026/1180', pgf_c4: '2231', pgf_c5: 48900, pgf_c9: 'Fiscal do contrato de limpeza', pgf_c10: 'Atestado com glosa', pgf_c11: 1630 } },
    { id: 'pgf_card2', titulo: 'Construtora Exemplo: medição 5 da Rua XV', etapaId: 'pgf_e2', valores: { pgf_c1: 'Construtora Exemplo: medição 5 da Rua XV', pgf_c2: '12.345.678/0001-90', pgf_c3: 'Contrato 019/2026 / Empenho 2026/0640', pgf_c4: '981', pgf_c5: 212450 } },
    { id: 'pgf_card3', titulo: 'Papelaria Exemplo: NF 455, material de expediente', etapaId: 'pgf_e3', etiquetas: ['Certidão Vencida'], valores: { pgf_c1: 'Papelaria Exemplo: NF 455, material de expediente', pgf_c2: '23.456.789/0001-01', pgf_c3: 'Ata 007/2026 / Empenho 2026/1302', pgf_c4: '455', pgf_c5: 3215.4, pgf_c9: 'Almoxarife', pgf_c10: 'Não atestado', pgf_c13: 'A NF saiu com o CNPJ da Câmara; emitir nova NF para a Prefeitura.' } },
    { id: 'pgf_card4', titulo: 'Transportes Exemplo: transporte escolar, setembro', etapaId: 'pgf_e5', etiquetas: ['Ordem Cronológica'], valores: { pgf_c1: 'Transportes Exemplo: transporte escolar, setembro', pgf_c2: '45.678.901/0001-23', pgf_c3: 'Contrato 003/2026 / Empenho 2026/0105', pgf_c4: '3390', pgf_c5: 87320, pgf_c9: 'Coordenação do Transporte Escolar', pgf_c10: 'Atestado integral', pgf_c14: 'LIQ 2026/2877', pgf_c15: 4366, pgf_c16: 3, pgf_c17: '2026-10-15' } },
    { id: 'pgf_card5', titulo: 'Gráfica Exemplo: NF 1201, carnês do IPTU', etapaId: 'pgf_e6', valores: { pgf_c1: 'Gráfica Exemplo: NF 1201, carnês do IPTU', pgf_c2: '34.567.890/0001-12', pgf_c3: 'Contrato 002/2026 / Empenho 2026/0088', pgf_c4: '1201', pgf_c5: 14800, pgf_c10: 'Atestado integral', pgf_c14: 'LIQ 2026/0410', pgf_c16: 1, pgf_c17: '2026-02-20', pgf_c18: '2026-02-19' } },
  ],
  tour: [
    '🧾 NF e medição do fornecedor até o pagamento, na ordem cronológica (Lei 14.133, art. 141).',
    '1️⃣ "NF / Medição Recebida": contrato, empenho, NF, valor e certidões.',
    '2️⃣ "Atesto do Fiscal": não atestado volta ao fornecedor e pausa o prazo.',
    '3️⃣ "Liquidação": nº da liquidação e retenções.',
    '4️⃣ "Ordem de Pagamento": posição na fila cronológica e previsão.',
  ],
  etapasDicas: [
    { quemFaz: 'Fornecedor / Protocolo', objetivo: 'Enviar NF, medição e certidões válidas.' },
    { quemFaz: 'Fiscal do contrato', objetivo: 'Atestar o que foi entregue.', automacoes: ['Já configurado: não atestado volta; glosa mostra o valor glosado'] },
    { quemFaz: 'Fornecedor', objetivo: 'Corrigir a NF ou a medição.' },
    { quemFaz: 'Contabilidade', objetivo: 'Liquidar no SIAFIC.' },
    { quemFaz: 'Tesouraria', objetivo: 'Pagar na ordem cronológica.' },
    { quemFaz: 'Tesouraria', objetivo: 'Anexar o comprovante.' },
  ],
};

// FAZ-01 (P2)
const fazFiscalizacao: DefaultTemplate = {
  id: 'tpl_faz_fiscalizacao',
  name: 'Fiscalização Tributária e Auto de Infração',
  descricao: 'Ação fiscal do ISS, IPTU, ITBI e taxas: ordem de fiscalização, termo de início com intimação, auditoria, auto de infração com prazo de defesa, julgamento de 1ª instância e crédito constituído (pago, parcelado ou para a dívida ativa). O prazo do contribuinte pausa o relógio.',
  categoria: 'publico',
  icone: '🕵️',
  prazoProcesso: { horas: 2160, etapasPausaIds: ['aft_e4'] },
  etiquetas: [
    { nome: 'Malha Fiscal', cor: COR.azul },
    { nome: 'Decadência Próxima', cor: COR.vermelho },
    { nome: 'Grande Contribuinte', cor: COR.roxo },
  ],
  etapas: [
    {
      id: 'aft_e1', nome: 'Ordem de Fiscalização',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['aft_c1', 'aft_c2', 'aft_c4'],
      permiteMoverPara: ['aft_e2'],
      campos: [
        { id: 'aft_c1', nome: 'Contribuinte', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        { id: 'aft_doc', nome: 'CPF / CNPJ', tipo: 'texto', obrigatorio: true },
        { id: 'aft_nome', nome: 'Nome / Razão Social', tipo: 'texto', obrigatorio: true },
        { id: 'aft_whats', nome: 'WhatsApp', tipo: 'telefone', obrigatorio: false },
        { id: 'aft_email', nome: 'E-mail', tipo: 'texto', obrigatorio: false },
        { id: 'aft_bairro', nome: 'Bairro', tipo: 'texto', obrigatorio: false },
        { id: 'aft_c2', nome: 'Tributo', tipo: 'lista', obrigatorio: true, opcoes: ['ISS', 'IPTU', 'ITBI', 'Taxas'] },
        { id: 'aft_c3', nome: 'Origem', tipo: 'lista', obrigatorio: true, opcoes: ['Malha fiscal', 'Denúncia', 'Cruzamento NFS-e', 'Plano anual', 'Pedido de outro órgão'] },
        { id: 'aft_c4', nome: 'Período Fiscalizado', tipo: 'texto', obrigatorio: true },
      ],
    },
    {
      id: 'aft_e2', nome: 'Termo de Início e Intimação',
      tempoMaximoHoras: 120,
      camposVisiveisNoCard: ['aft_c1', 'aft_c2', 'aft_c6'],
      permiteMoverPara: ['aft_e3'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'aft_c5', nome: 'Termo de Início', tipo: 'anexo', obrigatorio: true },
        { id: 'aft_c6', nome: 'Prazo de Entrega de Documentos', tipo: 'data_vencimento', obrigatorio: true },
      ],
    },
    {
      id: 'aft_e3', nome: 'Auditoria',
      tempoMaximoHoras: 720,
      camposVisiveisNoCard: ['aft_c1', 'aft_c2', 'aft_c8'],
      permiteMoverPara: ['aft_e4', 'aft_e7'],
      permiteCriarCards: false,
      campos: [
        { id: 'aft_c7', nome: 'Irregularidade encontrada?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
        { id: 'aft_c8', nome: 'Valor Apurado', tipo: 'valor', obrigatorio: false },
        { id: 'aft_c9', nome: 'Papéis de Trabalho', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'aft_e4', nome: 'Auto de Infração Lavrado / Prazo de Defesa',
      tempoMaximoHoras: 720,
      camposVisiveisNoCard: ['aft_c1', 'aft_c11', 'aft_c12'],
      permiteMoverPara: ['aft_e5', 'aft_e6'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'aft_c10', nome: 'Nº do Auto', tipo: 'texto', obrigatorio: true },
        { id: 'aft_c11', nome: 'Valor do Auto', tipo: 'valor', obrigatorio: true },
        { id: 'aft_c12', nome: 'Prazo de Defesa', tipo: 'data_vencimento', obrigatorio: true },
        { id: 'aft_c13', nome: 'Defesa apresentada?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'aft_e5', nome: 'Julgamento de 1ª Instância',
      tempoMaximoHoras: 720,
      camposVisiveisNoCard: ['aft_c1', 'aft_c11', 'aft_c14'],
      permiteMoverPara: ['aft_e6', 'aft_e7'],
      permiteCriarCards: false,
      campos: [
        { id: 'aft_c14', nome: 'Decisão', tipo: 'lista', obrigatorio: true, opcoes: ['Procedente', 'Parcialmente procedente', 'Improcedente'], coresOpcoes: { 'Procedente': COR.vermelho, 'Parcialmente procedente': COR.amarelo, 'Improcedente': COR.verde } },
        { id: 'aft_c15', nome: 'Decisão (documento)', tipo: 'anexo', obrigatorio: true },
      ],
    },
    {
      id: 'aft_e6', nome: 'Crédito Constituído',
      camposVisiveisNoCard: ['aft_c1', 'aft_c11', 'aft_c16'],
      permiteCriarCards: false,
      campos: [
        { id: 'aft_c16', nome: 'Situação do Crédito', tipo: 'lista', obrigatorio: true, opcoes: ['Pago', 'Parcelado', 'Inscrito em Dívida Ativa'], coresOpcoes: { 'Pago': COR.verde, 'Parcelado': COR.azul, 'Inscrito em Dívida Ativa': COR.vermelho } },
        { id: 'aft_c17', nome: 'Gerou card na Dívida Ativa?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'aft_e7', nome: 'Encerrada sem Lançamento',
      camposVisiveisNoCard: ['aft_c1', 'aft_c2'],
      permiteCriarCards: false,
      campos: [
        { id: 'aft_c18', nome: 'Termo de Encerramento', tipo: 'anexo', obrigatorio: true },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'mover',
      nome: 'Irregularidade lavra o auto; sem irregularidade encerra',
      etapaOrigemId: 'aft_e3',
      condicao: { campoId: 'aft_c7', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'aft_e4',
      etapaDestinoSenaoId: 'aft_e7',
    },
    {
      tipo: 'mover',
      nome: 'Com defesa vai a julgamento; sem defesa o crédito se constitui',
      etapaOrigemId: 'aft_e4',
      condicao: { campoId: 'aft_c13', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'aft_e5',
      etapaDestinoSenaoId: 'aft_e6',
    },
  ],
  cards: [
    { id: 'aft_card1', titulo: 'Serviços Exemplo Ltda: ISS 2024 e 2025', etapaId: 'aft_e3', etiquetas: ['Malha Fiscal', 'Grande Contribuinte'], valores: { aft_c1: 'Serviços Exemplo Ltda: ISS 2024 e 2025', aft_doc: '67.890.123/0001-45', aft_nome: 'Serviços Exemplo Ltda', aft_email: 'fiscal@servicosexemplo.com.br', aft_bairro: 'Centro', aft_c2: 'ISS', aft_c3: 'Cruzamento NFS-e', aft_c4: '01/2024 a 12/2025', aft_c6: '2026-09-15' } },
    { id: 'aft_card2', titulo: 'Clínica Exemplo: ISS de 2021', etapaId: 'aft_e4', etiquetas: ['Decadência Próxima'], valores: { aft_c1: 'Clínica Exemplo: ISS de 2021', aft_doc: '78.901.234/0001-56', aft_nome: 'Clínica Exemplo S/S', aft_c2: 'ISS', aft_c3: 'Plano anual', aft_c4: '01/2021 a 12/2021', aft_c7: 'Sim', aft_c8: 38450, aft_c10: 'AI 0034/2026', aft_c11: 52890, aft_c12: '2026-11-05' } },
    { id: 'aft_card3', titulo: 'Imóvel com construção não averbada', etapaId: 'aft_e1', valores: { aft_c1: 'Imóvel com construção não averbada', aft_doc: '123.456.789-00', aft_nome: 'Mário Exemplo', aft_bairro: 'Jardim', aft_c2: 'IPTU', aft_c3: 'Denúncia', aft_c4: '2022 a 2026' } },
    { id: 'aft_card4', titulo: 'Comércio Exemplo: taxa de licença', etapaId: 'aft_e7', valores: { aft_c1: 'Comércio Exemplo: taxa de licença', aft_doc: '89.012.345/0001-67', aft_nome: 'Comércio Exemplo ME', aft_c2: 'Taxas', aft_c3: 'Pedido de outro órgão', aft_c4: '2025', aft_c7: 'Não' } },
  ],
  tour: [
    '🕵️ Ação fiscal do ISS, IPTU, ITBI e taxas da ordem ao crédito constituído.',
    '1️⃣ "Ordem de Fiscalização": contribuinte, tributo, origem e período.',
    '2️⃣ "Termo de Início": intimação com prazo para entregar documentos.',
    '3️⃣ "Auditoria": irregularidade lavra o auto; sem irregularidade, encerra.',
    '4️⃣ "Auto de Infração": prazo de defesa pausa o relógio do processo.',
    '⚖️ "Julgamento" e "Crédito Constituído": pago, parcelado ou para a dívida ativa.',
  ],
  etapasDicas: [
    { quemFaz: 'Chefia da fiscalização', objetivo: 'Emitir a ordem com o período.' },
    { quemFaz: 'Auditor fiscal', objetivo: 'Intimar com prazo claro.' },
    { quemFaz: 'Auditor fiscal', objetivo: 'Auditar e documentar.', automacoes: ['Já configurado: irregularidade lavra o auto'] },
    { quemFaz: 'Auditor fiscal', objetivo: 'Lavrar o auto e controlar a defesa.', automacoes: ['Já configurado: com defesa vai a julgamento'] },
    { quemFaz: 'Julgador de 1ª instância', objetivo: 'Decidir fundamentado.' },
    { quemFaz: 'Tributos', objetivo: 'Constituir e encaminhar o crédito.', conexoes: ['Ligue ao fluxo "Dívida Ativa"'] },
    { quemFaz: 'Auditor fiscal', objetivo: 'Encerrar com termo.' },
  ],
};

// FAZ-02 (P2)
const fazCadastro: DefaultTemplate = {
  id: 'tpl_faz_cadastro',
  name: 'Atualização Cadastral Imobiliária',
  descricao: 'Pedido do contribuinte para desmembrar, unificar, averbar construção ou demolição, corrigir área, endereço ou titularidade: conferência documental, vistoria, pendências e atualização no cadastro com reflexo no IPTU do próximo exercício.',
  categoria: 'publico',
  icone: '🗺️',
  prazoProcesso: { horas: 720, etapasPausaIds: ['cad_e4'] },
  etiquetas: [
    { nome: 'Aumenta o IPTU', cor: COR.amarelo },
    { nome: 'Divergência de Área', cor: COR.vermelho },
  ],
  etapas: [
    {
      id: 'cad_e1', nome: 'Pedido',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['cad_c1', 'cad_c3', 'cad_c2'],
      permiteMoverPara: ['cad_e2'],
      campos: [
        { id: 'cad_c1', nome: 'Imóvel', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        { id: 'cad_doc', nome: 'CPF / CNPJ', tipo: 'texto', obrigatorio: true },
        { id: 'cad_nome', nome: 'Nome / Razão Social', tipo: 'texto', obrigatorio: true },
        { id: 'cad_whats', nome: 'WhatsApp', tipo: 'telefone', obrigatorio: false },
        { id: 'cad_email', nome: 'E-mail', tipo: 'texto', obrigatorio: false },
        { id: 'cad_canal', nome: 'Canal de Entrada', tipo: 'lista', obrigatorio: true, opcoes: ['Portal', 'WhatsApp', 'Balcão', 'Telefone'], coresOpcoes: { 'Portal': COR.azul, 'WhatsApp': COR.verde, 'Balcão': COR.amarelo, 'Telefone': COR.cinza } },
        { id: 'cad_prot', nome: 'Nº do Protocolo', tipo: 'texto', obrigatorio: false },
        { id: 'cad_bairro', nome: 'Bairro', tipo: 'texto', obrigatorio: false },
        { id: 'cad_lgpd', nome: 'Aceite LGPD', tipo: 'checkbox_single', obrigatorio: true, opcoes: ['Autorizo o uso dos meus dados para este atendimento (LGPD)'] },
        { id: 'cad_c2', nome: 'Inscrição Imobiliária', tipo: 'texto', obrigatorio: true },
        { id: 'cad_c3', nome: 'Tipo', tipo: 'lista', obrigatorio: true, opcoes: ['Desmembramento', 'Unificação', 'Averbação de construção', 'Demolição', 'Correção de área', 'Endereço / numeração', 'Titularidade'] },
        { id: 'cad_c4', nome: 'Matrícula Atualizada', tipo: 'anexo', obrigatorio: true },
        { id: 'cad_c5', nome: 'Planta / Projeto', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'cad_e2', nome: 'Conferência Documental',
      tempoMaximoHoras: 72,
      camposVisiveisNoCard: ['cad_c1', 'cad_c3', 'cad_c6'],
      permiteMoverPara: ['cad_e3', 'cad_e4'],
      permiteCriarCards: false,
      campos: [
        { id: 'cad_c6', nome: 'Documentação completa?', tipo: 'lista', obrigatorio: true, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'cad_e3', nome: 'Vistoria',
      tempoMaximoHoras: 240,
      camposVisiveisNoCard: ['cad_c1', 'cad_c3', 'cad_c7'],
      permiteMoverPara: ['cad_e5'],
      permiteCriarCards: false,
      campos: [
        { id: 'cad_c7', nome: 'Área Apurada (m²)', tipo: 'numero', obrigatorio: false },
        { id: 'cad_c8', nome: 'Fotos', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'cad_e4', nome: 'Pendência do Contribuinte',
      tempoMaximoHoras: 360,
      camposVisiveisNoCard: ['cad_c1', 'cad_c9'],
      permiteMoverPara: ['cad_e2'],
      permiteCriarCards: false,
      campos: [
        { id: 'cad_c9', nome: 'Exigência', tipo: 'texto_longo', obrigatorio: true },
      ],
    },
    {
      id: 'cad_e5', nome: 'Atualizado no Cadastro',
      camposVisiveisNoCard: ['cad_c1', 'cad_c3', 'cad_c11'],
      permiteCriarCards: false,
      campos: [
        { id: 'cad_c10', nome: 'Novas Inscrições', tipo: 'texto', obrigatorio: false },
        { id: 'cad_c11', nome: 'Reflexo no IPTU do próximo exercício?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'mover',
      nome: 'Documentação completa vai para vistoria; incompleta abre pendência',
      etapaOrigemId: 'cad_e2',
      condicao: { campoId: 'cad_c6', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'cad_e3',
      etapaDestinoSenaoId: 'cad_e4',
    },
  ],
  cards: [
    { id: 'cad_card1', titulo: 'Lote 14 da quadra 22: desmembramento em 2 lotes', etapaId: 'cad_e3', valores: { cad_c1: 'Lote 14 da quadra 22: desmembramento em 2 lotes', cad_doc: '234.567.890-11', cad_nome: 'Rosa Exemplo', cad_whats: '(47) 99999-0001', cad_canal: 'Portal', cad_prot: '2026/CAD-000214', cad_bairro: 'Jardim', cad_lgpd: 'Autorizo o uso dos meus dados para este atendimento (LGPD)', cad_c2: '01.02.022.0014', cad_c3: 'Desmembramento', cad_c6: 'Sim' } },
    { id: 'cad_card2', titulo: 'Casa na Rua das Palmas: averbação de ampliação', etapaId: 'cad_e4', etiquetas: ['Aumenta o IPTU'], valores: { cad_c1: 'Casa na Rua das Palmas: averbação de ampliação', cad_doc: '345.678.901-22', cad_nome: 'Sérgio Exemplo', cad_canal: 'Balcão', cad_bairro: 'Bairro Sul', cad_lgpd: 'Autorizo o uso dos meus dados para este atendimento (LGPD)', cad_c2: '01.05.010.0088', cad_c3: 'Averbação de construção', cad_c6: 'Não', cad_c9: 'Enviar o projeto aprovado e o habite-se da ampliação de 42 m².' } },
    { id: 'cad_card3', titulo: 'Sala comercial: troca de titularidade', etapaId: 'cad_e5', valores: { cad_c1: 'Sala comercial: troca de titularidade', cad_doc: '90.123.456/0001-78', cad_nome: 'Imobiliária Exemplo Ltda', cad_canal: 'Portal', cad_bairro: 'Centro', cad_lgpd: 'Autorizo o uso dos meus dados para este atendimento (LGPD)', cad_c2: '01.01.003.0102', cad_c3: 'Titularidade', cad_c6: 'Sim', cad_c11: 'Não' } },
    { id: 'cad_card4', titulo: 'Terreno na Av. Brasil: correção de área', etapaId: 'cad_e1', etiquetas: ['Divergência de Área'], valores: { cad_c1: 'Terreno na Av. Brasil: correção de área', cad_doc: '456.789.012-33', cad_nome: 'Paulo Exemplo', cad_canal: 'WhatsApp', cad_bairro: 'Centro', cad_lgpd: 'Autorizo o uso dos meus dados para este atendimento (LGPD)', cad_c2: '01.01.009.0045', cad_c3: 'Correção de área' } },
  ],
  tour: [
    '🗺️ Atualização do cadastro imobiliário pedida pelo contribuinte, com vistoria.',
    '1️⃣ "Pedido": imóvel, inscrição, tipo e matrícula atualizada.',
    '2️⃣ "Conferência": completa vai para vistoria; incompleta abre pendência (o prazo pausa).',
    '3️⃣ "Vistoria": área apurada e fotos.',
    '✅ "Atualizado": novas inscrições e reflexo no IPTU do próximo exercício.',
  ],
  etapasDicas: [
    { quemFaz: 'Atendimento / contribuinte', objetivo: 'Registrar com a matrícula.' },
    { quemFaz: 'Cadastro imobiliário', objetivo: 'Conferir os documentos.', automacoes: ['Já configurado: completa vai para vistoria; incompleta abre pendência'] },
    { quemFaz: 'Fiscal de cadastro', objetivo: 'Vistoriar e medir.' },
    { quemFaz: 'Contribuinte', objetivo: 'Atender a exigência.' },
    { quemFaz: 'Cadastro imobiliário', objetivo: 'Atualizar e avisar o contribuinte.' },
  ],
};

// FAZ-03 (P2)
const fazDivida: DefaultTemplate = {
  id: 'tpl_faz_divida',
  name: 'Dívida Ativa: Inscrição, Cobrança e Protesto',
  descricao: 'Do débito vencido à quitação: controle de legalidade e inscrição com CDA, cobrança administrativa, protesto em cartório e execução fiscal pela Procuradoria, sempre de olho na prescrição de 5 anos (CTN, art. 174).',
  categoria: 'publico',
  icone: '📑',
  etiquetas: [
    { nome: 'Prescrição Próxima', cor: COR.vermelho },
    { nome: 'Acordo em Andamento', cor: COR.verde },
    { nome: 'Grande Devedor', cor: COR.roxo },
  ],
  etapas: [
    {
      id: 'dva_e1', nome: 'Débito Vencido',
      camposVisiveisNoCard: ['dva_c1', 'dva_c3', 'dva_c5'],
      permiteMoverPara: ['dva_e2'],
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'dva_c1', nome: 'Devedor', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        { id: 'dva_doc', nome: 'CPF / CNPJ', tipo: 'texto', obrigatorio: true },
        { id: 'dva_nome', nome: 'Nome / Razão Social do Devedor', tipo: 'texto', obrigatorio: true },
        { id: 'dva_whats', nome: 'WhatsApp', tipo: 'telefone', obrigatorio: false },
        { id: 'dva_email', nome: 'E-mail', tipo: 'texto', obrigatorio: false },
        { id: 'dva_bairro', nome: 'Bairro', tipo: 'texto', obrigatorio: false },
        { id: 'dva_c2', nome: 'Tributo e Exercício', tipo: 'texto', obrigatorio: true },
        { id: 'dva_c3', nome: 'Valor Original', tipo: 'valor', obrigatorio: true },
        { id: 'dva_c4', nome: 'Data da Constituição Definitiva', tipo: 'data', obrigatorio: true },
        { id: 'dva_c5', nome: 'Prescrição em', tipo: 'data_vencimento', obrigatorio: true },
      ],
    },
    {
      id: 'dva_e2', nome: 'Controle de Legalidade e Inscrição',
      tempoMaximoHoras: 240,
      camposVisiveisNoCard: ['dva_c1', 'dva_c6', 'dva_c5'],
      permiteMoverPara: ['dva_e3'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'dva_c6', nome: 'Nº da CDA', tipo: 'texto', obrigatorio: true },
        { id: 'dva_c7', nome: 'CDA', tipo: 'anexo', obrigatorio: true },
      ],
    },
    {
      id: 'dva_e3', nome: 'Cobrança Administrativa',
      tempoMaximoHoras: 720,
      camposVisiveisNoCard: ['dva_c1', 'dva_c3', 'dva_c5'],
      permiteMoverPara: ['dva_e4', 'dva_e6'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'dva_c8', nome: 'Notificação enviada', tipo: 'lista', obrigatorio: false, opcoes: ['Carta', 'E-mail', 'WhatsApp', 'Edital'] },
        { id: 'dva_c9', nome: 'Contribuinte procurou acordo?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'dva_e4', nome: 'Protesto da CDA',
      tempoMaximoHoras: 720,
      camposVisiveisNoCard: ['dva_c1', 'dva_c3', 'dva_c10'],
      permiteMoverPara: ['dva_e5', 'dva_e6'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'dva_c10', nome: 'Nº do Protesto', tipo: 'texto', obrigatorio: false },
        { id: 'dva_c11', nome: 'Cartório', tipo: 'texto', obrigatorio: false },
      ],
    },
    {
      id: 'dva_e5', nome: 'Execução Fiscal (PGM)',
      camposVisiveisNoCard: ['dva_c1', 'dva_c12', 'dva_c13'],
      permiteMoverPara: ['dva_e6'],
      permiteCriarCards: false,
      campos: [
        { id: 'dva_c12', nome: 'Nº do Processo Judicial', tipo: 'texto', obrigatorio: true },
        { id: 'dva_c13', nome: 'Valor Atualizado', tipo: 'valor', obrigatorio: false },
      ],
    },
    {
      id: 'dva_e6', nome: 'Quitado / Parcelado',
      camposVisiveisNoCard: ['dva_c1', 'dva_c14', 'dva_c15'],
      permiteCriarCards: false,
      campos: [
        { id: 'dva_c14', nome: 'Forma', tipo: 'lista', obrigatorio: true, opcoes: ['Pagamento à vista', 'Parcelamento / REFIS', 'Compensação'], coresOpcoes: { 'Pagamento à vista': COR.verde, 'Parcelamento / REFIS': COR.azul } },
        { id: 'dva_c15', nome: 'Protesto/execução baixados?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não'], coresOpcoes: CORES_SIM_NAO },
      ],
    },
  ],
  cards: [
    { id: 'dva_card1', titulo: 'Loteadora Exemplo: IPTU 2021 a 2023', etapaId: 'dva_e4', etiquetas: ['Grande Devedor'], valores: { dva_c1: 'Loteadora Exemplo: IPTU 2021 a 2023', dva_doc: '01.234.567/0001-89', dva_nome: 'Loteadora Exemplo Ltda', dva_bairro: 'Loteamento Bela Vista', dva_c2: 'IPTU 2021-2023', dva_c3: 184320, dva_c4: '2022-03-10', dva_c5: '2027-03-10', dva_c6: 'CDA 2024/00871', dva_c8: 'Carta', dva_c9: 'Não', dva_c10: 'Protocolo 77812', dva_c11: 'Tabelionato de Protesto de Exemplo' } },
    { id: 'dva_card2', titulo: 'Bar Exemplo: ISS fixo 2021', etapaId: 'dva_e2', etiquetas: ['Prescrição Próxima'], valores: { dva_c1: 'Bar Exemplo: ISS fixo 2021', dva_doc: '12.345.000/0001-11', dva_nome: 'Bar Exemplo ME', dva_c2: 'ISS 2021', dva_c3: 2140, dva_c4: '2021-12-20', dva_c5: '2026-12-20' } },
    { id: 'dva_card3', titulo: 'João Exemplo: IPTU 2024', etapaId: 'dva_e3', etiquetas: ['Acordo em Andamento'], valores: { dva_c1: 'João Exemplo: IPTU 2024', dva_doc: '567.890.123-44', dva_nome: 'João Exemplo', dva_whats: '(47) 99999-0002', dva_bairro: 'Centro', dva_c2: 'IPTU 2024', dva_c3: 1875.6, dva_c4: '2024-12-15', dva_c5: '2029-12-15', dva_c6: 'CDA 2025/01544', dva_c8: 'WhatsApp', dva_c9: 'Sim' } },
    { id: 'dva_card4', titulo: 'Indústria Exemplo: ISS 2020', etapaId: 'dva_e6', valores: { dva_c1: 'Indústria Exemplo: ISS 2020', dva_doc: '23.456.000/0001-22', dva_nome: 'Indústria Exemplo S/A', dva_c2: 'ISS 2020', dva_c3: 96200, dva_c4: '2021-06-30', dva_c5: '2026-06-30', dva_c6: 'CDA 2022/00312', dva_c12: '5004321-00.2023.8.24.0999', dva_c13: 141880, dva_c14: 'Parcelamento / REFIS', dva_c15: 'Sim' } },
  ],
  tour: [
    '📑 Dívida ativa da inscrição à quitação, de olho na prescrição de 5 anos.',
    '1️⃣ "Débito Vencido": devedor, tributo, valor e data da prescrição como vencimento.',
    '2️⃣ "Inscrição": controle de legalidade e CDA.',
    '3️⃣ "Cobrança Administrativa": carta, e-mail, WhatsApp ou edital.',
    '4️⃣ "Protesto" e "Execução Fiscal": cartório e processo judicial.',
    '✅ "Quitado / Parcelado": baixe protesto e execução.',
  ],
  etapasDicas: [
    { quemFaz: 'Tributos', objetivo: 'Separar o que vence primeiro.' },
    { quemFaz: 'Procuradoria / Tributos', objetivo: 'Inscrever com CDA válida.' },
    { quemFaz: 'Tributos', objetivo: 'Cobrar e oferecer acordo.' },
    { quemFaz: 'Procuradoria', objetivo: 'Protestar a CDA.' },
    { quemFaz: 'Procuradoria', objetivo: 'Ajuizar antes da prescrição.' },
    { quemFaz: 'Tributos / Procuradoria', objetivo: 'Baixar protesto e execução.' },
  ],
};

export const TEMPLATES_PUBLICO: DefaultTemplate[] = [
  processoAdministrativo,
  contratacaoPublica,
  execucaoDespesa,
  ouvidoria,
  acessoInformacao,
  solicitacoesCidadao,
  conveniosTransferencias,
  fiscalizacaoContratos,
  // Prefeitura: P1
  gabCamara,
  gabMp,
  gabAtos,
  admMemo,
  cgmObrigacoes,
  // Prefeitura: P2
  cgmTce,
  rhLicencas,
  rhDiarias,
  rhProbatorio,
  rhPad,
  admPatrimonio,
  admFrota,
  admVeiculo,
  cmpCrc,
  cmpPagamento,
  fazFiscalizacao,
  fazCadastro,
  fazDivida,
];
